// src/index.ts
import { z as z56 } from "zod";

// src/schemas/common.ts
import { z } from "zod";
var IdSchema = z.string().uuid();
var IsoTimestampSchema = z.string().datetime();
var RawJsonSchema = z.unknown().optional();

// src/schemas/enums.ts
import { z as z2 } from "zod";
var StoreStateSchema = z2.enum(["assigned", "enabled", "disabled", "removed"]);
var PlatformKeySchema = z2.enum(["talabat", "careem", "mythings"]);
var RoleSchema = z2.enum(["admin", "client"]);
var RecommendationCadenceSchema = z2.enum(["daily", "3-day", "weekly"]);
var AccountStatusSchema = z2.enum(["healthy", "at_risk", "critical", "unknown"]);
var MappingStatusSchema = z2.enum(["unmapped", "auto", "manual", "ambiguous"]);
var RestaurantCategorySchema = z2.enum([
  "general",
  // all-category / catch-all
  "fast_food",
  // burgers, sandwiches, fried chicken, pizza
  "arabic",
  // Arabic / Levantine mains
  "desserts",
  // desserts, Arabic sweets (حلويات), maamoul, cakes
  "bakery",
  // bakeries, pastries
  "drinks",
  // juices, cold drinks, refreshments
  "cafe",
  // coffee shops / cafés
  "breakfast",
  // breakfast specialists
  "grocery",
  // grocery / mini-market
  "healthy"
  // healthy / salads / diet
]);

// src/schemas/client.ts
import { z as z3 } from "zod";
var ClientConfigSchema = z3.object({
  /** RESERVED (Req 2.4): per-store cadence keyed by store id. No tiers, no limits, no behavior yet. */
  recommendationCadence: z3.record(IdSchema, RecommendationCadenceSchema).default({})
});
var ClientSchema = z3.object({
  id: IdSchema,
  name: z3.string().min(1),
  /** Optional contact email for the tenant (1–254, valid email) — primary admin contact. */
  email: z3.string().min(1).max(254).email().optional(),
  /** Optional contact phone for the tenant, E.164 (+ then 1–15 digits). */
  phone: z3.string().regex(/^\+[1-9]\d{0,14}$/, "must be E.164 (+ then 1\u201315 digits)").optional(),
  config: ClientConfigSchema,
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/brand.ts
import { z as z4 } from "zod";
var BrandSchema = z4.object({
  id: IdSchema,
  /** Owning tenant — a Brand belongs to exactly one Client (Req 3.1). */
  clientId: IdSchema,
  /** The single platform this brand lives on (brand-model spec R1). */
  platformKey: PlatformKeySchema,
  name: z4.string().min(1),
  // ── Talabat brand-level ids (Req 3.3) ──
  /** Talabat chain id (e.g. "707597"). */
  talabatChainId: z4.string().min(1).optional(),
  /** Talabat grid code (e.g. "HPFGFL"). */
  talabatGrid: z4.string().min(1).optional(),
  // ── Careem brand-level ids (Req 3.3; COMPANY → BRAND → MERCHANT) ──
  /** Careem brand id (mapped from the connector's `chainId → brandId`). */
  careemBrandId: z4.string().min(1).optional(),
  /** Careem company id (mapped from the connector's `grid → companyId`). */
  careemCompanyId: z4.string().min(1).optional(),
  /**
   * Optional shared-catalog reference (Req 3.6): WHERE a brand shares one menu,
   * a Store may inherit this catalog as a default (branch override wins). The
   * catalog itself is defined in `clarity-onboarding-ingestion`; this is just
   * the link. Reference by id only here.
   */
  sharedCatalogId: IdSchema.optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/store.ts
import { z as z5 } from "zod";
var StoreProfileSchema = z5.object({
  // ── entered (onboarding Step 1; onboarding Req 2.1) ──
  /** THE single markup source (Req 5.2). Percentage in [0, 100]. */
  markup_pct: z5.number().min(0).max(100),
  /** Store-level default food-cost %, the only food-cost fallback (onboarding Req 3.1). */
  default_food_cost_pct: z5.number().min(0).max(100).optional(),
  /** ISO currency code for this branch (e.g. "JOD"). Optional until onboarding sets it. */
  currency: z5.string().length(3).optional(),
  /**
   * IANA timezone for this branch (e.g. "Asia/Amman"), used to resolve
   * store-LOCAL day boundaries — most importantly the daily ingestion window,
   * which ends at the end of YESTERDAY in the store's own day (today is never
   * fetched, as the platform's day isn't settled yet). Optional: absent ⇒ the
   * ingestion layer falls back to `Asia/Amman` (the current single-region
   * reality). A 1–64-char string; validated as a real zone where it is used.
   */
  timezone: z5.string().min(1).max(64).optional(),
  // ── confirmed / overridable payment-gateway params (onboarding Req 4.1) ──
  /** Variable payment-gateway fee %, per-platform default overridable per store. */
  pay_fee_pct: z5.number().min(0).max(100).optional(),
  /** Flat payment-gateway fee per order. */
  pay_fee_fixed: z5.number().min(0).optional(),
  /** Tax applied on the payment-gateway fee (%). */
  pay_fee_tax_pct: z5.number().min(0).max(100).optional(),
  // ── derived read-only (onboarding Step 3; onboarding Req 6.1) ──
  /** Commission %, derived from finalized detailed orders; `null` ⇒ unavailable (Req 5.1, 5.4). */
  comm_pct: z5.number().min(0).max(100).nullable().optional(),
  /** Commission tax %, derived from finalized detailed orders; `null` ⇒ unavailable. */
  comm_tax_pct: z5.number().min(0).max(100).nullable().optional(),
  /** Online/card share of orders (%). */
  visa_pct: z5.number().min(0).max(100).optional(),
  /** Count of Plus/Pro (subscription) orders in the derivation window. */
  n_plus: z5.number().int().nonnegative().optional(),
  /** Count of non-Plus orders in the derivation window. */
  n_non_plus: z5.number().int().nonnegative().optional(),
  /** Average order value for Plus/Pro orders. */
  avg_order_plus: z5.number().optional(),
  /** Average order value for non-Plus orders. */
  avg_order_non_plus: z5.number().optional(),
  /** Cost per Plus order, where derivable. */
  plus_fee: z5.number().optional(),
  /** Discounted-delivery %; N/A for Talabat (reserved 0), used by other platforms later (Req 6.1). */
  disc_del_pct: z5.number().min(0).max(100).optional(),
  /**
   * The restaurant's PRIMARY category (clarity-campaign-matching — campaign
   * applicability). Drives which category-scoped monthly offers apply: a
   * `drinks`/`desserts`/`cafe` offer never matches a `fast_food` store. The
   * portal carries no usable cuisine field, so the admin sets this; absent ⇒
   * treated as `general` (only all-category campaigns apply). See
   * `RestaurantCategorySchema`.
   */
  category: RestaurantCategorySchema.optional(),
  /**
   * Optional SUB-categories a store also belongs to (e.g. a fast-food place
   * that also sells desserts). A campaign applies when its `appliesTo`
   * intersects the store's primary category OR any of these. Defaults absent
   * (none).
   */
  subCategories: z5.array(RestaurantCategorySchema).optional()
}).passthrough();
var StoreSchema = z5.object({
  id: IdSchema,
  /** Owning brand — a Store belongs to exactly one Brand (Req 3.1). */
  brandId: IdSchema,
  /** The single platform this branch settles on (Req 3.2). */
  platformKey: PlatformKeySchema,
  /**
   * The outlet id on the platform (Req 3.3): Talabat `externalStoreId`
   * (e.g. "780014") / Careem `merchantId`. This is the join key to ingested
   * reports; it lives on the Store, not flattened onto the Brand.
   */
  externalStoreId: z5.string().min(1),
  /**
   * Human branch name as the platform exposes it (e.g. "One Piece Burger, Al
   * Sahel"), captured at discovery/import. Display-only; `externalStoreId`
   * remains the settlement/join key. Nullable + optional: a hand-created branch
   * may have none, and the DB stores absence as `null`, so both `null` and
   * absent are accepted (keeps the DB row assignable to `Store` directly).
   */
  name: z5.string().min(1).nullable().optional(),
  /** Economic/platform-shape container incl. the single markup source (Req 5.2). */
  profile: StoreProfileSchema,
  // ── Lifecycle / tracking fields (Req 4.9) ──
  /** Lifecycle state; sole writer is the `core` lifecycle machine (Req 4.1). */
  state: StoreStateSchema,
  /** Gate read by the `enable` guard (Req 4.3); set by the onboarding spec. */
  onboardingComplete: z5.boolean(),
  /**
   * Set when the onboarding user confirms Step 1 — food cost + markup (+ the
   * payment-gateway params; onboarding Req 2.1, 4.1). Records the explicit
   * CONFIRMATION so the wizard resumes at Step 2 next time instead of Step 1.
   */
  step1Confirmed: z5.boolean(),
  /**
   * Set when the onboarding user confirms the Step-2 catalog review (onboarding
   * Req 2.2). Distinct from "a catalog is persisted" — Step 2 fetches + persists
   * the catalog on first visit, so this flag records the explicit CONFIRMATION
   * so the wizard resumes past Step 2 next time (and never on a merely-fetched,
   * unconfirmed catalog).
   */
  catalogConfirmed: z5.boolean(),
  /** Set when the `reference_data` effect has been satisfied (Req 4.2). */
  referenceDataPresent: z5.boolean(),
  /** Set when the first full backfill has completed (Req 4.4). */
  backfillComplete: z5.boolean(),
  /** True once the store's data has been purged on `removed` (Req 4.7). */
  dataDeleted: z5.boolean(),
  enabledAt: IsoTimestampSchema.nullable(),
  disabledAt: IsoTimestampSchema.nullable(),
  removedAt: IsoTimestampSchema.nullable(),
  lastIngestedAt: IsoTimestampSchema.nullable(),
  lastPollAt: IsoTimestampSchema.nullable(),
  /**
   * The ISO timestamp at which Step-2 orders-driven provisioning
   * (`runStep2SourcesOnce` → `provisionFromOrders`) first completed SUCCESSFULLY
   * for this store; `null` ⇒ never provisioned. The Step-2 `getCatalog` run-once
   * guard reads it as nullable-truthy: non-null ⇒ skip the orders crawl on
   * revisit; null ⇒ run it once. Set on a successful run, cleared back to `null`
   * only when the catalog is re-pulled from scratch (empty current catalog +
   * menu re-pulled). Platform-agnostic. Defaults `null` so existing stores
   * re-provision once on their next visit, then settle.
   */
  ordersProvisionedAt: IsoTimestampSchema.nullable().optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/user.ts
import { z as z6 } from "zod";
var E164Schema = z6.string().regex(/^\+[1-9]\d{0,14}$/, "must be E.164 (+ then 1\u201315 digits)");
var UserSchema = z6.object({
  id: IdSchema,
  /**
   * The login identifier (clarity-workspace-users phone-first): an E.164 phone,
   * globally unique. Phone replaces email as how a user signs in and is
   * verified. Nullable only to tolerate legacy rows created before the switch;
   * every user created now carries a phone.
   */
  phone: E164Schema.nullable().default(null),
  /**
   * When the phone was verified via a WhatsApp one-time code, or null when not
   * yet verified. An unverified user exists but cannot sign in (Req: verify on
   * add). Reset also requires a verified phone.
   */
  phoneVerifiedAt: IsoTimestampSchema.nullable().default(null),
  /**
   * Legacy contact email — NO LONGER used for auth or required (phone-first).
   * Kept nullable so pre-switch rows and admin bootstrap still parse; new users
   * are created with a phone, not an email.
   */
  email: z6.string().email().nullable().default(null),
  role: RoleSchema,
  /** Tenant binding — null for admins (Req 2.2), set for client users (Req 2.1). */
  clientId: IdSchema.nullable(),
  /** argon2 password hash (Req 6.3). Server-only; projected out of read responses. */
  passwordHash: z6.string().min(1),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/report.ts
import { z as z7 } from "zod";
var ReportSchema = z7.object({
  id: IdSchema,
  /** The branch this report lands on — reports are per-store (Req 2.5, 3.2). */
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** Report family, e.g. "statement" | "payout" | "detailed" (ingestion spec owns the vocabulary). */
  kind: z7.string().min(1),
  /** Processing status; ingestion spec owns the authoritative states. */
  status: z7.string().min(1),
  /** Original file name as pulled/received, when known. */
  fileName: z7.string().min(1).nullable(),
  /** Reporting period start, when the report covers a window. */
  periodStart: IsoTimestampSchema.nullable(),
  /** Reporting period end, when the report covers a window. */
  periodEnd: IsoTimestampSchema.nullable(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/connection.ts
import { z as z8 } from "zod";
var ConnectionStateSchema = z8.enum(["connected", "expired", "disconnected"]);
var ConnectionStatusSchema = z8.object({
  /** The platform this status reports on (Talabat now; Careem deferred). */
  platform: PlatformKeySchema,
  /** The derived connection state. */
  status: ConnectionStateSchema,
  /** When the probe ran, ISO-8601. */
  checkedAt: IsoTimestampSchema
});
var StoreConnStateSchema = z8.enum([
  "connected",
  "expired",
  "error",
  "needs-credentials",
  "checking"
]);
var RollupStateSchema = z8.enum([
  "connected",
  "checking",
  "partial",
  "needs-credentials"
]);
var StoreStatusSchema = z8.object({
  /** App store id (the join key the credential + status are keyed on). */
  storeId: IdSchema,
  /** The MyThings SubStoreId; null until the store has signed in at least once. */
  externalStoreId: z8.string().nullable(),
  /** Store display name (optional, display only). */
  name: z8.string().optional(),
  /** The per-store probe state. */
  state: StoreConnStateSchema,
  /** When this store was last probed, ISO-8601; null before the first check. */
  checkedAt: IsoTimestampSchema.nullable()
});
var SessionConnectionEntrySchema = ConnectionStatusSchema.extend({
  authModel: z8.literal("session")
});
var CredentialConnectionEntrySchema = z8.object({
  platform: PlatformKeySchema,
  authModel: z8.literal("credential"),
  stores: z8.array(StoreStatusSchema),
  rollup: RollupStateSchema
});
var AdminConnectionEntrySchema = z8.discriminatedUnion("authModel", [
  SessionConnectionEntrySchema,
  CredentialConnectionEntrySchema
]);
var MythingsCredentialWriteSchema = z8.object({
  accountLogin: z8.string().min(1),
  password: z8.string().min(1)
});

// src/schemas/platform-settings.ts
import { z as z9 } from "zod";
var PlatformFeeDefaultsSchema = z9.object({
  /** The platform these defaults apply to — the row key (one per platform). */
  platformKey: PlatformKeySchema,
  /** Variable payment-gateway fee %, the per-platform default (overridable per store). */
  pay_fee_pct: z9.number().min(0).max(100).optional(),
  /** Flat payment-gateway fee per order, the per-platform default. */
  pay_fee_fixed: z9.number().min(0).optional(),
  /** Tax applied on the payment-gateway fee (%), the per-platform default. */
  pay_fee_tax_pct: z9.number().min(0).max(100).optional(),
  updatedAt: IsoTimestampSchema
});
var PlatformFeeDefaultsUpdateSchema = z9.object({
  pay_fee_pct: z9.number().min(0).max(100).optional(),
  pay_fee_fixed: z9.number().min(0).optional(),
  pay_fee_tax_pct: z9.number().min(0).max(100).optional()
});

// src/schemas/platform.ts
import { z as z10 } from "zod";
var PlatformLiveStatusSchema = z10.enum(["live", "reserved"]);
var PlatformCapabilitiesSchema = z10.object({
  /** Whether the platform exposes paid ad-spend (the `app.ts` ad-spend guard). */
  supportsAdSpend: z10.boolean(),
  /**
   * Whether the platform exposes a fetchable MENU/catalog source. `false` ⇒
   * Step-2 catalog review degrades to a clean empty state with an explanatory
   * note (the catalog is built later from ordered items), NOT a failure. Careem
   * has no confirmed menu API in its portal capture; Talabat does.
   */
  supportsMenu: z10.boolean()
});
var PlatformDescriptorSchema = z10.object({
  /** The platform's enum key — the sole home stays `PlatformKeySchema`. */
  key: PlatformKeySchema,
  /** Display label shown in the UI ("Talabat", "Careem"). */
  name: z10.string(),
  /** Whether the platform is wired end to end or merely reserved. */
  liveStatus: PlatformLiveStatusSchema,
  /** What features the platform supports. */
  capabilities: PlatformCapabilitiesSchema
});

// src/schemas/money.ts
import { z as z11 } from "zod";
var OrderMoneySchema = z11.object({
  /** Net payout to the restaurant for this order (the settlement target). */
  payout: z11.number(),
  /** Platform commission charged on this order. */
  commission: z11.number(),
  /** Payment-gateway / processing fee charged on this order. */
  fees: z11.number(),
  /**
   * Restaurant-funded discount portion:
   * `"Discount Funded by you" + "Voucher Funded by you"` (Req 8.3).
   */
  restaurantFunded: z11.number(),
  /** Platform-funded discount portion (e.g. "Talabat-Funded Discount") (Req 8.3). */
  platformFundedDiscount: z11.number(),
  /**
   * Total funded discount: `restaurantFunded + platformFundedDiscount`
   * (Req 8.3). Carried explicitly so a consumer never has to re-derive it.
   */
  fundedDiscount: z11.number(),
  /**
   * MyThings only: the vendor's per-order delivery contribution, sourced verbatim
   * from the AccountantSummary column `Shipping Discount By Vendor`. It is a REAL
   * per-order cost that MyThings has ALREADY deducted before the net payout
   * (`Net To Vendor After Deduction`), NOT a customer promo (that is
   * `restaurantFunded` ← `Order Discount By Vendor`).
   *
   * Defaulted to `0` so a Talabat / Careem order — which never sets it — parses
   * BYTE-IDENTICAL, and a MyThings order with no delivery contribution (subtotal
   * below the 6 JOD threshold) carries a real reported `0`. The breakdown reads
   * it only for a MyThings store, so the default is invisible elsewhere.
   */
  vendorDeliveryFee: z11.number().default(0)
});
var NullableOrderMoneySchema = OrderMoneySchema.nullable();

// src/schemas/catalog.ts
import { z as z12 } from "zod";
var FoodCostPctSchema = z12.number().min(0).max(100);
var CatalogItemStatusSchema = z12.enum(["active", "archived"]);
var CatalogBaseProvenanceSchema = z12.enum([
  "line-price",
  "menu",
  "sibling-median",
  "unset",
  "order-residual"
]);
var EffectiveDatingSchema = z12.object({
  /** Inclusive start of this version's validity (ISO-8601). */
  validFrom: IsoTimestampSchema,
  /** Exclusive end of validity (ISO-8601), or `null` for the open-ended current version. */
  validTo: IsoTimestampSchema.nullable()
});
var CatalogOptionSchema = EffectiveDatingSchema.extend({
  id: IdSchema,
  /** The platform's option identifier, for linking order lines. */
  externalOptionId: z12.string().min(1),
  name: z12.string().min(1),
  /** Price added to the item's base price when this option is chosen. */
  priceDelta: z12.number(),
  /**
   * OPTIONAL per-option food-cost override (Req 11.1). WHEN omitted, the
   * option inherits (cascades from) the owning item's `foodCostPct`.
   */
  foodCostPct: FoodCostPctSchema.optional(),
  /**
   * Lifecycle status (clarity-catalog-options Req 5.1). Defaults to `active` so
   * prior inline options that omit it still parse unchanged. An `archived`
   * option was auto-provisioned from an order basket token that matched no
   * option of its parent; it still costs orders but is flagged for review.
   */
  status: CatalogItemStatusSchema.default("active"),
  /**
   * OPTIONAL provenance of `priceDelta` for an auto-provisioned (`archived`)
   * option (clarity-catalog-options Req 5.5–5.7): `menu`, `order-residual`,
   * `sibling-median`, or `unset` (`line-price` is never used for options).
   * Absent on menu-synced and owner-confirmed options. The option "needs a
   * price" ⇔ `status === 'archived'` AND `baseProvenance ∈ {sibling-median,
   * unset, order-residual}` (core `optionNeedsPrice`).
   */
  baseProvenance: CatalogBaseProvenanceSchema.optional(),
  /**
   * The verbatim platform response this option was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — an option is a
   * Persisted_Platform_Entity in its own right, so it adopts the shared shape).
   * Optional so pre-field / test records still parse.
   */
  raw_json: RawJsonSchema
});
var CatalogItemSchema = EffectiveDatingSchema.extend({
  id: IdSchema,
  /** The branch(store) this catalog item belongs to (per-store grain, Req 11.1). */
  storeId: IdSchema,
  /** The platform this catalog item is modeled on (Req 11.1). */
  platformKey: PlatformKeySchema,
  /** The platform's product/item identifier, for linking order lines. */
  externalItemId: z12.string().min(1),
  name: z12.string().min(1),
  /** Pre-markup base price (Req 3.3); platform price is derived with the store markup. */
  basePrice: z12.number(),
  /** Item-level food-cost percentage, `[0,100]` (Req 11.1). */
  foodCostPct: FoodCostPctSchema,
  /**
   * Lifecycle status (Req 11.1). Defaults to `active` so existing fixtures /
   * prior catalog data that omit it still parse and round-trip to `active`.
   * An `archived` item (auto-provisioned on an unmatched order line) still
   * resolves + costs orders — it is just flagged for review in the UI.
   */
  status: CatalogItemStatusSchema.default("active"),
  /**
   * OPTIONAL provenance of `basePrice` for an auto-provisioned (`archived`)
   * item (catalog-coverage Req 4.2 / 4.4). Absent on menu-pulled items. See
   * {@link CatalogBaseProvenanceSchema}; `sibling-median` / `unset` ⇒ the item
   * needs a price in Step-2 review.
   */
  baseProvenance: CatalogBaseProvenanceSchema.optional(),
  /**
   * The platform's ORIGINAL image URL for this product, when it exposes one.
   * Captured verbatim at catalog pull; the durable copy lives at
   * {@link imagePath}. Optional — a product may have no image.
   */
  imageUrl: z12.string().url().optional(),
  /**
   * A stable, server-owned reference to the DOWNLOADED product image — a path/
   * key the image store resolves to bytes (local `/catalog-images/…` now; an
   * S3/GCS key later). The storage provider is swappable behind this one field:
   * callers read `imagePath` and ask the image store to serve it, never the
   * platform URL directly (which expires / rate-limits). Optional — set only
   * once the image has been fetched + stored.
   */
  imagePath: z12.string().min(1).optional(),
  /** Options/modifiers for this item; each may override the item's food-cost pct. */
  options: z12.array(CatalogOptionSchema).default([]),
  /**
   * Alias names that resolve to this item (catalog-item-aliases Req 1). Each
   * entry is a non-empty string (a normalized name or `externalItemId` absorbed
   * from a merged source item). Defaults to `[]` so existing payloads that omit
   * it parse unchanged (backward-compatible).
   */
  aliases: z12.array(z12.string().min(1)).default([]),
  /**
   * The verbatim platform response this catalog item was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — the shared shape every
   * Persisted_Platform_Entity adopts). Optional so pre-field / test records
   * still parse.
   */
  raw_json: RawJsonSchema
});

// src/schemas/order.ts
import { z as z14 } from "zod";

// src/schemas/campaign.ts
import { z as z13 } from "zod";
var CampaignSourceSchema = z13.enum(["monthly", "ads", "platform"]);
var CampaignProvenanceSchema = z13.enum(["admin", "ads_api", "deals_api"]);
var CampaignAudienceSchema = z13.enum(["all", "plus", "nonplus", "new_customer"]);
var PricingModelSchema = z13.enum(["cpc", "cpm"]).nullable();
var CampaignPrioritySchema = z13.enum(["high", "medium", "low"]);
var CampaignSchema = z13.object({
  id: IdSchema,
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** The primary source discriminator (design §0). */
  source: CampaignSourceSchema,
  /** Every source this campaign came from — ≥1; >1 when deduped (Req 5.1). */
  sources: z13.array(CampaignSourceSchema).min(1),
  provenance: CampaignProvenanceSchema,
  name: z13.string().min(1),
  /** Optional Arabic display name; defaults null so fixtures without it parse. */
  arabicName: z13.string().nullable().default(null),
  audience: CampaignAudienceSchema.default("all"),
  /**
   * The restaurant CATEGORIES this campaign applies to (campaign applicability).
   * A category-specific offer (e.g. "Summer Refreshments" → `['drinks']`,
   * "Dessert Hits" → `['desserts']`, "Coffee Day" → `['cafe']`) matches ONLY a
   * store whose primary or sub-category intersects this set — so a fast-food
   * restaurant never matches a drinks/desserts/café offer (fixing the
   * false-match bug). An EMPTY set OR a set containing `general` means the
   * campaign applies to EVERY store (Flash Sale, Lqta, Jam3at are all-category).
   * Admin-set during monthly-offer review; defaults `[]` (applies to all) so
   * pre-field fixtures still parse.
   */
  appliesTo: z13.array(RestaurantCategorySchema).default([]),
  // ── discount terms (monthly / platform) ──
  /**
   * Headline discount as a fraction 0..1 (e.g. 0.5 = 50%); null for ads. For a
   * RANGED offer (a sheet that states "from X% to Y%") this is the headline
   * POINT estimate the forward profit model and the what-if slider center on —
   * by convention the guaranteed FLOOR (`= discountPctMin`). The declared band
   * itself is carried by `discountPctMin`/`discountPctMax`.
   */
  discountPct: z13.number().min(0).max(1).nullable().default(null),
  /**
   * Lower bound of a DECLARED discount range 0..1; null ⇒ not a range (a single
   * `discountPct`). When set, the matcher accepts an order whose observed
   * discount-% lands anywhere in `[discountPctMin, discountPctMax]` (±tolerance
   * at the edges), still gated by cap / min-order / audience (Req 6.2).
   */
  discountPctMin: z13.number().min(0).max(1).nullable().default(null),
  /** Upper bound of a DECLARED discount range 0..1; null ⇒ not a range. */
  discountPctMax: z13.number().min(0).max(1).nullable().default(null),
  /** Cap on the per-order discount amount; null ⇒ uncapped. */
  maxDiscountCap: z13.number().nonnegative().nullable().default(null),
  /** DECLARED co-funding share 0..1 — never inferred from orders (Req 1.5, 7.3). */
  coFundPct: z13.number().min(0).max(1).default(0),
  /** Minimum order value for the offer to apply; null ⇒ none. */
  minOrder: z13.number().nonnegative().nullable().default(null),
  /**
   * PRIORITY tier for overlap resolution (see {@link CampaignPrioritySchema}).
   * When an order fits two discount campaigns equally well on the same day, the
   * higher tier wins and the lower is skipped — so an always-on offer (`low`,
   * e.g. Lqta) yields to a dated special (`medium`). Defaults to `medium` so
   * pre-field fixtures still parse and existing campaigns keep normal priority.
   */
  priority: CampaignPrioritySchema.default("medium"),
  /**
   * Whether this campaign discounts only SELECTED PRODUCTS (not the whole menu)
   * — e.g. Lqta / "Join Best Sellers 30%" (30% off 6 specific items). The matcher
   * must not let a whole-order discount signature falsely claim an item-scoped
   * deal: with line items it requires a scoped product; without line items it is
   * down-ranked below a whole-menu candidate (clarity-campaign-match-accuracy).
   * Defaults false so pre-field fixtures parse.
   */
  itemScoped: z13.boolean().default(false),
  /** The platform product ids an item-scoped deal applies to (when known); [] otherwise. */
  scopedProductIds: z13.array(z13.string()).default([]),
  /**
   * The days of week this deal RUNS, as `0=Sunday … 6=Saturday` (JS
   * `getUTCDay()` convention). A deal that runs only certain days (Careem's
   * `operational_time` with non-empty `active_hours`; Talabat's
   * `dayOfWeekMatrixItems`) lists them here, so the matcher never attributes an
   * order placed on a day the deal did NOT run. EMPTY ⇒ every day (the default
   * and the backward-compatible behavior — a deal with no day restriction, and
   * every pre-field fixture, matches on all days).
   */
  daysOfWeek: z13.array(z13.number().int().min(0).max(6)).default([]),
  // ── ads terms ──
  pricingModel: PricingModelSchema.default(null),
  /** Cost-per-click bid; null for non-ads campaigns. */
  cpc: z13.number().nonnegative().nullable().default(null),
  // ── dedupe + provenance ──
  /** Admin-set key linking a monthly offer to a platform deal (Req 5.2). */
  platformDealKey: z13.string().nullable().default(null),
  // ── effective-dating + stamps ──
  validFrom: IsoTimestampSchema,
  validTo: IsoTimestampSchema.nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  /**
   * The verbatim platform response this campaign was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — the shared shape every
   * Persisted_Platform_Entity adopts). Optional so pre-field / test records
   * still parse.
   */
  raw_json: RawJsonSchema
});
var CampaignOccurrenceSchema = z13.object({
  id: IdSchema,
  campaignId: IdSchema,
  startDate: IsoTimestampSchema,
  endDate: IsoTimestampSchema,
  // ── overridable terms; null ⇒ inherit the parent campaign (Req 1.2) ──
  discountPct: z13.number().min(0).max(1).nullable().default(null),
  /** Override the declared range lower bound; null ⇒ inherit the campaign. */
  discountPctMin: z13.number().min(0).max(1).nullable().default(null),
  /** Override the declared range upper bound; null ⇒ inherit the campaign. */
  discountPctMax: z13.number().min(0).max(1).nullable().default(null),
  maxDiscountCap: z13.number().nonnegative().nullable().default(null),
  coFundPct: z13.number().min(0).max(1).nullable().default(null),
  audience: CampaignAudienceSchema.nullable().default(null),
  minOrder: z13.number().nonnegative().nullable().default(null),
  cpc: z13.number().nonnegative().nullable().default(null)
});
var CampaignMatchSchema = z13.object({
  campaignId: IdSchema,
  occurrenceId: IdSchema.nullable().default(null),
  matchSource: z13.enum(["discount_signature", "window_attribution", "promo_code"]),
  confidence: z13.enum(["high", "medium", "low"]),
  reason: z13.string()
});
function resolve(occurrence, campaign) {
  return {
    discountPct: occurrence.discountPct ?? campaign.discountPct,
    discountPctMin: occurrence.discountPctMin ?? campaign.discountPctMin,
    discountPctMax: occurrence.discountPctMax ?? campaign.discountPctMax,
    maxDiscountCap: occurrence.maxDiscountCap ?? campaign.maxDiscountCap,
    coFundPct: occurrence.coFundPct ?? campaign.coFundPct,
    audience: occurrence.audience ?? campaign.audience,
    minOrder: occurrence.minOrder ?? campaign.minOrder,
    cpc: occurrence.cpc ?? campaign.cpc
  };
}
var WaterfallLineKindSchema = z13.enum(["start", "neg", "total"]);
var WaterfallLineSchema = z13.object({
  /** Stable key for the step (e.g. `custPays`, `selfDiscount`, `profit`). */
  key: z13.string().min(1),
  /** Human label for the UI (e.g. "Customer pays", "Commission (+VAT)"). */
  label: z13.string().min(1),
  /** The step's amount (round2); `neg` lines carry the subtracted magnitude. */
  amount: z13.number(),
  kind: WaterfallLineKindSchema
});
var OrderTypeWaterfallSchema = z13.object({
  bucket: z13.enum(["plus", "nonplus"]),
  /** The ordered waterfall steps, `start` → `neg…` → `total`. */
  lines: z13.array(WaterfallLineSchema).min(1),
  /** Net profit per order for this bucket (round2). */
  profit: z13.number(),
  /** This bucket's share of the modeled mix, 0..1 (weights the blend). */
  mixShare: z13.number().min(0).max(1)
});
var ProjectionVerdictSchema = z13.enum(["win", "even", "lose"]);
var ProvenanceMapSchema = z13.object({
  mix: z13.enum(["history", "store"]),
  aov: z13.enum(["history", "store"]),
  cr: z13.enum(["history", "store"]),
  coFund: z13.literal("input"),
  economics: z13.literal("store")
});
var CrCurvePointSchema = z13.object({
  /** Conversion rate 0..1 (share of clicks that become orders). */
  cr: z13.number().min(0).max(1),
  /** Profit per order at this CR (round2): `margin − (1/cr) × cpc`. */
  profit: z13.number()
});
var CpcBreakEvenSchema = z13.object({
  /** Discount-free margin per converted order (round2). */
  margin: z13.number(),
  /** The CPC bid this block was computed at (round2). */
  cpc: z13.number().nonnegative(),
  /** Break-even conversion rate = cpc / margin (0..1). */
  breakEvenCR: z13.number(),
  /** Sampled profit-vs-CR curve for the UI chart. */
  profitAtCR: z13.array(CrCurvePointSchema).min(1),
  /** Conversion rate projected from history; null ⇒ no estimate yet. */
  projectedCR: z13.number().min(0).max(1).nullable().default(null)
});
var PivotCellSchema = z13.object({
  value: z13.number(),
  verdict: ProjectionVerdictSchema,
  here: z13.boolean().default(false)
});
var PivotAxisSchema = z13.object({
  /** What the axis varies — `orderValue`/`discountPct` or `cpc`/`cr`. */
  variable: z13.enum(["orderValue", "discountPct", "cpc", "cr"]),
  /** Display label for the axis (e.g. "Order value", "Discount %"). */
  label: z13.string().min(1),
  /** The per-tick display labels, one per row/column, in grid order. */
  ticks: z13.array(z13.string()).min(1)
});
var BreakEvenPivotSchema = z13.object({
  rowAxis: PivotAxisSchema,
  colAxis: PivotAxisSchema,
  /** Row-major grid of cells: `cells[row][col]`. */
  cells: z13.array(z13.array(PivotCellSchema).min(1)).min(1)
});
var CampaignProjectionSchema = z13.object({
  campaignId: IdSchema,
  /** The occurrence this projection was scoped to; null ⇒ campaign defaults. */
  occurrenceId: IdSchema.nullable().default(null),
  /** The per-order-type modeled waterfalls (1 bucket when audience-scoped). */
  waterfalls: z13.array(OrderTypeWaterfallSchema).min(1),
  /** Mix-weighted net profit per order (round2). */
  blendedProfit: z13.number(),
  verdict: ProjectionVerdictSchema,
  provenance: ProvenanceMapSchema,
  /** Ads-only CPC break-even block; null for discount campaigns. */
  cpc: CpcBreakEvenSchema.nullable().default(null),
  pivot: BreakEvenPivotSchema
});
var CampaignPerformanceSchema = z13.object({
  id: IdSchema,
  platformKey: PlatformKeySchema,
  /** The store this performance belongs to (a real branch, or the platform sentinel for shared). */
  storeId: IdSchema,
  /** The resolved deal name / promo code (our link to a Campaign); null when unresolved. */
  platformDealKey: z13.string().nullable().default(null),
  /** The raw platform campaign id — Talabat `customerIncentiveId` / Careem `promo_code`. */
  externalCampaignId: z13.string().min(1),
  /** The performance day (ISO instant at day start). */
  date: IsoTimestampSchema,
  orderCount: z13.number().int().nonnegative(),
  /** Gross food sales that day; null when the source omits it. */
  grossSales: z13.number().nullable().default(null),
  /** The restaurant-funded discount (Talabat vendorInvestmentValue / Careem discount spend). */
  vendorInvestment: z13.number().nonnegative(),
  newCustomerCount: z13.number().int().nonnegative().nullable().default(null),
  raw_json: RawJsonSchema
});
var ReconciliationVerdictSchema = z13.enum(["aligned", "minor_drift", "off", "untracked"]);
var ReconciliationSideSchema = z13.object({
  orders: z13.number().int().nonnegative(),
  /** Restaurant-funded discount total (round2). */
  discount: z13.number(),
  /** Gross sales total; null when unknown (our side does not always carry it). */
  grossSales: z13.number().nullable().default(null)
});
var CampaignReconciliationSchema = z13.object({
  /** Our matched Campaign id; null for the store-period TOTAL row. */
  campaignId: IdSchema.nullable().default(null),
  platformDealKey: z13.string().nullable().default(null),
  label: z13.string().min(1),
  platform: ReconciliationSideSchema,
  clarity: ReconciliationSideSchema,
  /** Order-count accuracy 0..1 (min/max). */
  ordersAccuracy: z13.number().min(0).max(1),
  /** Funded-discount accuracy 0..1 (min/max). */
  discountAccuracy: z13.number().min(0).max(1),
  verdict: ReconciliationVerdictSchema,
  /**
   * True when the platform reported per-deal performance for this campaign (a
   * real reconcilable target). False for an our-only row (a keyless collection
   * the platform does not track per-deal) — those are `untracked` and excluded
   * from the store TOTAL accuracy. The TOTAL row itself is always `true`.
   */
  platformTracked: z13.boolean().default(true)
});
var TermsSpreadEntrySchema = z13.object({
  /** Human label for the slice — the campaign name, or "Unexplained". */
  label: z13.string().min(1),
  /** The matched campaign id; null for the `unexplained` slice. */
  campaignId: IdSchema.nullable().default(null),
  /** Orders in this slice (round whole count). */
  orders: z13.number().int().nonnegative(),
  /** True for the single striped `unexplained` slice (no reconciling campaign). */
  unexplained: z13.boolean().default(false)
});
var TermsBucketSchema = z13.object({
  /** Stable signature string `center% | min | cap | audience | itemScope`. */
  key: z13.string().min(1),
  /** The bucket's quantized center percentage (e.g. 20 for the 17..23 band). */
  percentage: z13.number(),
  /** The band width in points the percentage was quantized to (the ±3 band ⇒ 6). */
  percentageBandWidth: z13.number().positive().default(6),
  /** The signature's minimum order value; null ⇒ no min constraint. */
  minOrder: z13.number().nullable().default(null),
  /** The signature's per-order discount cap; null ⇒ uncapped. */
  cap: z13.number().nullable().default(null),
  /** The signature's audience (from the matched campaign, else the order signal). */
  audience: CampaignAudienceSchema.default("all"),
  /** Whether the signature is an item-scoped deal + an optional label for it. */
  itemScope: z13.object({
    scoped: z13.boolean().default(false),
    label: z13.string().nullable().default(null)
  }).default({ scoped: false, label: null }),
  /** Discounted orders carrying this signature. */
  orderCount: z13.number().int().nonnegative(),
  /** Restaurant-funded discount total for the bucket (round2). */
  discountTotal: z13.number(),
  /** The split of the bucket's orders across campaigns + the unexplained slice. */
  spread: z13.array(TermsSpreadEntrySchema).default([])
});
var TermsResultSchema = z13.object({
  buckets: z13.array(TermsBucketSchema).default([]),
  summary: z13.object({
    /** Total discounted orders in range (Σ bucket.orderCount). */
    discountedOrders: z13.number().int().nonnegative(),
    /** Restaurant-funded discount total across all buckets (round2). */
    discountTotal: z13.number(),
    /** Share of discounted orders tied to a campaign, 0..1. */
    tiedPct: z13.number().min(0).max(1),
    /** Total discounted orders with no reconciling campaign. */
    unexplained: z13.number().int().nonnegative()
  })
});
var TermHistogramBinSchema = z13.object({
  /** The bin's human label, e.g. "20–25%" or "≥50%". */
  label: z13.string().min(1),
  /** Orders in this bucket whose observed % falls in this bin (whole count). */
  count: z13.number().int().nonnegative()
});
var TermDetailOrderSchema = z13.object({
  /** Internal order id. */
  orderId: z13.string().min(1),
  /** The platform's external order id (shown in the table). */
  externalOrderId: z13.string(),
  /** When the order was placed — an ISO-8601 timestamp (sort key, newest first). */
  placedAt: z13.string(),
  /** The order subtotal (round2). */
  subtotal: z13.number(),
  /** Restaurant-funded discount for the order (round2). */
  discount: z13.number(),
  /** Observed discount share in PERCENT units (20 = 20%). */
  observedPct: z13.number(),
  /** Primary campaign name, or "Unexplained". */
  attributedLabel: z13.string().min(1),
  /** Primary matched campaign id; null when unexplained. */
  campaignId: IdSchema.nullable().default(null),
  /** Primary match confidence; null when unexplained. */
  confidence: z13.enum(["high", "medium", "low"]).nullable().default(null),
  /** Subscription (Plus/tPro) order. */
  isPro: z13.boolean().default(false),
  /** New-customer-targeted order. */
  newCustomer: z13.boolean().default(false)
});
var TermBucketDetailSchema = z13.object({
  /** Stable signature string — IDENTICAL to the list bucket's `key`. */
  key: z13.string().min(1),
  /** The bucket's declared percentage (e.g. 50); 0 for Unexplained. */
  percentage: z13.number(),
  /** The band width the percentage carries (exact declared rate ⇒ sentinel). */
  percentageBandWidth: z13.number().positive().default(1),
  /** The signature's minimum order value; null ⇒ no min constraint. */
  minOrder: z13.number().nullable().default(null),
  /** The signature's per-order discount cap; null ⇒ uncapped. */
  cap: z13.number().nullable().default(null),
  /** The signature's audience. */
  audience: CampaignAudienceSchema.default("all"),
  /** Whether the signature is an item-scoped deal + an optional label for it. */
  itemScope: z13.object({
    scoped: z13.boolean().default(false),
    label: z13.string().nullable().default(null)
  }).default({ scoped: false, label: null }),
  /** Roll-up over ALL the bucket's orders (not just the returned page). */
  summary: z13.object({
    /** Order count carrying this signature. */
    orders: z13.number().int().nonnegative(),
    /** Σ subtotal (round2). */
    subtotal: z13.number(),
    /** Σ restaurant-funded discount (round2). */
    restaurantFunded: z13.number(),
    /** Σ platform-funded discount (round2). */
    platformFunded: z13.number(),
    /** Σ restaurantFunded / Σ subtotal × 100 (the diluted effective rate). */
    effectiveDiscountPct: z13.number(),
    /** Σ subtotal / orders (round2); 0 when no orders. */
    avgBasket: z13.number(),
    /** Count of subscription (Plus/tPro) orders. */
    pro: z13.number().int().nonnegative(),
    /** Count of new-customer-targeted orders. */
    newCustomer: z13.number().int().nonnegative(),
    /** Σ restaurantFunded / orders (round2); 0 when no orders. */
    avgDiscountPerOrder: z13.number()
  }),
  /** Observed-% histogram over ALL the bucket's orders (fixed bins). */
  histogram: z13.array(TermHistogramBinSchema).default([]),
  /** The histogram bin label the DECLARED rate falls in; null for Unexplained. */
  declBin: z13.string().nullable().default(null),
  /** The requested PAGE of orders, newest-first (page/pageSize/total describe it). */
  orders: z13.array(TermDetailOrderSchema).default([]),
  /** 1-based page index of the returned `orders` slice. */
  page: z13.number().int().positive().default(1),
  /** Page size of the returned slice. */
  pageSize: z13.number().int().positive().default(50),
  /** The bucket's FULL order count (independent of the returned page). */
  total: z13.number().int().nonnegative()
});
var ScopedItemEconomicsSchema = z13.object({
  /** The scoped product's display name. */
  name: z13.string().min(1),
  /** Catalog base price (pre-markup); round2. */
  basePrice: z13.number(),
  /** Food cost share 0..100 (catalog `foodCostPct`, store-default fallback). */
  foodCostPct: z13.number(),
  /** Food cost amount `basePrice × foodCostPct/100` (round2). */
  foodCost: z13.number(),
  /** Discount given `min(basePrice × pct, cap)` (round2). */
  discountGiven: z13.number(),
  /** True when the cap bound the discount (observed below the headline %). */
  capped: z13.boolean().default(false),
  /** Margin per order `netItemRevenue − foodCost − commission` (round2). */
  marginPerOrder: z13.number()
});
var ItemScopedDealEconomicsSchema = z13.object({
  campaignId: IdSchema,
  /** Human label for the deal (campaign name). */
  label: z13.string().min(1),
  /** Matched orders this economics was computed over. */
  matchedOrders: z13.number().int().nonnegative(),
  /** The declared headline discount fraction 0..1. */
  pct: z13.number().min(0).max(1),
  /** The declared per-order discount cap; null ⇒ uncapped. */
  cap: z13.number().nullable().default(null),
  /** The per-scoped-item economics rows (empty for a non-item-scoped campaign). */
  items: z13.array(ScopedItemEconomicsSchema).default([]),
  summary: z13.object({
    /** Average food cost across the scoped items (round2). */
    avgFoodCost: z13.number(),
    /** Average discount given across the scoped items (round2). */
    avgDiscount: z13.number(),
    /** Average margin per order across the scoped items (round2). */
    avgMargin: z13.number()
  })
});

// src/schemas/order.ts
var SettlementStatusSchema = z14.enum(["pending", "finalized", "cancelled"]);
var AccuracySchema = z14.enum([
  "pending",
  "ai_estimated",
  "platform_estimated",
  "finalized"
]);
var OrderLineItemSchema = z14.object({
  /** Platform's product identifier as it appeared on the order. */
  externalItemId: z14.string().min(1),
  name: z14.string().min(1),
  quantity: z14.number().int().nonnegative(),
  /** Per-line price as reported by the platform. */
  price: z14.number(),
  /**
   * Per-line PRE-DISCOUNT price (MyThings `OriginalPrice`). When the source omits it,
   * normalization sets it equal to `price` (Req 2.4). Defaults to null so every existing
   * order-line payload/fixture parses unchanged, and `price` is used as the pre-discount
   * value wherever `originalPrice` is null (treated as "no per-item discount observed").
   */
  originalPrice: z14.number().nullable().default(null),
  /** Per-line discount amount, when reported. */
  discount: z14.number().nullable().default(null),
  /** Chosen option identifiers, for linking to catalog options. */
  externalOptionIds: z14.array(z14.string().min(1)).default([]),
  /**
   * Per-option settled price (MyThings per-option `Price`), parallel to `externalOptionIds`:
   * `optionPrices[i]` is the price of the option `externalOptionIds[i]`. An option with no
   * captured price is null. Defaults to [] so existing payloads parse unchanged; a non-empty
   * array need not be the same length as `externalOptionIds` (a short/absent entry ⇒ null).
   */
  optionPrices: z14.array(z14.number().nullable()).default([]),
  // ── catalog linkage (Req 10.1) ──
  /** True when this line resolved to a catalog item; false ⇒ retained + flagged. */
  resolved: z14.boolean(),
  /** The linked catalog item id, when resolved. */
  catalogItemId: IdSchema.nullable().default(null),
  /**
   * The resolved food-cost percentage basis applied to this line (item/option
   * rate, else the store default for unresolved lines). `null` until costed.
   */
  foodCostPctBasis: z14.number().min(0).max(100).nullable().default(null)
});
var NormalizedOrderSchema = z14.object({
  id: IdSchema,
  /** The branch(store) this order settles on — orders are per-store (Req 8.x). */
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** The platform's own order identifier (e.g. the "Order Id" column). */
  externalOrderId: z14.string().min(1),
  /**
   * The `NormalizedPayout.id` this order settled on, set by order↔payout linking
   * (clarity-reconciliation Req 5.1). `null` until linked — not yet settled, or
   * the payout hasn't been pulled. Defaults so pre-link fixtures still parse.
   */
  payoutId: IdSchema.nullable().default(null),
  /**
   * Every campaign this order matched, set by order↔campaign matching
   * (clarity-campaign-matching Req 2.1). A **set** — an order may match more than
   * one campaign at once (a keyword ad AND a monthly deal) — kept sorted by
   * `campaignId` so re-matching is idempotent (Req 2.3). Defaults `[]` (mirroring
   * how `payoutId` was added above) so existing pre-feature order fixtures still
   * parse (Req 2.1); an order that matched nothing stays `[]` (Req 2.4).
   */
  campaignMatches: z14.array(CampaignMatchSchema).default([]),
  /** When the order was placed (ISO-8601). */
  timestamp: IsoTimestampSchema,
  /** ISO currency code for this order's amounts (e.g. "JOD"). */
  currency: z14.string().length(3),
  /** Money-settlement axis (Req 8.1). */
  settlementStatus: SettlementStatusSchema,
  /** Data-completeness axis, independent of settlement (Req 9.1). */
  accuracy: AccuracySchema,
  /** Subtotal (goods value) — present even while pending (Req 9.1 `pending` = subtotal only). */
  subtotal: z14.number(),
  /** Settlement figures; `null` while pending, never zero (Req 8.2). */
  money: NullableOrderMoneySchema,
  /** True for a Talabat Plus/Pro ("Is Subscription Order") order; drives the Plus/non-Plus mix. */
  isSubscriptionOrder: z14.boolean().default(false),
  /** Catalog-linked line items (Req 10.1). */
  lineItems: z14.array(OrderLineItemSchema).default([]),
  /**
   * The platform's PER-ORDER deal/promo identifier when the feed carries one —
   * Careem's `partner-orders` `promo_code` (= our Careem `platformDealKey`).
   * This is GROUND TRUTH for which deal the customer actually used, so the
   * matcher attributes directly to the matching campaign instead of inferring
   * from the observed discount signature (clarity-campaign-match-accuracy).
   * null when the feed carries no per-order deal tag (Talabat's detailed export
   * does not — it falls back to signature matching). Trimmed; empty ⇒ null.
   */
  dealCode: z14.string().min(1).nullable().default(null),
  /**
   * True when the platform charged a NEW-CUSTOMER targeting fee on this order
   * (Talabat's `Marketing fees reasons` = "Deal Premium Targeting Fee", a
   * Talabat-wide flat 1.17 JOD). This is ground-truth evidence the order rode a
   * NEW-CUSTOMER-targeted deal (distinct from the Plus/subscription axis —
   * `isSubscriptionOrder`). The matcher uses it to attribute the order to a
   * new-customer campaign (clarity-campaign-match-accuracy). Defaults false so
   * every pre-field order/fixture parses unchanged.
   */
  newCustomerTargeted: z14.boolean().default(false),
  /**
   * The verbatim platform response this order was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — the generalization of the
   * former `raw` field; the shared shape every Persisted_Platform_Entity adopts).
   * Optional so pre-field / test records still parse.
   */
  raw_json: RawJsonSchema
});

// src/schemas/statement.ts
import { z as z15 } from "zod";
var StatementSourceSchema = z15.enum(["xlsx", "pdf-text", "csv-aggregate", "ocr"]);
var OtherFeeSchema = z15.object({
  label: z15.string().min(1),
  amount: z15.number()
});
var DiscountSplitSchema = z15.object({
  restaurantFunded: z15.number(),
  platformFunded: z15.number()
});
var AccountFlowSchema = z15.object({
  openingBalance: z15.number(),
  downpayment: z15.number(),
  finalBalance: z15.number()
});
var NormalizedStatementSchema = z15.object({
  id: IdSchema,
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** Reporting period (inclusive start / exclusive end), ISO-8601. */
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  // ── period totals (Req 8.5) ──
  /** Gross sales for the period (`= Net Sales` from the SOA, Req 8.5). */
  totalGross: z15.number(),
  /**
   * What the period EARNED, net: the SOA's "Month Net" (`Total Earning − Total
   * Expenses`; Total Earning is Net Sales plus any "Order Cost Reimbursement").
   * Not the same as what was paid out in the month — see {@link AccountFlowSchema}.
   */
  totalNetPayout: z15.number(),
  /** Total commission charged for the period. */
  totalCommission: z15.number(),
  /** Marketing fees (`Premium Listing + Targeted Deal + Keyword Ads`, Req 8.5). */
  totalMarketingFees: z15.number(),
  /** Discounts split by funder (Req 8.3). */
  discounts: DiscountSplitSchema,
  /** Any other labelled fees, unioned by label during merge (Req 12.1). */
  otherFees: z15.array(OtherFeeSchema).default([]),
  /**
   * The SOA's cash-flow lines (opening balance / paid in the month / closing
   * balance). `null` when the source didn't carry them (e.g. an XLSX statement,
   * or one stored before they were read).
   */
  accountFlow: AccountFlowSchema.nullable().default(null),
  // ── merge metadata (Req 12.1) ──
  /** The source that produced this (pre-merge) statement. */
  source: StatementSourceSchema,
  /** Per-field provenance: which source filled each scalar after merge. */
  provenance: z15.record(z15.string(), StatementSourceSchema).default({}),
  /** True when any contributing value is estimated (e.g. an OCR source). */
  estimated: z15.boolean().default(false),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  /**
   * The verbatim platform response this statement was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — the shared shape every
   * Persisted_Platform_Entity adopts). Optional so pre-field / test records
   * still parse.
   */
  raw_json: RawJsonSchema
});

// src/schemas/payout.ts
import { z as z16 } from "zod";
var VouchersByBrandSchema = z16.record(z16.string(), z16.number());
var AdditionalSalesSchema = z16.object({
  total: z16.number().default(0),
  byReason: z16.record(z16.string(), z16.number()).default({})
});
var PayoutBreakdownItemSchema = z16.object({
  label: z16.string(),
  amount: z16.number()
});
var PayoutBreakdownGroupSchema = z16.object({
  total: z16.number(),
  items: z16.array(PayoutBreakdownItemSchema)
});
var PayoutBreakdownSchema = z16.object({
  grossSales: z16.number(),
  cashCollected: z16.number(),
  commissions: PayoutBreakdownGroupSchema,
  marketingFees: PayoutBreakdownGroupSchema,
  /** Payment fee + any additional charges. `paymentFee` is the reported payment-fee line. */
  additionalFees: PayoutBreakdownGroupSchema.extend({ paymentFee: z16.number() }),
  vouchers: z16.object({ bySelf: z16.number(), byBrand: z16.number() }),
  discounts: z16.object({ bySelf: z16.number(), byBrand: z16.number() }),
  /** Additional sales: named components + `additionalEarnings[]` reason lines. */
  additionalSales: PayoutBreakdownGroupSchema.extend({
    deliveryFees: z16.number(),
    packagingFees: z16.number(),
    movFees: z16.number(),
    foodCostReimbursement: z16.number(),
    tips: z16.number()
  }),
  customerRefundCharges: PayoutBreakdownGroupSchema,
  loans: PayoutBreakdownGroupSchema,
  taxCharges: PayoutBreakdownGroupSchema,
  customerFees: PayoutBreakdownGroupSchema,
  waitTimeFee: z16.number(),
  /** The node-level `taxes` figure, when reported. */
  taxes: z16.number(),
  currentBalance: z16.number().nullable()
});
var NormalizedPayoutSchema = z16.object({
  id: IdSchema,
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** The platform's payout identifier (e.g. "15467760"). */
  externalPayoutId: z16.string().min(1),
  /** Payout period (ISO-8601). */
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  /** Net payout — the settlement target (`← totalNetPayout`, Req 8.6). */
  netPayout: z16.number(),
  /** Gross sales for the payout window. */
  grossSales: z16.number(),
  /** Total commissions deducted. */
  commissions: z16.number(),
  /** Total fees deducted (marketing + additional/payment fees). */
  fees: z16.number(),
  /**
   * The ADVERTISING portion of the payout's marketing fees, as a positive
   * magnitude (clarity-ads-spend R1): Σ|amount| of ad-type `marketingFee[]`
   * entries (Search CPC/Keyword, Premium Listing, Targeted/Sponsored deals),
   * EXCLUDING the per-order Pro-delivery loyalty charge. An additional read-only
   * breakout of what's already summed into `fees`, surfaced so the Step-3 money
   * breakdown can show ads as a cost. Defaults 0.
   */
  marketingAds: z16.number().nonnegative().default(0),
  /**
   * Careem-only itemized cycle adjustments (clarity-careem-payout-status Req 2),
   * read-only breakouts of what's already summed into `grossSales`/`fees` so the
   * weekly-cycle expand can show each line (gross − partner-funded promo ⇒ order
   * earnings − commission − payment-gateway fee − Careem Plus − bank fee − ads =
   * net). `null` for Talabat and for cycles whose detail wasn't itemized, so a
   * Talabat payout parses + behaves byte-identically. The collapsed `fees` total
   * is unchanged (= `paymentGatewayFee + careemPlus + bankTransferFee`).
   */
  careemCycle: z16.object({
    partnerFundedPromo: z16.number().nonnegative(),
    /**
     * Σ promo-sharing (+tax) Careem books at the CYCLE level — a discount NOT
     * deducted from the per-order earnings, so the order-based rebuild must
     * subtract it to match the cycle balance. Defaults 0 (older rows / none).
     */
    promoSharing: z16.number().nonnegative().default(0),
    paymentGatewayFee: z16.number().nonnegative(),
    careemPlus: z16.number().nonnegative(),
    bankTransferFee: z16.number().nonnegative(),
    /** Σ clawback (+tax) Careem reclaimed this cycle. Defaults 0 (older rows). */
    clawback: z16.number().nonnegative().default(0),
    /** SIGNED carry-over: the previous cycle's balance Careem rolled into this one. */
    carryOver: z16.number().default(0)
  }).nullable().optional(),
  /** Voucher totals split by brand (`← breakdown.vouchers.vouchersByBrand`, Req 8.6). */
  vouchersByBrand: VouchersByBrandSchema.default({}),
  /**
   * Account-level additional sales (`← breakdown.additionalSales`): adjustments,
   * tax pass-through, reimbursements, tips, etc. Part of `netPayout` but not
   * attributable to orders. Defaults `{ total: 0, byReason: {} }` so pre-existing
   * payout blobs (normalized before this field was captured) still parse.
   */
  additionalSales: AdditionalSalesSchema.default({ total: 0, byReason: {} }),
  /**
   * The full reported breakdown for line-by-line reconciliation. `null` on rows
   * normalized before it was captured (they are re-fetched on the next pull).
   */
  breakdown: PayoutBreakdownSchema.nullable().default(null),
  /**
   * The `NormalizedOrder.id`s linked to this payout by window-based linking
   * (clarity-reconciliation Req 5.1), kept SORTED for deterministic, idempotent
   * re-linking. Defaults `[]` so pre-link fixtures still parse.
   */
  linkedOrderIds: z16.array(IdSchema).default([]),
  /**
   * The order count the payout itself reports (`payoutOrders`/`ordersCount`,
   * §4) — the coverage target reconciliation checks `linkedOrderIds.length`
   * against. `null` until linking copies it from the reported figure, so
   * pre-link fixtures still parse.
   */
  expectedOrderCount: z16.number().int().nonnegative().nullable().default(null),
  /**
   * The day Talabat PAID this settlement (`payout.at` / the invoice's processed
   * date), as 00:00 UTC of that day. `null` when unknown (stored before it was
   * read). Cash is reconciled by this date, not by the earning window: a payout
   * for late-May orders is paid in June.
   */
  paidAt: IsoTimestampSchema.nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  /**
   * The verbatim platform response this payout was normalized from, for
   * provenance/debug (unified-data-gathering Req 1.4 — the shared shape every
   * Persisted_Platform_Entity adopts). DISTINCT from `breakdown`, which is the
   * NORMALIZED component-split view: this is the untouched platform JSON.
   * Optional so pre-field / test records still parse.
   */
  raw_json: RawJsonSchema
});

// src/schemas/reconciliation.ts
import { z as z17 } from "zod";
var GrainSchema = z17.enum([
  "payout",
  "orders-csv",
  "detailed-xlsx",
  "soa-allocated"
]);
var LineEconomicsSchema = z17.object({
  /** The platform's own line identifier (or the item name, for the CSV grain). */
  externalItemId: z17.string().min(1),
  name: z17.string().min(1),
  quantity: z17.number().int().nonnegative(),
  /** Whether the line matched a catalog item (Req 10.1, 10.2). */
  resolved: z17.boolean(),
  /** The matched catalog item id, or null when unresolved. */
  catalogItemId: IdSchema.nullable(),
  /** How the match was made — by platform id, by normalized name, by a merged
   * alias (catalog-item-aliases Req 10.1, 10.2, 10.5), or not at all. */
  matchBasis: z17.enum(["id", "name", "alias", "unresolved"]),
  /** Pre-markup base price (per unit). */
  basePrice: z17.number(),
  /** This line's share of the order's real discount pool (Req 10.3–10.5). */
  allocatedDiscount: z17.number(),
  foodCostPct: z17.number(),
  foodCostBasis: z17.enum(["item", "option", "store-default"]),
  /** Food cost — per unit × quantity. */
  foodCost: z17.number(),
  /** Commission rate; null when `comm_pct` is unavailable on a miss. */
  commissionPct: z17.number().nullable(),
  commissionBasis: z17.enum(["order-allocated", "store-default", "unavailable"]),
  /** Line commission; null + line flagged when unavailable (Req 10.6). */
  commission: z17.number().nullable(),
  /** basePrice × quantity − allocatedDiscount. */
  netItemRevenue: z17.number(),
  /** netItemRevenue − foodCost − commission − allocatedFeeShare; null when
   * commission is null. */
  itemMargin: z17.number().nullable(),
  /**
   * OPTIONAL add-on breakdown (clarity-catalog-options Req 4.9). `addOnBase` is
   * the per-unit pre-markup add-on value Σ optQty × max(priceDelta, 0);
   * `optionFoodCost` is the option share of `foodCost` (× quantity). The counts
   * are the line's option tokens, those matched, and those matched to a priced
   * (not needs-a-price) option. Absent on lines costed before this change.
   */
  addOnBase: z17.number().optional(),
  optionFoodCost: z17.number().optional(),
  optionsTotal: z17.number().int().nonnegative().optional(),
  optionsMatched: z17.number().int().nonnegative().optional(),
  optionsPriced: z17.number().int().nonnegative().optional()
});
var OrderEconomicsSchema = z17.object({
  /** The platform's own order identifier this projection is for. */
  externalOrderId: z17.string().min(1),
  /** The source grain this projection was computed from (Req 3.1). */
  grain: GrainSchema,
  /** The order's data-completeness axis, carried through so Finalized is
   * distinguishable from Platform-estimated (Req 3.4). */
  accuracy: AccuracySchema,
  /** Goods value — present even while pending (Req 3.3). */
  subTotal: z17.number(),
  // ── settlement-dependent figures — null while pending, never zero (Req 3.3) ──
  totalSale: z17.number().nullable(),
  commissionableGmv: z17.number().nullable(),
  commissionPreVat: z17.number().nullable(),
  commissionOnPayout: z17.number().nullable(),
  netPayout: z17.number().nullable(),
  /** Discount funded by the restaurant (always present; 0 when none). */
  restaurantFundedDiscount: z17.number(),
  /** Discount funded by the platform (always present; 0 when none). */
  platformFundedDiscount: z17.number(),
  /** True ⇒ the reconciler skips this order from the standard assertions
   * (partial-refund handling, Req 3.5). */
  excludedFromAssertions: z17.boolean().default(false),
  /** Why the order is excluded, when it is; null otherwise. */
  exclusionReason: z17.string().nullable().default(null),
  // ── item-level breakdown (Req 10, design §3a) ──
  /** The matched, discount-allocated, costed order lines. Defaults to `[]` so
   * the order-level projection (which doesn't set lines until task 3A.4) and
   * existing fixtures still parse. */
  lines: z17.array(LineEconomicsSchema).default([]),
  /** Σ line `foodCost` — nullable (null until the line pipeline runs). */
  foodCostTotal: z17.number().nullable().default(null),
  /** Σ line `itemMargin` — nullable (null until the line pipeline runs). */
  marginTotal: z17.number().nullable().default(null)
});
var VerdictSchema = z17.enum(["reconciled", "discrepant", "incomplete"]);
var DiscrepancyKindSchema = z17.enum([
  "coverage",
  "amount",
  "self-consistency",
  "cross-grain"
]);
var DiscrepancySchema = z17.object({
  kind: DiscrepancyKindSchema,
  /** Human-readable name of the check, e.g. "payout.net vs reconstructed". */
  check: z17.string().min(1),
  expected: z17.number().nullable(),
  actual: z17.number().nullable(),
  delta: z17.number().nullable(),
  /** Human-readable explanation of the disagreement. */
  message: z17.string().min(1)
});
var ReconciliationReportSchema = z17.object({
  id: IdSchema,
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  /** Whether this report reconciles a single payout or a whole period. */
  scope: z17.enum(["payout", "period"]),
  /** The thing reconciled: a payoutId, or `${periodStart}..${periodEnd}`. */
  scopeRef: z17.string().min(1),
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  verdict: VerdictSchema,
  /** The tolerance the checks ran at (0.01). */
  tolerance: z17.number(),
  discrepancies: z17.array(DiscrepancySchema).default([]),
  /** Which grains were absent — populated only when `verdict` is `incomplete`. */
  missingGrains: z17.array(z17.string()).default([]),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/notifications.ts
import { z as z18 } from "zod";
var ChannelSchema = z18.enum(["email", "whatsapp"]);
var E164 = /^\+[1-9]\d{0,14}$/;
var RecipientContactSchema = z18.object({
  email: z18.string().email().optional(),
  whatsapp: z18.string().regex(E164, "must be E.164 (+ then 1\u201315 digits)").optional()
});
var RecipientSchema = z18.object({
  subscriberId: z18.string().min(1).max(128),
  contact: RecipientContactSchema,
  locale: z18.string().min(2).max(35)
  // BCP-47 language tag, e.g. "en", "ar-AE"
});
var ChannelPreferencesSchema = z18.array(ChannelSchema).min(1).max(2).refine((cs) => new Set(cs).size === cs.length, "channels must be distinct");
var NotificationEventEnvelopeSchema = z18.object({
  workflowKey: z18.string().min(1),
  payload: z18.record(z18.unknown()),
  recipient: RecipientSchema,
  channelPreferences: ChannelPreferencesSchema
});
var TEST_PING_WORKFLOW_KEY = "clarity.test-ping";
var TestPingPayloadSchema = z18.object({
  message: z18.string().min(1),
  // required
  echo: z18.string().optional(),
  // optional
  issuedAt: z18.string().datetime().optional()
  // optional ISO instant
});

// src/schemas/subscription.ts
import { z as z19 } from "zod";
var JodAmountSchema = z19.number().finite().min(0).max(999999.99).refine((n) => Math.round(n * 100) === n * 100, "at most two decimal places");
var JodInvoiceAmountSchema = z19.number().finite().min(0).max(99999999999e-2).refine((n) => Math.round(n * 100) === n * 100, "at most two decimal places");
var SubscriptionStatusSchema = z19.enum(["active", "suspended", "cancelled"]);
var E1642 = /^\+[1-9]\d{0,14}$/;
var BillingContactSchema = z19.object({
  email: z19.string().min(1).max(254).email().optional(),
  whatsapp: z19.string().regex(E1642, "must be E.164 (+ then 1\u201315 digits)").optional()
});
var SubscriptionSchema = z19.object({
  id: IdSchema,
  clientId: IdSchema,
  // the owning tenant; UNIQUE (one subscription per client, Req 1.2)
  activationDate: IsoTimestampSchema,
  // the anniversary anchor (Req 1.3, 5)
  priceOverride: JodAmountSchema.optional(),
  // wins over the ladder when present (Req 3.2)
  addonEnabled: z19.boolean(),
  // account-manager add-on toggle (Req 3.5)
  addonDiscount: z19.number().finite().min(0).max(100).optional(),
  // 0–100 JOD, 2dp (Req 3.6)
  status: SubscriptionStatusSchema,
  billingContact: BillingContactSchema,
  // Req 13.2 — lives on the subscription (see Decisions)
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var PricingResultSchema = z19.object({
  enabledStoreCount: z19.number().int().min(0),
  baseSubscriptionPrice: JodAmountSchema,
  // override wins else ladder (Req 3.1/3.2)
  addonAmount: JodAmountSchema,
  // 0 | 100 | discounted (Req 3.5/3.6)
  totalMonthly: JodAmountSchema
  // base + addon, 2dp (Req 1.5)
});
var SubscriptionReadSchema = z19.object({
  subscription: SubscriptionSchema,
  pricing: PricingResultSchema
});

// src/schemas/invoice.ts
import { z as z20 } from "zod";
var LineItemKindSchema = z20.enum(["base", "account_manager", "proration"]);
var InvoiceLineItemSchema = z20.object({
  kind: LineItemKindSchema,
  description: z20.string().min(1),
  basis: z20.number().finite().optional(),
  // e.g. enabled-store count for the base line
  amount: z20.number().finite()
  // JOD, 2dp, signed; validated ±range by kind at assembly
});
var InvoiceStatusSchema = z20.enum(["issued", "paid", "overdue", "void"]);
var PaymentMethodSchema = z20.enum(["manual", "meps"]);
var PaymentRecordSchema = z20.object({
  id: IdSchema,
  invoiceId: IdSchema,
  amount: JodInvoiceAmountSchema,
  // equals the invoice total due (Req 9.3/9.4)
  method: PaymentMethodSchema,
  recordedBy: IdSchema,
  // the acting admin's user id (Req 9.3)
  paidAt: IsoTimestampSchema
  // server UTC time at record creation
});
var InvoiceSchema = z20.object({
  id: IdSchema,
  clientId: IdSchema,
  subscriptionId: IdSchema,
  periodStart: IsoTimestampSchema,
  // inclusive
  periodEnd: IsoTimestampSchema,
  // exclusive — identifies the cycle (Req 8.4 idempotency key)
  lineItems: z20.array(InvoiceLineItemSchema),
  total: JodInvoiceAmountSchema,
  // = Σ rounded line items (Req 7.5/7.6)
  status: InvoiceStatusSchema,
  issueDate: IsoTimestampSchema,
  // = generation date (Req 8.1)
  dueDate: IsoTimestampSchema,
  // issueDate + configured term; ≥ issueDate (Req 8.1)
  pdfState: z20.enum(["stored", "not_stored"]),
  // Req 8.7/8.8 — raw key omitted from the wire
  graceStartedAt: IsoTimestampSchema.nullable(),
  // set = dueDate when it goes overdue (Req 10.1)
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});

// src/schemas/billing-events.ts
import { z as z21 } from "zod";
var BillingEventBaseSchema = z21.object({
  clientId: IdSchema,
  invoiceId: IdSchema,
  invoiceTotal: z21.number().finite().min(0.01).max(99999999999e-2),
  // Req 12.1
  dueDate: IsoTimestampSchema,
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema
});
var INVOICE_ISSUED_WORKFLOW_KEY = "clarity.invoice-issued";
var PAYMENT_OVERDUE_WORKFLOW_KEY = "clarity.payment-overdue";
var SUBSCRIPTION_SUSPENDED_WORKFLOW_KEY = "clarity.subscription-suspended";
var InvoiceIssuedPayloadSchema = BillingEventBaseSchema.extend({
  issueDate: IsoTimestampSchema
});
var PaymentOverduePayloadSchema = BillingEventBaseSchema.extend({
  graceEndsAt: IsoTimestampSchema
  // dueDate + 72h (informational)
});
var SubscriptionSuspendedPayloadSchema = BillingEventBaseSchema.extend({
  suspendedAt: IsoTimestampSchema,
  disabledStoreCount: z21.number().int().min(0)
});

// src/schemas/whatsapp-messaging.ts
import { z as z22 } from "zod";
var ContactRoleSchema = z22.enum(["owner", "manager", "other"]);
var ContactLocaleSchema = z22.enum(["ar", "en"]);
var OptInSourceSchema = z22.enum([
  "lead_form",
  "onboarding",
  "verbal",
  "inbound_message",
  "admin"
]);
var MessageChannelSchema = z22.enum(["whatsapp", "email", "web"]);
var MessageStatusSchema = z22.enum([
  "expected",
  "sent",
  "delivered",
  "read",
  "skipped",
  "failed"
]);
var SkipReasonSchema = z22.enum([
  "no_contact",
  "no_opt_in",
  "out_of_scope",
  "duplicate",
  "not_configured",
  "template_unavailable",
  "invalid_variables"
]);
var TriggerKindSchema = z22.enum([
  "test",
  "billing",
  "monthly_review",
  "manual",
  "campaign"
]);
var DeclaredCategorySchema = z22.enum(["utility", "marketing", "service"]);
var BranchScopeSchema = z22.union([
  z22.literal("all"),
  z22.object({ storeIds: z22.array(IdSchema).min(1) })
]);
var E1643 = /^\+[1-9]\d{0,14}$/;
var WhatsAppContactSchema = z22.object({
  id: IdSchema,
  clientId: IdSchema,
  name: z22.string().trim().min(1),
  phone: z22.string().regex(E1643, "must be E.164 (+ then 1\u201315 digits)"),
  role: ContactRoleSchema,
  locale: ContactLocaleSchema,
  scope: BranchScopeSchema,
  optInSource: OptInSourceSchema.nullable().default(null),
  optInNote: z22.string().max(500).nullable().default(null),
  optedInAt: IsoTimestampSchema.nullable().default(null),
  optedOutAt: IsoTimestampSchema.nullable().default(null),
  optOutReason: z22.string().max(500).nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var OutboundMessageSchema = z22.object({
  id: IdSchema,
  /** Groups the rows written by one Dispatch (Req 3.1, 4.1). */
  batchId: IdSchema,
  channel: MessageChannelSchema,
  triggerKind: TriggerKindSchema,
  /** Stable trigger reference, e.g. `monthly_review:talabat:2026-11` (Req 3.1). */
  triggerRef: z22.string().min(1),
  triggerLabel: z22.string().min(1),
  clientId: IdSchema.nullable().default(null),
  storeIds: z22.array(IdSchema).default([]),
  contactId: IdSchema.nullable().default(null),
  /** E.164 phone (WhatsApp) or email address. */
  recipient: z22.string().min(1),
  templateKey: z22.string().nullable().default(null),
  templateName: z22.string().nullable().default(null),
  language: z22.string().nullable().default(null),
  declaredCategory: DeclaredCategorySchema.nullable().default(null),
  variables: z22.record(z22.string()).default({}),
  renderedText: z22.string().nullable().default(null),
  sender: z22.enum(["system", "agent", "campaign"]),
  senderName: z22.string().nullable().default(null),
  status: MessageStatusSchema,
  skipReason: SkipReasonSchema.nullable().default(null),
  errorCode: z22.string().nullable().default(null),
  /** Short, NON-SECRET failure/skip detail; never a token, cookie, or stack. */
  errorMessage: z22.string().nullable().default(null),
  /** The Chatwoot message id (WhatsApp) / Novu transaction id (email). */
  providerMessageId: z22.string().nullable().default(null),
  providerConversationId: z22.string().nullable().default(null),
  expectedAt: IsoTimestampSchema.nullable().default(null),
  sentAt: IsoTimestampSchema.nullable().default(null),
  deliveredAt: IsoTimestampSchema.nullable().default(null),
  readAt: IsoTimestampSchema.nullable().default(null),
  failedAt: IsoTimestampSchema.nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var OutboundMessageListItemSchema = OutboundMessageSchema.extend({
  recipient: z22.string().min(1)
  // already masked by the server for the list
});
var TemplateButtonSchema = z22.object({
  type: z22.enum(["quick_reply", "url"]),
  key: z22.string().min(1),
  labels: z22.record(ContactLocaleSchema, z22.string().min(1).max(20)),
  /** URL buttons only: the approved URL may end in a single `{{1}}` variable. */
  urlHasVariable: z22.boolean().default(false)
});
var TemplateButtonsSchema = z22.array(TemplateButtonSchema).max(3, "WhatsApp allows at most 3 buttons per message").refine(
  (bs) => bs.filter((b) => b.type === "url").length <= 2,
  "WhatsApp allows at most 2 URL buttons per message"
);
var TemplateDriftSchema = z22.enum([
  "none",
  "category_changed",
  "not_approved",
  "missing",
  "body_mismatch",
  "unknown"
]);
var TemplateHealthSchema = z22.object({
  templateKey: z22.string().min(1),
  templateName: z22.string().min(1),
  declaredCategory: DeclaredCategorySchema,
  /** Meta's category as reported by Chatwoot; null when unknown/unreachable. */
  metaCategory: DeclaredCategorySchema.nullable().default(null),
  /** Meta's approval status string as reported by Chatwoot; null when unknown. */
  metaStatus: z22.string().nullable().default(null),
  drift: TemplateDriftSchema
});
var TriggerRollupSchema = z22.object({
  triggerRef: z22.string().min(1),
  triggerKind: TriggerKindSchema,
  triggerLabel: z22.string().min(1),
  firstAt: IsoTimestampSchema,
  lastAt: IsoTimestampSchema,
  total: z22.number().int().nonnegative(),
  counts: z22.object({
    expected: z22.number().int().nonnegative(),
    sent: z22.number().int().nonnegative(),
    delivered: z22.number().int().nonnegative(),
    read: z22.number().int().nonnegative(),
    skipped: z22.number().int().nonnegative(),
    failed: z22.number().int().nonnegative()
  })
});
var DispatchRowSchema = z22.object({
  messageId: IdSchema,
  clientId: IdSchema.nullable(),
  contactId: IdSchema.nullable(),
  status: MessageStatusSchema,
  skipReason: SkipReasonSchema.nullable().default(null)
});
var DispatchSummarySchema = z22.object({
  batchId: IdSchema,
  triggerRef: z22.string().min(1),
  templateKey: z22.string().min(1),
  rows: z22.array(DispatchRowSchema)
});
var ListMessagesQuerySchema = z22.object({
  clientId: IdSchema.optional(),
  storeId: IdSchema.optional(),
  triggerRef: z22.string().optional(),
  triggerKind: TriggerKindSchema.optional(),
  templateKey: z22.string().optional(),
  status: MessageStatusSchema.optional(),
  sender: z22.enum(["system", "agent", "campaign"]).optional(),
  channel: MessageChannelSchema.optional(),
  from: IsoTimestampSchema.optional(),
  to: IsoTimestampSchema.optional(),
  limit: z22.coerce.number().int().positive().optional()
});
var TriggerRollupQuerySchema = z22.object({
  from: IsoTimestampSchema.optional(),
  to: IsoTimestampSchema.optional()
});
var CreateContactBodySchema = z22.object({
  name: z22.string().trim().min(1),
  phone: z22.string().regex(E1643, "must be E.164 (+ then 1\u201315 digits)"),
  role: ContactRoleSchema,
  locale: ContactLocaleSchema,
  scope: BranchScopeSchema,
  /** Optional opt-in captured at create time. */
  optIn: z22.object({ source: OptInSourceSchema, note: z22.string().max(500).optional() }).optional()
});
var UpdateContactBodySchema = z22.object({
  name: z22.string().trim().min(1).optional(),
  phone: z22.string().regex(E1643, "must be E.164 (+ then 1\u201315 digits)").optional(),
  role: ContactRoleSchema.optional(),
  locale: ContactLocaleSchema.optional(),
  scope: BranchScopeSchema.optional()
}).refine((b) => Object.keys(b).length > 0, "at least one field is required");
var OptInBodySchema = z22.object({
  source: OptInSourceSchema,
  note: z22.string().max(500).optional()
});
var OptOutBodySchema = z22.object({
  reason: z22.string().max(500).optional()
});
var SendTestBodySchema = z22.object({
  contactId: IdSchema
});

// src/schemas/location.ts
import { z as z23 } from "zod";
var LocationSchema = z23.object({
  id: IdSchema,
  clientId: IdSchema,
  name: z23.string().trim().min(1),
  arabicName: z23.string().trim().min(1).nullable().default(null),
  /** Member store ids, order preserved. A store is in at most one location. */
  storeIds: z23.array(IdSchema).default([]),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var LocationStoreSchema = z23.object({
  storeId: IdSchema,
  platformKey: PlatformKeySchema,
  name: z23.string().min(1).nullable(),
  externalStoreId: z23.string().min(1),
  locationId: IdSchema.nullable()
});
var SuggestedLinkSchema = z23.object({
  key: z23.string().min(1),
  localityLabel: z23.string(),
  storeIds: z23.array(IdSchema).min(2),
  platforms: z23.array(PlatformKeySchema).min(2)
});
var ClientLocationsViewSchema = z23.object({
  locations: z23.array(LocationSchema),
  stores: z23.array(LocationStoreSchema),
  suggestions: z23.array(SuggestedLinkSchema)
});
var CreateLocationBodySchema = z23.object({
  name: z23.string().trim().min(1),
  arabicName: z23.string().trim().min(1).nullable().optional(),
  storeIds: z23.array(IdSchema).min(1)
});
var UpdateLocationBodySchema = z23.object({
  name: z23.string().trim().min(1).optional(),
  arabicName: z23.string().trim().min(1).nullable().optional(),
  storeIds: z23.array(IdSchema).min(1).optional()
}).refine((b) => Object.keys(b).length > 0, "at least one field is required");

// src/schemas/monthly-review.ts
import { z as z24 } from "zod";
var ReviewVerdictSchema = z24.enum(["join", "skip", "ask_am", "join_with_condition"]);
var ReviewHistorySchema = z24.object({
  /** Our matched order count for the prior run. */
  orders: z24.number().int().nonnegative(),
  /** Restaurant-funded discount we attributed for the prior run (JOD). */
  fundedDiscount: z24.number(),
  /** New customers the prior run drew; null when the source omits it. */
  newCustomers: z24.number().int().nonnegative().nullable().default(null),
  /** True ONLY when the platform reconciliation was `aligned` (Req 1.4, 3.3). */
  agrees: z24.boolean(),
  /** Human month label of the prior run, e.g. "October". */
  monthLabel: z24.string()
});
var SimilarByTermsSchema = z24.object({
  /** Plain-words signature of the similar terms (e.g. "50% off, capped 3 JOD"). */
  label: z24.string(),
  /** Observed profit per order of that terms bucket (JOD). */
  avgProfitPerOrder: z24.number(),
  /** Orders observed in that bucket. */
  orderCount: z24.number().int().nonnegative()
});
var ReviewLineSchema = z24.object({
  /** The campaign id (the version-chain head) this line is for. */
  campaignId: IdSchema,
  name: z24.string(),
  arabicName: z24.string().nullable().default(null),
  /** Plain-words terms description (the "what it is" line). */
  description: z24.string(),
  verdict: ReviewVerdictSchema,
  /** `even` projections are a Join marked marginal (Req 1.2). */
  marginal: z24.boolean().default(false),
  /** Expected net profit per order (JOD); null when no basis (never fabricated). */
  expectedProfitPerOrder: z24.number().nullable().default(null),
  /** Expected order range when history supports one; null otherwise. */
  expectedOrders: z24.object({ low: z24.number().int().nonnegative(), high: z24.number().int().nonnegative() }).nullable().default(null),
  /** The SAME campaign's prior run; null when none exists. */
  history: ReviewHistorySchema.nullable().default(null),
  /** The nearest campaign by terms; null when none (or history is present). */
  similar: SimilarByTermsSchema.nullable().default(null),
  /** For an item-scoped deal: items to include (healthy margin at the deal %). */
  includeItems: z24.array(z24.string()).default([]),
  /** For an item-scoped deal: items to keep out (thin/negative margin). */
  excludeItems: z24.array(z24.string()).default([]),
  /** The decision prompt (join/skip, or the % / item choice). */
  decision: z24.string(),
  /** The earliest occurrence start as an ISO calendar day; null when undated. */
  deadline: z24.string().nullable().default(null),
  /** True when the deadline is at or before the review instant + 1 day. */
  deadlineTooLate: z24.boolean().default(false),
  /** True when this campaign's co-fund was unstated and modelled at 0%. */
  coFundAssumedZero: z24.boolean().default(false)
});
var MonthlyReviewSchema = z24.object({
  storeId: IdSchema,
  storeLabel: z24.string(),
  platformKey: PlatformKeySchema,
  /** The published month as "YYYY-MM". */
  month: z24.string().regex(/^\d{4}-\d{2}$/),
  /** The kept-last-month figure when available; null otherwise. */
  keptLastMonth: z24.number().nullable().default(null),
  /** True when ANY line assumed 0% co-fund (said once at the top of the review). */
  coFundNote: z24.boolean().default(false),
  lines: z24.array(ReviewLineSchema)
});
var AmBriefSchema = z24.object({ text: z24.string() });
var PublishStateSchema = z24.enum(["draft", "published"]);
var PublishStatusSchema = z24.object({
  platformKey: PlatformKeySchema,
  month: z24.string().regex(/^\d{4}-\d{2}$/),
  state: PublishStateSchema,
  /** The last publish instant; null when never published. */
  lastPublishedAt: z24.string().nullable().default(null),
  /** Enabled stores on the platform (the fan-out size). */
  storeCount: z24.number().int().nonnegative()
});
var PublishResultSchema = z24.object({
  /** Stores a WhatsApp message was dispatched to this publish. */
  dispatched: z24.number().int().nonnegative(),
  /** Stores skipped (unchanged since last publish, or no opt-in owner). */
  skipped: z24.number().int().nonnegative(),
  /** The dispatch batch id for the Messages log (null when nothing dispatched). */
  batchId: z24.string().nullable().default(null)
});

// src/schemas/advisor-chat.ts
import { z as z25 } from "zod";
var AdvisorChannelSchema = z25.enum(["whatsapp", "web"]);
var AdvisorIntentSchema = z25.enum([
  "campaign_status",
  "whats_to_join",
  "payout_balance",
  "am_brief",
  "unknown"
]);
var AdvisorVerdictRowSchema = z25.object({
  verdict: ReviewVerdictSchema,
  marginal: z25.boolean().default(false),
  name: z25.string(),
  /** One-line figure, e.g. "18 orders \u00b7 you funded 41 JOD \u00b7 ~2.30/order". */
  sub: z25.string()
});
var BranchOptionSchema = z25.object({
  key: z25.string().min(1),
  label: z25.string(),
  /** A short hint, e.g. the platforms the place spans ("Talabat + Careem"). */
  hint: z25.string().default("")
});
var AdvisorChipSchema = z25.object({
  intent: AdvisorIntentSchema,
  label: z25.string()
});
var AdvisorBlockSchema = z25.discriminatedUnion("kind", [
  z25.object({ kind: z25.literal("text"), text: z25.string() }),
  z25.object({
    kind: z25.literal("verdicts"),
    lead: z25.string(),
    rows: z25.array(AdvisorVerdictRowSchema)
  }),
  z25.object({
    kind: z25.literal("branch_picker"),
    prompt: z25.string(),
    options: z25.array(BranchOptionSchema).min(1)
  }),
  z25.object({ kind: z25.literal("am_brief"), head: z25.string(), text: z25.string() }),
  z25.object({ kind: z25.literal("deep_link"), label: z25.string(), href: z25.string() }),
  z25.object({ kind: z25.literal("chips"), chips: z25.array(AdvisorChipSchema).min(1) })
]);
var AdvisorReplySchema = z25.object({
  intent: AdvisorIntentSchema,
  blocks: z25.array(AdvisorBlockSchema),
  /** The place we answered for; null when the reply asks the owner to pick a branch. */
  resolvedBranchLabel: z25.string().nullable().default(null),
  /** True when the message was left for a human (ambiguous / unknown). */
  handoff: z25.boolean().default(false)
});
var AdvisorMessageSchema = z25.object({
  // Opaque message id. Pure B2: these come from Chatwoot (e.g. `cw-<id>`), which
  // are NOT UUIDs, so this is a plain non-empty string, not IdSchema.
  id: z25.string().min(1),
  // The Chatwoot conversation id the message belongs to — also an opaque string.
  conversationId: z25.string().min(1),
  direction: z25.enum(["inbound", "outbound"]),
  /** Inbound text, or the outbound rendered text; null when only blocks matter. */
  text: z25.string().nullable().default(null),
  /** The outbound structured reply; null for an inbound message. */
  reply: AdvisorReplySchema.nullable().default(null),
  /**
   * Who authored this message (clarity-advisor-chat human takeover):
   * - `customer` — the owner's own message (an inbound bubble).
   * - `ai` — the Clarity Advisor's answer (carries structured `reply` blocks).
   * - `human` — a HUMAN AGENT's reply, merged in from Chatwoot after a takeover;
   *   plain text, rendered distinctly ("Clarity team") with no AI blocks.
   * Defaults by direction (inbound⇒customer, outbound⇒ai) so an older producer
   * that omits it stays correct.
   */
  sender: z25.enum(["customer", "ai", "human"]).optional(),
  /** The human agent's display name, when `sender==='human'` (from Chatwoot). */
  senderName: z25.string().nullable().default(null),
  createdAt: IsoTimestampSchema
});
var AdvisorConversationSchema = z25.object({
  id: IdSchema,
  /** The user this thread belongs to (per user + per client + per channel). */
  userId: z25.string().min(1),
  clientId: IdSchema,
  channel: AdvisorChannelSchema,
  /** The Chatwoot conversation id this thread mirrors; null until created. */
  chatwootConversationId: z25.string().nullable().default(null),
  /** The last-used branch key, so a follow-up inherits it (Req 2.2 / 4.2). */
  lastBranchKey: z25.string().nullable().default(null),
  /**
   * True when a HUMAN AGENT is handling this conversation in Chatwoot (assigned
   * to a non-bot User). While true the AI stays silent and the panel shows a
   * "Clarity team is replying" state; unassigning in Chatwoot returns control to
   * the AI. Defaults false (AI-handled). (clarity-advisor-chat human takeover.)
   */
  humanHandled: z25.boolean().default(false),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var AdvisorThreadViewSchema = z25.object({
  conversation: AdvisorConversationSchema,
  messages: z25.array(AdvisorMessageSchema),
  /**
   * Whether the LIVE (human/team) chat is available right now — i.e. Chatwoot is
   * configured with a Website inbox so the panel's messages mirror to the team
   * (clarity-advisor-chat). When false (Chatwoot unconfigured / no website inbox)
   * the panel shows "live chat not available right now". The AI advisor still
   * answers regardless; this flag is only about the human/team side. Defaults
   * true so an older producer stays compatible.
   */
  liveChatAvailable: z25.boolean().default(true)
});
var SendAdvisorMessageBodySchema = z25.object({
  text: z25.string().trim().min(1),
  /**
   * The human-readable message to STORE and show as the user's bubble, when it
   * differs from `text`. Chip/branch-picker taps send the raw intent/branch key
   * as `text` (so the server resolves it exactly) but a readable label here
   * (e.g. "What to join") so the thread never shows a raw key like
   * `whats_to_join`. Omitted for free-typed messages, where `text` is already
   * what the user wrote.
   */
  displayText: z25.string().trim().min(1).optional(),
  branchKey: z25.string().min(1).nullable().optional(),
  clientId: IdSchema.optional()
});
var AdvisorMessagesSinceSchema = z25.object({
  messages: z25.array(AdvisorMessageSchema)
});

// src/schemas/advisor-llm.ts
import { z as z26 } from "zod";
var TokenUsageSchema = z26.object({
  inputTokens: z26.number().int().nonnegative(),
  outputTokens: z26.number().int().nonnegative()
});
var AdvisorUsageViewSchema = z26.object({
  clientId: IdSchema,
  month: z26.string().regex(/^\d{4}-\d{2}$/),
  spentUsd: z26.number().nonnegative(),
  /** The effective monthly cap = override ?? global default. */
  capUsd: z26.number().nonnegative(),
  /** The per-workspace override, or null when none is set (using the default). */
  overrideUsd: z26.number().nonnegative().nullable().default(null),
  /** capUsd − spentUsd; may be negative if a cap was lowered below current spend. */
  remainingUsd: z26.number(),
  calls: z26.number().int().nonnegative()
});
var SetAdvisorCapBodySchema = z26.object({
  capUsd: z26.number().nonnegative().nullable()
});

// src/schemas/workspace-users.ts
import { z as z27 } from "zod";
var CreateWorkspaceUserBodySchema = z27.object({
  phone: z27.string().regex(/^\+[1-9]\d{0,14}$/, "must be E.164 (+ then 1\u201315 digits)"),
  /** The initial password, 8–128 chars (a floor, not a policy engine). */
  password: z27.string().min(8).max(128)
});

// src/schemas/mythings-manual.ts
import { z as z28 } from "zod";
var AmountSchema = z28.number().finite().nonnegative();
var MythingsManualPayoutSchema = z28.object({
  id: IdSchema,
  storeId: IdSchema,
  /** Inclusive window start the payout covers (ISO-8601). */
  periodStart: IsoTimestampSchema,
  /** Window end the payout covers (ISO-8601). */
  periodEnd: IsoTimestampSchema,
  /** The day the owner received it (ISO-8601). */
  paidAt: IsoTimestampSchema,
  /** The net amount MyThings paid (store currency, ≥ 0). */
  amount: AmountSchema,
  /** An optional owner note. */
  note: z28.string().nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var NewMythingsManualPayoutBodySchema = z28.object({
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  paidAt: IsoTimestampSchema,
  amount: AmountSchema,
  note: z28.string().max(500).nullable().default(null)
});
var MythingsExpenseCategorySchema = z28.enum(["marketing", "banner", "other"]);
var MythingsManualExpenseSchema = z28.object({
  id: IdSchema,
  storeId: IdSchema,
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  category: MythingsExpenseCategorySchema,
  /** The cost (store currency, ≥ 0). */
  amount: AmountSchema,
  note: z28.string().nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var NewMythingsManualExpenseBodySchema = z28.object({
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  category: MythingsExpenseCategorySchema,
  amount: AmountSchema,
  note: z28.string().max(500).nullable().default(null)
});

// src/schemas/store-analytics.ts
import { z as z29 } from "zod";
var AnalyticsSourceSchema = z29.enum(["owner", "talabat", "careem", "mythings"]);
var WeekdaySchema = z29.number().int().min(0).max(6);
var OpeningHoursDaySchema = z29.object({
  weekday: WeekdaySchema,
  /** `[open, close]` minute-of-day pairs; empty ⇒ closed that day. */
  windows: z29.array(z29.object({ open: z29.number().int().min(0).max(1440), close: z29.number().int().min(0).max(1440) })).default([])
});
var StoreAnalyticsSchema = z29.object({
  id: IdSchema,
  storeId: IdSchema,
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  source: AnalyticsSourceSchema.default("owner"),
  // ── Ratings & reviews ──
  /** Average customer rating over the period (e.g. 4.6 out of 5). `null` when unknown. */
  ratingAvg: z29.number().min(0).max(5).nullable().default(null),
  /** How many ratings the average is over. `null` when unknown. */
  ratingCount: z29.number().int().nonnegative().nullable().default(null),
  /** New reviews in the period. `null` when unknown. */
  reviewCount: z29.number().int().nonnegative().nullable().default(null),
  // ── Delivery / prep time ──
  /** Average time from order to delivered, in minutes. `null` when unknown. */
  avgDeliveryMinutes: z29.number().nonnegative().nullable().default(null),
  /** Average kitchen prep time, in minutes. `null` when unknown. */
  avgPrepMinutes: z29.number().nonnegative().nullable().default(null),
  // ── Storefront conversion funnel ──
  /** Storefront impressions / menu views. `null` when unknown. */
  impressions: z29.number().int().nonnegative().nullable().default(null),
  /** Menu opens / sessions that reached the store. `null` when unknown. */
  menuVisits: z29.number().int().nonnegative().nullable().default(null),
  /** Carts started / add-to-cart events. `null` when unknown. */
  cartsStarted: z29.number().int().nonnegative().nullable().default(null),
  /** Completed orders from those sessions (for a conversion rate). `null` when unknown. */
  conversions: z29.number().int().nonnegative().nullable().default(null),
  // ── Opening hours ──
  /** The weekly opening schedule; empty ⇒ not set. */
  openingHours: z29.array(OpeningHoursDaySchema).default([]),
  note: z29.string().nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var UpsertStoreAnalyticsBodySchema = z29.object({
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  ratingAvg: z29.number().min(0).max(5).nullable().optional(),
  ratingCount: z29.number().int().nonnegative().nullable().optional(),
  reviewCount: z29.number().int().nonnegative().nullable().optional(),
  avgDeliveryMinutes: z29.number().nonnegative().nullable().optional(),
  avgPrepMinutes: z29.number().nonnegative().nullable().optional(),
  impressions: z29.number().int().nonnegative().nullable().optional(),
  menuVisits: z29.number().int().nonnegative().nullable().optional(),
  cartsStarted: z29.number().int().nonnegative().nullable().optional(),
  conversions: z29.number().int().nonnegative().nullable().optional(),
  openingHours: z29.array(OpeningHoursDaySchema).optional(),
  note: z29.string().max(500).nullable().optional()
});
var CustomerSplitSchema = z29.object({
  orders: z29.number().int().nonnegative(),
  newCustomerOrders: z29.number().int().nonnegative().nullable(),
  returningOrders: z29.number().int().nonnegative().nullable(),
  /** `deal` = inferred from new-customer-deal attribution (Talabat); `none` = not reported. */
  basis: z29.enum(["deal", "none"])
});
var DealIncrementalitySchema = z29.object({
  campaignName: z29.string(),
  dealOrders: z29.number().int().nonnegative(),
  /** Orders/day while the deal ran. */
  ratePerDealDay: z29.number().nonnegative(),
  /** Orders/day on baseline (no-deal) days. */
  baselineRatePerDay: z29.number().nonnegative(),
  /** Estimated extra orders attributable to the deal over its run; floored at 0. */
  estimatedExtraOrders: z29.number().nonnegative(),
  /** How confident: `low` when few baseline/deal days. */
  confidence: z29.enum(["low", "medium", "high"]),
  dealDays: z29.number().int().nonnegative(),
  baselineDays: z29.number().int().nonnegative()
});

// src/platforms.ts
var PLATFORMS = {
  talabat: {
    key: "talabat",
    name: "Talabat",
    liveStatus: "live",
    capabilities: { supportsAdSpend: true, supportsMenu: true }
  },
  careem: {
    key: "careem",
    name: "Careem",
    liveStatus: "live",
    // Careem's catalog IS fetchable (the catalog-catalogs→categories→products→
    // customization-groups chain, verified live and run in-page over CDP). The
    // earlier "no menu source" read was wrong — the capture just lacked these
    // endpoints. supportsAdSpend is TRUE: Careem has one ad product (CPC), billed
    // as daily `ADVERTISEMENTS` billingEntries (NET + TAX per day), read per-day
    // via `fetchCareemAdSpendByDay`.
    capabilities: { supportsAdSpend: true, supportsMenu: true }
  },
  mythings: {
    key: "mythings",
    name: "MyThings",
    liveStatus: "live",
    // No ads/campaign source in the MyThings capture (supportsAdSpend:false);
    // the catalog IS fetchable via the GetUserMenu → Product/Category →
    // paged ProductStock chain (supportsMenu:true).
    capabilities: { supportsAdSpend: false, supportsMenu: true }
  }
};
var livePlatforms = () => Object.values(PLATFORMS).filter((p) => p.liveStatus === "live");
var capabilityOf = (k, c27) => PLATFORMS[k].capabilities[c27];
var authModelOf = (k) => k === "mythings" ? "credential" : "session";

// src/contracts/index.ts
import { initContract as initContract26 } from "@ts-rest/core";

// src/contracts/auth.ts
import { initContract } from "@ts-rest/core";
import { z as z31 } from "zod";

// src/contracts/common.ts
import { z as z30 } from "zod";
var UserResponseSchema = UserSchema.omit({ passwordHash: true });
var ClientResponseSchema = ClientSchema;
var BrandResponseSchema = BrandSchema;
var StoreResponseSchema = StoreSchema.extend({
  /**
   * `true` when the store has ANY Ingestion_Run in `running` status (reference /
   * backfill / daily) — server-derived from `ingestion_runs` (Req 8.5). Defaults
   * `false` so a raw stored store row parses unchanged.
   */
  gatherRunning: z30.boolean().default(false),
  /**
   * The `batchId` of the running gather this read observed (the key the run
   * history / admin panel groups by), so the resuming client can poll the exact
   * run. `null` when nothing is running. Defaults `null`.
   */
  gatherBatchId: z30.string().nullable().default(null)
});
var ErrorBodySchema = z30.object({
  code: z30.string(),
  message: z30.string()
});
var commonErrorResponses = {
  400: ErrorBodySchema,
  401: ErrorBodySchema,
  403: ErrorBodySchema,
  404: ErrorBodySchema
};
var OrderResponseSchema = NormalizedOrderSchema;
var OrderDetailResponseSchema = z30.object({
  /** The order record (carries `payoutId`, line items, money block, provenance). */
  order: OrderResponseSchema,
  /** The economics projected on read from the order's current grain. */
  economics: OrderEconomicsSchema
});
var OrderListItemResponseSchema = z30.object({
  /** The order record (carries `payoutId`, line items, money block, provenance). */
  order: OrderResponseSchema,
  /** The economics projected on read from the order's current grain. */
  economics: OrderEconomicsSchema
});
var StatementResponseSchema = NormalizedStatementSchema;
var PayoutResponseSchema = NormalizedPayoutSchema;
var CatalogItemResponseSchema = CatalogItemSchema;

// src/contracts/auth.ts
var c = initContract();
var LoginBodySchema = z31.object({
  phone: E164Schema,
  password: z31.string().min(1)
});
var LoginResponseSchema = z31.object({
  token: z31.string().min(1),
  user: UserResponseSchema
});
var RequestCodeBodySchema = z31.object({ phone: E164Schema });
var RequestCodeResponseSchema = z31.object({
  sent: z31.boolean(),
  /** DEV-ONLY echo of the code when WhatsApp is unconfigured; never in prod. */
  devCode: z31.string().nullable().default(null)
});
var VerifyPhoneBodySchema = z31.object({
  phone: E164Schema,
  code: z31.string().regex(/^\d{6}$/, "a 6-digit code")
});
var ResetPasswordBodySchema = z31.object({
  phone: E164Schema,
  code: z31.string().regex(/^\d{6}$/, "a 6-digit code"),
  newPassword: z31.string().min(8).max(128)
});
var authContract = c.router(
  {
    login: {
      method: "POST",
      path: "/auth/login",
      body: LoginBodySchema,
      responses: {
        200: LoginResponseSchema,
        401: ErrorBodySchema,
        400: ErrorBodySchema
      },
      summary: "Authenticate with phone + password and receive a session token."
    },
    requestVerify: {
      method: "POST",
      path: "/auth/verify/request",
      body: RequestCodeBodySchema,
      responses: {
        200: RequestCodeResponseSchema,
        429: ErrorBodySchema,
        400: ErrorBodySchema
      },
      summary: "Send a WhatsApp verification code to a phone (uniform response)."
    },
    verifyPhone: {
      method: "POST",
      path: "/auth/verify",
      body: VerifyPhoneBodySchema,
      responses: {
        200: LoginResponseSchema,
        400: ErrorBodySchema,
        429: ErrorBodySchema
      },
      summary: "Verify a phone with its code; activates login and returns a session."
    },
    requestReset: {
      method: "POST",
      path: "/auth/reset/request",
      body: RequestCodeBodySchema,
      responses: {
        200: RequestCodeResponseSchema,
        429: ErrorBodySchema,
        400: ErrorBodySchema
      },
      summary: "Send a WhatsApp password-reset code to a phone (uniform response)."
    },
    resetPassword: {
      method: "POST",
      path: "/auth/reset",
      body: ResetPasswordBodySchema,
      responses: {
        200: LoginResponseSchema,
        400: ErrorBodySchema,
        429: ErrorBodySchema
      },
      summary: "Set a new password with a reset code; returns a fresh session."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/brands.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { z as z33 } from "zod";

// src/contracts/stores.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z32 } from "zod";
var c2 = initContract2();
var CreateStoreBodySchema = z32.object({
  brandId: z32.string().uuid(),
  platformKey: PlatformKeySchema,
  externalStoreId: z32.string().min(1),
  /** Economic container incl. the single markup source; defaults applied by `makeStore`. */
  profile: StoreProfileSchema.partial().optional()
});
var DiscoveredStoreSchema = z32.object({
  /** The platform outlet id — the join key to ingested reports; feeds CreateStore. */
  externalStoreId: z32.string().min(1),
  platformKey: PlatformKeySchema,
  /** Human-facing branch name, when the portal exposes one. */
  name: z32.string().optional(),
  /** Branch area/city label, when exposed. */
  area: z32.string().optional(),
  /** Talabat chain id (brand-level), when exposed. */
  chainId: z32.string().optional(),
  /** Talabat grid, when exposed. */
  grid: z32.string().optional(),
  /** Global entity id (e.g. TB_JO), when exposed. */
  globalEntityId: z32.string().optional(),
  /** True when a store with this externalStoreId is already assigned under the caller. */
  alreadyAssigned: z32.boolean()
});
var storesContract = c2.router(
  {
    list: {
      method: "GET",
      path: "/stores",
      responses: {
        200: z32.array(StoreResponseSchema),
        ...commonErrorResponses
      },
      summary: "List stores (branches) visible to the caller (tenant-scoped)."
    },
    getById: {
      method: "GET",
      path: "/stores/:id",
      pathParams: z32.object({ id: z32.string().uuid() }),
      responses: {
        200: StoreResponseSchema,
        ...commonErrorResponses
      },
      summary: "Read a store by id incl. its lifecycle state (Req 3.5, 6.4)."
    },
    listByBrand: {
      method: "GET",
      path: "/brands/:brandId/stores",
      pathParams: z32.object({ brandId: z32.string().uuid() }),
      responses: {
        200: z32.array(StoreResponseSchema),
        ...commonErrorResponses
      },
      summary: "List a brand's branches, scoped to the caller."
    },
    discover: {
      method: "GET",
      path: "/stores/discover",
      query: z32.object({
        /** Platform to enumerate outlets for (one Chrome login per platform). */
        platformKey: PlatformKeySchema
      }),
      responses: {
        200: z32.object({
          /** Live connection state at discovery time (so an empty list is explainable). */
          connection: ConnectionStateSchema,
          /** The outlets the linked account exposes (empty when not `connected`). */
          stores: z32.array(DiscoveredStoreSchema)
        }),
        ...commonErrorResponses
      },
      summary: "Discover the platform outlets for the linked account (auto store discovery; tenant-scoped)."
    },
    create: {
      method: "POST",
      path: "/stores",
      body: CreateStoreBodySchema,
      responses: {
        201: StoreResponseSchema,
        ...commonErrorResponses
      },
      summary: 'Create (assign) a store under a brand; returns state "assigned".'
    },
    enable: {
      method: "POST",
      path: "/stores/:id/enable",
      pathParams: z32.object({ id: z32.string().uuid() }),
      body: z32.object({}).optional(),
      responses: {
        200: StoreResponseSchema,
        // 409: the lifecycle guard/transition rejected the enable (state unchanged).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Enable a store (guarded on platform + onboarding; Req 4.3)."
    },
    unassign: {
      method: "POST",
      path: "/stores/:id/unassign",
      pathParams: z32.object({ id: z32.string().uuid() }),
      body: z32.object({}).optional(),
      responses: {
        200: StoreResponseSchema,
        // 409: only an `assigned` (never-enabled) branch may be unassigned; an
        // enabled/disabled store must go through disable + the grace window.
        // 403 (via commonErrorResponses): admin-only action; a client-role
        // caller is refused regardless of tenancy.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: 'Unassign a never-enabled branch (reverse of assign; assigned \u2192 removed). Admin only; legal only from "assigned".'
    },
    disable: {
      method: "POST",
      path: "/stores/:id/disable",
      pathParams: z32.object({ id: z32.string().uuid() }),
      body: z32.object({}).optional(),
      responses: {
        200: StoreResponseSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Disable a store (stop ingestion + arm 30-day grace; Req 4.5)."
    },
    remove: {
      method: "DELETE",
      path: "/stores/:id",
      pathParams: z32.object({ id: z32.string().uuid() }),
      body: z32.object({}).optional(),
      responses: {
        // 204: the branch row was hard-deleted — fully gone from the client, so a
        // re-import recreates it fresh (there is no row to dedupe against). Admin
        // only, and legal ONLY from the `removed` lifecycle state: a live
        // (assigned / enabled / disabled) branch must go through unassign / the
        // disable+grace path first, so data is never dropped without that route.
        204: z32.undefined(),
        // 409: the branch is not in `removed` state — refuse the hard delete.
        409: ErrorBodySchema,
        // 403 (via commonErrorResponses): admin-only; a client-role caller is
        // refused regardless of tenancy.
        ...commonErrorResponses
      },
      summary: 'Hard-delete a removed branch (drops the row so a re-import recreates it fresh). Admin only; legal only from "removed".'
    },
    setCategory: {
      method: "POST",
      path: "/stores/:id/category",
      pathParams: z32.object({ id: z32.string().uuid() }),
      // The restaurant category (campaign applicability): a primary category + an
      // optional sub-category set. Drives which category-scoped monthly offers
      // apply to this branch (a `fast_food` store never matches a drinks/desserts
      // /café offer). Admin only — the portal carries no cuisine field, so the
      // admin sets it. Does NOT re-match persisted orders (a separate re-match
      // pass does); it updates the store so future matching is correct.
      body: z32.object({
        category: RestaurantCategorySchema,
        subCategories: z32.array(RestaurantCategorySchema).optional()
      }),
      responses: {
        200: StoreResponseSchema,
        // 403 (via commonErrorResponses): admin-only; a client-role caller is refused.
        ...commonErrorResponses
      },
      summary: "Set a branch's restaurant category + sub-categories (campaign applicability). Admin only."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/brands.ts
var c3 = initContract3();
var CreateBrandBodySchema = z33.object({
  clientId: z33.string().uuid(),
  platformKey: PlatformKeySchema,
  name: z33.string().min(1),
  talabatChainId: z33.string().min(1).optional(),
  talabatGrid: z33.string().min(1).optional(),
  careemBrandId: z33.string().min(1).optional(),
  careemCompanyId: z33.string().min(1).optional(),
  sharedCatalogId: z33.string().uuid().optional()
});
var DiscoveredBrandSchema = z33.object({
  platformKey: PlatformKeySchema,
  /** Talabat chain id (brand grain) — the idempotency key with clientId+platform. */
  chainId: z33.string().min(1),
  /** Talabat grid, when exposed. */
  grid: z33.string().optional(),
  /** Chain/brand display name; falls back to chainId when the portal omits it. */
  name: z33.string().min(1),
  /** The outlets under this chain. */
  branches: z33.array(DiscoveredStoreSchema),
  /** True when a brand with this chainId is already assigned under the caller's target client. */
  alreadyImported: z33.boolean()
});
var AddMythingsStoreBranchSchema = z33.object({
  /** Per-branch MyThings login username. */
  accountLogin: z33.string().min(1),
  /** Per-branch MyThings password (write-only; never read back, never returned). */
  password: z33.string().min(1)
});
var AddMythingsStoreBodySchema = z33.object({
  clientId: z33.string().uuid(),
  name: z33.string().min(1),
  branches: z33.array(AddMythingsStoreBranchSchema).min(1)
});
var AddMythingsStoreBranchResultSchema = z33.object({
  store: StoreResponseSchema,
  status: StoreStatusSchema
});
var AddMythingsStoreResponseSchema = z33.object({
  brand: BrandResponseSchema,
  branches: z33.array(AddMythingsStoreBranchResultSchema)
});
var ImportBrandBodySchema = z33.object({
  clientId: z33.string().uuid(),
  platformKey: PlatformKeySchema,
  chainId: z33.string().min(1),
  name: z33.string().min(1),
  grid: z33.string().optional(),
  branches: z33.array(DiscoveredStoreSchema)
});
var brandsContract = c3.router(
  {
    list: {
      method: "GET",
      path: "/brands",
      responses: {
        200: z33.array(BrandResponseSchema),
        ...commonErrorResponses
      },
      summary: "List brands visible to the caller (tenant-scoped)."
    },
    getById: {
      method: "GET",
      path: "/brands/:id",
      pathParams: z33.object({ id: z33.string().uuid() }),
      responses: {
        200: BrandResponseSchema,
        ...commonErrorResponses
      },
      summary: "Read a brand by id, scoped to the caller."
    },
    listByClient: {
      method: "GET",
      path: "/clients/:clientId/brands",
      pathParams: z33.object({ clientId: z33.string().uuid() }),
      responses: {
        200: z33.array(BrandResponseSchema),
        ...commonErrorResponses
      },
      summary: "List a client's brands, scoped to the caller."
    },
    create: {
      method: "POST",
      path: "/brands",
      body: CreateBrandBodySchema,
      responses: {
        201: BrandResponseSchema,
        ...commonErrorResponses
      },
      summary: "Create a brand under a client (tenant-scoped)."
    },
    discover: {
      method: "GET",
      path: "/brands/discover",
      query: z33.object({ platformKey: PlatformKeySchema, clientId: z33.string().uuid() }),
      responses: {
        200: z33.object({
          connection: ConnectionStateSchema,
          brands: z33.array(DiscoveredBrandSchema)
        }),
        ...commonErrorResponses
      },
      summary: "Discover the platform chains (as brands) + their outlets for the linked account (brand-model R4)."
    },
    import: {
      method: "POST",
      path: "/brands/import",
      body: ImportBrandBodySchema,
      responses: {
        201: z33.object({
          brand: BrandResponseSchema,
          branches: z33.array(StoreResponseSchema)
        }),
        ...commonErrorResponses
      },
      summary: "Import a discovered brand + its branches (idempotent; brand-model R5)."
    },
    addMythings: {
      method: "POST",
      path: "/brands/mythings",
      body: AddMythingsStoreBodySchema,
      responses: {
        201: AddMythingsStoreResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin-only: find-or-create a MyThings brand and create its branches as assigned stores with sealed credentials + first-probe status (the only MyThings creation path)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/clients.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z34 } from "zod";
var c4 = initContract4();
var CreateClientBodySchema = z34.object({
  name: z34.string().min(1),
  /** Optional tenant contact email (1–254, valid email). */
  email: z34.string().min(1).max(254).email().optional(),
  /** Optional tenant contact phone, E.164 (+ then 1–15 digits). */
  phone: z34.string().regex(/^\+[1-9]\d{0,14}$/, "must be E.164 (+ then 1\u201315 digits)").optional()
});
var clientsContract = c4.router(
  {
    list: {
      method: "GET",
      path: "/clients",
      responses: {
        200: z34.array(ClientResponseSchema),
        ...commonErrorResponses
      },
      summary: "List clients (admin: all; client: own only)."
    },
    getById: {
      method: "GET",
      path: "/clients/:id",
      pathParams: z34.object({ id: z34.string().uuid() }),
      responses: {
        200: ClientResponseSchema,
        ...commonErrorResponses
      },
      summary: "Read a client by id, scoped to the caller."
    },
    create: {
      method: "POST",
      path: "/clients",
      body: CreateClientBodySchema,
      responses: {
        201: ClientResponseSchema,
        ...commonErrorResponses
      },
      summary: "Create a client (admin only)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/connections.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z35 } from "zod";
var c5 = initContract5();
var connectionsContract = c5.router(
  {
    talabatStatus: {
      method: "GET",
      path: "/connections/talabat",
      responses: {
        200: ConnectionStatusSchema,
        ...commonErrorResponses
      },
      summary: "Read the live Talabat connection status (tenant-scoped; Req 5.2)."
    },
    adminConnections: {
      method: "GET",
      path: "/admin/connections",
      responses: {
        200: z35.array(AdminConnectionEntrySchema),
        ...commonErrorResponses
      },
      summary: "Read the live connection status of every live platform (admin only; session- or credential-shaped per auth model; Req 4.1, 4.2, 4.4)."
    },
    putMythingsStoreCredentials: {
      method: "PUT",
      path: "/admin/connections/mythings/stores/:storeId/credentials",
      pathParams: z35.object({ storeId: IdSchema }),
      body: MythingsCredentialWriteSchema,
      responses: {
        // The refreshed per-store status after storing + re-probing — never a credential.
        200: StoreStatusSchema,
        ...commonErrorResponses
      },
      summary: "Admin-only write-only upsert of a MyThings store credential; stores it reversibly-encrypted and returns the refreshed store status (never reads a credential back)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/sync.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z36 } from "zod";
var c6 = initContract6();
var SyncSourceSchema = z36.enum(["orders", "payouts", "statements", "detailed"]);
var SyncPersistedSchema = z36.object({
  orders: z36.number().int().nonnegative(),
  statements: z36.number().int().nonnegative(),
  payouts: z36.number().int().nonnegative()
});
var SyncSummarySchema = z36.object({
  /** `true` when at least one of the four sources failed to pull (Req 7.5). */
  incomplete: z36.boolean(),
  /** The sources that failed to pull (empty when the run was complete). */
  failedSources: z36.array(SyncSourceSchema),
  /** `true` when the (clamped) window was empty, so NO pull was issued (Req 7.4). */
  skipped: z36.boolean(),
  /** Counts persisted this run, per source. */
  persisted: SyncPersistedSchema
});
var syncContract = c6.router(
  {
    sync: {
      method: "POST",
      path: "/stores/:id/sync",
      pathParams: z36.object({ id: z36.string().uuid() }),
      body: z36.object({}).optional(),
      responses: {
        200: SyncSummarySchema,
        ...commonErrorResponses
      },
      summary: "Run the ingestion orchestrator for a store via the acquisition session (no upload; Req 7.2)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/onboarding.ts
import { initContract as initContract8 } from "@ts-rest/core";
import { z as z38 } from "zod";

// src/contracts/payout-schedule.ts
import { initContract as initContract7 } from "@ts-rest/core";
import { z as z37 } from "zod";
var PayoutScheduleRowTypeSchema = z37.enum(["window", "monthly_closing"]);
var PayoutScheduleRowSchema = z37.object({
  type: PayoutScheduleRowTypeSchema,
  /** Coverage window start (ISO-8601 date); `null` for a monthly-closing row. */
  coverageStart: IsoTimestampSchema.nullable(),
  /** Coverage window end (ISO-8601 date); `null` for a monthly-closing row. */
  coverageEnd: IsoTimestampSchema.nullable(),
  /** The scheduled payout/transfer date (ISO-8601). */
  payoutDate: IsoTimestampSchema,
  /** Optional human label (e.g. "Month 9 closing"). */
  label: z37.string().optional()
});
var PayoutScheduleSchema = z37.object({
  id: IdSchema,
  platformKey: PlatformKeySchema,
  /** The calendar month this schedule covers, `YYYY-MM`. */
  month: z37.string().regex(/^\d{4}-\d{2}$/, "month must be YYYY-MM"),
  /** The payout rows, in schedule order. */
  rows: z37.array(PayoutScheduleRowSchema),
  /** Effective-dating: when this version became current. */
  validFrom: IsoTimestampSchema,
  /** Effective-dating: when it was superseded; `null` ⇒ current. */
  validTo: IsoTimestampSchema.nullable(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var UpcomingPayoutSchema = z37.object({
  /** The scheduled payout date (ISO-8601). */
  payoutDate: IsoTimestampSchema,
  /** The coverage window it settles; `null`/`null` for a monthly-closing row. */
  coverageStart: IsoTimestampSchema.nullable(),
  coverageEnd: IsoTimestampSchema.nullable(),
  type: PayoutScheduleRowTypeSchema,
  label: z37.string().optional(),
  /** `true` when `payoutDate` is the store-local TODAY (the "payout due today" cue). */
  dueToday: z37.boolean(),
  /** Best-effort estimated amount (a slice of what's owed); `null` for a closing row. */
  amount: z37.number().nullable(),
  /** Always `true` when `amount` is present — it is an estimate, never a settled figure. */
  amountIsEstimate: z37.boolean(),
  /**
   * Of `amount`, the part carried in from earlier skipped (under-minimum) rounds:
   * unpaid orders after the last paid window and before this coverage window.
   * `null` when nothing is carried / not computed.
   */
  carriedAmount: z37.number().nullable().default(null),
  /** Talabat's minimum payout applied (JOD); `null` when not applied. */
  minimumPayout: z37.number().nullable().default(null),
  /** `true` when `amount` is under `minimumPayout` — Talabat will skip this round. */
  belowMinimum: z37.boolean().default(false),
  /** When `belowMinimum`, the following scheduled payout the amount rolls into; else `null`. */
  rollsIntoPayoutDate: IsoTimestampSchema.nullable().default(null),
  /**
   * Account-level ads already deducted from `amount` (positive magnitude):
   * reported Talabat Ads spend + VAT, estimated CPC for unreported days, and
   * estimated sponsored-deal fees. `null` when none.
   */
  adsDeducted: z37.number().nullable().default(null),
  /** Where the CPC part came from: all days reported, some, or none. `null` when no ads. */
  adsSource: z37.enum(["reported", "mixed", "estimated"]).nullable().default(null)
});
var SavePayoutScheduleBodySchema = z37.object({
  platformKey: PlatformKeySchema,
  month: z37.string().regex(/^\d{4}-\d{2}$/, "month must be YYYY-MM"),
  rows: z37.array(PayoutScheduleRowSchema)
});
var SavePayoutScheduleResponseSchema = z37.object({
  schedule: PayoutScheduleSchema,
  warnings: z37.array(z37.string())
});
var GetPayoutScheduleQuerySchema = z37.object({
  platformKey: PlatformKeySchema,
  month: z37.string().regex(/^\d{4}-\d{2}$/, "month must be YYYY-MM")
});
var GetPayoutScheduleResponseSchema = PayoutScheduleSchema.nullable();
var ListPayoutSchedulesResponseSchema = z37.array(PayoutScheduleSchema);
var c7 = initContract7();
var payoutScheduleContract = c7.router(
  {
    listSchedules: {
      method: "GET",
      path: "/admin/payout-schedules",
      responses: {
        200: ListPayoutSchedulesResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: list every current payout schedule across platforms + months (admin only; Req 2.4)."
    },
    getSchedule: {
      method: "GET",
      path: "/admin/payout-schedule",
      query: GetPayoutScheduleQuerySchema,
      responses: {
        200: GetPayoutScheduleResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: read the current payout schedule for a platform + month (admin only; Req 2.1)."
    },
    saveSchedule: {
      method: "POST",
      path: "/admin/payout-schedule",
      body: SavePayoutScheduleBodySchema,
      responses: {
        200: SavePayoutScheduleResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: save a platform + month payout schedule, shared across the platform stores, effective-dated (admin only; Req 2.1, 2.2)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/onboarding.ts
var c8 = initContract8();
var OnboardingStep1BodySchema = z38.object({
  markup_pct: z38.number().min(0).max(100),
  default_food_cost_pct: z38.number().min(0).max(100),
  pay_fee_pct: z38.number().min(0).max(100).optional(),
  pay_fee_fixed: z38.number().min(0).optional(),
  pay_fee_tax_pct: z38.number().min(0).max(100).optional()
});
var CatalogReviewItemSchema = z38.object({
  item: CatalogItemResponseSchema,
  /** `basePrice × (1 + markup_pct/100)` — the marked-up platform price (Req 2.2). */
  platformPrice: z38.number(),
  /**
   * Server-derived "needs a price" flag (catalog-coverage Req 4.4, 6.2, 6.3) so
   * the web and server agree on ONE rule instead of each re-deriving it:
   * `needsPrice ⇔ item.status === 'archived' AND item.baseProvenance ∈
   * {'sibling-median', 'unset'}` — an auto-provisioned item whose base is an
   * estimate or unset. `item.status` alone distinguishes provisioned-from-orders
   * (`archived`) from confirmed menu items (`active`); `item.baseProvenance`
   * names where the base came from. Cleared once the owner prices/confirms the
   * item (the save/edit flow flips it `archived → active` and drops the
   * provenance). Defaults to `false` so a payload omitting it still parses.
   */
  needsPrice: z38.boolean().default(false)
});
var CatalogOrdersSourceSchema = z38.object({
  status: z38.enum(["ok", "failed", "skipped"]),
  provisionedCount: z38.number().int().min(0),
  error: z38.string().optional()
});
var CatalogOptionRowSchema = z38.object({
  optionKey: z38.string().min(1),
  name: z38.string().min(1),
  priceDelta: z38.number(),
  platformPriceDelta: z38.number(),
  rateState: z38.enum(["inherited", "override", "mixed"]),
  foodCostPct: FoodCostPctSchema.nullable(),
  needsPrice: z38.boolean(),
  needsPriceCount: z38.number().int().nonnegative(),
  status: CatalogItemStatusSchema,
  baseProvenance: CatalogBaseProvenanceSchema.optional(),
  linked: z38.array(
    z38.object({
      itemId: z38.string().uuid(),
      itemName: z38.string(),
      optionId: z38.string(),
      effectiveFoodCostPct: FoodCostPctSchema,
      status: CatalogItemStatusSchema
    })
  )
});
var CatalogOptionsSourceSchema = z38.object({
  status: z38.enum(["ok", "failed", "skipped"]),
  attachedCount: z38.number().int().min(0),
  failedGroups: z38.number().int().min(0).default(0),
  error: z38.string().optional()
});
var CatalogReviewSchema = z38.object({
  markup_pct: z38.number().min(0).max(100),
  items: z38.array(CatalogReviewItemSchema),
  /**
   * The live Talabat connection state at the time this review was built
   * (clarity-acquisition Req 5.1; onboarding Req 2.2, 7.2). When the catalog is
   * empty, this disambiguates WHY: `disconnected`/`expired` ⇒ the acquisition
   * session isn't usable so the pull couldn't run — the UI SHALL surface it and
   * offer Retry, and SHALL NOT let the user confirm an empty catalog (hard
   * block). `connected` with an empty list ⇒ a genuinely empty menu. Always
   * populated so the UI never has to guess.
   */
  connection: ConnectionStateSchema,
  /**
   * OPTIONAL — present only on `GET .../catalog` (catalog-coverage Req 6.1): the
   * outcome of the orders fetch that provisions ordered-but-unlisted items, so
   * the UI can show a non-blocking notice when it failed. Absent on save/edit.
   */
  ordersSource: CatalogOrdersSourceSchema.optional(),
  /**
   * The Options view (clarity-catalog-options Req 6.8): one row per Option_Key,
   * on every review payload. Defaults to `[]` so a payload omitting it parses.
   */
  options: z38.array(CatalogOptionRowSchema).default([]),
  /**
   * OPTIONAL — present only on `GET .../catalog?provision=run`: the outcome of
   * the menu option sync (clarity-catalog-options Req 1.8). Absent on save/edit.
   */
  optionsSource: CatalogOptionsSourceSchema.optional(),
  /**
   * OPTIONAL — a short human note explaining an EXPECTED empty catalog, set when
   * the platform has no fetchable menu source (`capabilities.supportsMenu` is
   * `false`, e.g. Careem) and the pull succeeded on a `connected` session. Lets
   * the UI distinguish "this platform doesn't expose a menu — fine, the catalog
   * fills from ordered items" from "the fetch failed". Absent for platforms that
   * DO have a menu (Talabat) and whenever items are present.
   */
  catalogNote: z38.string().optional()
});
var CatalogReviewQuerySchema = z38.object({
  provision: z38.enum(["run", "skip", "retry"]).optional()
});
var CatalogEditSchema = z38.object({
  itemId: z38.string().uuid(),
  foodCostPct: FoodCostPctSchema.optional(),
  basePrice: z38.number().optional(),
  /** The marked-up platform price; when supplied, the base is derived with the markup. */
  platformPrice: z38.number().optional(),
  /**
   * OPTIONAL explicit review confirmation: when `true` and the item is
   * `archived`, flip it to `active` even if its values are unchanged. Ignored
   * for an already-active item. Defaults to `false`.
   */
  confirm: z38.boolean().optional()
});
var CatalogOptionEditSchema = z38.object({
  optionKey: z38.string().min(1),
  foodCostPct: FoodCostPctSchema.nullable().optional(),
  priceDelta: z38.number().min(0).optional(),
  platformPriceDelta: z38.number().min(0).optional(),
  confirm: z38.boolean().optional()
});
var OnboardingStep2BodySchema = z38.object({
  edits: z38.array(CatalogEditSchema),
  /**
   * Options-row edits (clarity-catalog-options Req 7.1–7.5), persisted with the
   * item edits as one version set. Defaults to `[]` (backward compatible).
   */
  optionEdits: z38.array(CatalogOptionEditSchema).default([])
});
var CatalogPickItemSchema = z38.object({
  /** The platform's own per-item id as the order line items carry it. */
  externalItemId: z38.string(),
  /** The item's display name shown in the picker. */
  name: z38.string(),
  /** The pre-markup base price, for the picker row. */
  basePrice: z38.number()
});
var CampaignScopeCandidateSchema = z38.object({
  campaign: CampaignSchema,
  /** Why this campaign is a candidate — drives the "why" line in the step. */
  reason: z38.enum(["already-scoped", "name-signals-scope"]),
  /** The campaign's current scoped item ids (verbatim externalItemIds); [] when unset. */
  scopedProductIds: z38.array(z38.string()).default([])
});
var CampaignScopeCandidatesResponseSchema = z38.object({
  candidates: z38.array(CampaignScopeCandidateSchema).default([]),
  catalog: z38.array(CatalogPickItemSchema).default([])
});
var SetCampaignScopeBodySchema = z38.object({
  campaignId: z38.string().uuid(),
  scopedProductIds: z38.array(z38.string()).default([])
});
var SetCampaignScopeResponseSchema = z38.object({
  campaign: CampaignSchema
});
var CareemAdjustmentEntrySchema = z38.object({
  category: z38.string(),
  date: z38.string().nullable(),
  amount: z38.number(),
  description: z38.string().nullable()
});
var MoneyBreakdownSchema = z38.object({
  gross: z38.number(),
  commission: z38.number(),
  fees: z38.number(),
  marketing: z38.number(),
  /**
   * The SPONSORED/TARGETED-DEALS portion of `marketing`, as a read-only breakout
   * so the owner summary can split advertising into two lines — plain Advertising
   * (`marketing − marketingSponsoredDeals`) and Promotional / Sponsored Deals
   * (`marketingSponsoredDeals`). Sourced from the payout `marketingFees` lines
   * whose label is a sponsored/targeted-deal fee (not Search CPC/Keyword, not the
   * Pro-delivery loyalty charge). Already summed into `marketing`, so it never
   * changes `marketing` or `netToOwner`. OPTIONAL and ABSENT when the platform
   * doesn't report a deals split (Careem, and Talabat periods with none), so the
   * breakdown stays byte-identical and the summary keeps a single Advertising row.
   */
  marketingSponsoredDeals: z38.number().optional(),
  restaurantFundedDiscount: z38.number(),
  platformFundedDiscount: z38.number(),
  foodCost: z38.number(),
  /** Pro/Plus loyalty delivery fee for the period (named). */
  proDelivery: z38.number().default(0),
  /** Talabat Service Fee for the period — a named platform fee from the statement. */
  serviceFee: z38.number().default(0),
  /** Talabat Delivery Fee for the period — a named platform fee from the statement. */
  deliveryFee: z38.number().default(0),
  /** Payment Handling Charges for the period — a named platform fee from the statement. */
  paymentHandling: z38.number().default(0),
  /**
   * What Talabat charged for orders the RESTAURANT cancelled in the period
   * ("Amount owed back to Talabat" on cancelled orders), as a positive cost.
   * 0 when none, or when the orders predate capturing it.
   */
  cancellationCharges: z38.number().default(0),
  /**
   * Per-order operational and waiting-time charges Talabat deducts on delivered
   * orders (the export's "Operational charges" + "Wait time fee"). 0 when none,
   * or when the orders predate capturing them.
   */
  operationalCharges: z38.number().default(0),
  /**
   * A period-level **subscription-program** deduction (Careem Plus), surfaced as
   * its own named owner-summary line (clarity-careem-ingestion Req 6.2) rather
   * than folded into commission or fees. Sourced from the statement's labelled
   * "Subscription program (e.g. Careem Plus)" fee and/or the Plus-mix snapshot's
   * `periodPlusCost`. OPTIONAL and ABSENT for Talabat (a platform with no such
   * charge) — only present when the caller supplies `careemPlus`, so the Talabat
   * breakdown is byte-identical. A real `0` here is a genuine "no Plus charge
   * this period", distinct from the field being absent.
   */
  careemPlus: z38.number().optional(),
  /**
   * The breakdown fields a platform explicitly CANNOT report for this period,
   * distinct from a real `0` (clarity-careem-ingestion Req 6.3). A key here means
   * "the platform doesn't break this figure out" — the owner summary SHALL render
   * it as unavailable, never as `0`-as-fact (a `0` would read as "the platform
   * charged nothing", which is a different, false claim). For Careem this is
   * `deliveryFee`, per-order `marketing`, and the platform-funded discount split.
   * OPTIONAL and ABSENT for Talabat (which reports these), keeping the Talabat
   * breakdown byte-identical.
   */
  unavailable: z38.array(z38.enum(["deliveryFee", "marketing", "platformFundedDiscount"])).optional(),
  /**
   * True when `deliveryFee` was DERIVED from the gross/commission/fee/promo/net
   * residual rather than reported by the platform (clarity-careem-ingestion
   * Req 6.3, flagged). The owner summary labels such a figure as estimated/
   * derived, not reported. ABSENT when `deliveryFee` is a reported figure.
   */
  deliveryFeeDerived: z38.boolean().optional(),
  /**
   * Σ every Careem adjustment category that is neither `CPLUS_FEE` nor
   * `PROMO_SHARING`, using the with-tax magnitude (clarity-careem-adjustments
   * Req 3.3). The single lumped "other Careem adjustments" owner-summary
   * deduction. OPTIONAL and ABSENT for Talabat (a platform with no such
   * adjustments) — only present when the caller supplies it, so the Talabat
   * breakdown is byte-identical. A real `0` is a genuine "no other adjustments
   * this period", distinct from the field being absent.
   */
  otherCareemAdjustments: z38.number().optional(),
  /**
   * Each such category → its summed with-tax magnitude, so the owner summary can
   * itemize the lumped `otherCareemAdjustments` (clarity-careem-adjustments
   * Req 3.4). OPTIONAL and ABSENT for Talabat, keeping the Talabat breakdown
   * byte-identical.
   */
  otherCareemAdjustmentsByCategory: z38.record(z38.string(), z38.number()).optional(),
  netToOwner: z38.number(),
  /**
   * Careem only: the delivery fee CUSTOMERS paid that Careem's basket figure
   * included but Careem kept (never part of the owner's payout or cost). It is
   * removed from `gross` so the breakdown adds up, and surfaced here as a memo so
   * nothing is hidden. Absent for Talabat and for periods where the basket
   * excluded delivery.
   */
  customerPaidDelivery: z38.number().optional(),
  /**
   * MyThings only: Σ of each finalized non-cancelled order's `vendorDeliveryFee`
   * (`money.vendorDeliveryFee` ← `Shipping Discount By Vendor`) for the period —
   * the owner-summary line "Delivery fees (you paid)". An ITEMIZED COST in the
   * gross→net bridge (a reason net is below gross), NOT re-subtracted from
   * `netToOwner` (the per-order payout already nets it). Distinct from the
   * Talabat statement-sourced `deliveryFee`, and never represented as unavailable
   * or derived/estimated — it is a reported per-order figure.
   *
   * OPTIONAL and ABSENT for Talabat and Careem (and for a MyThings period whose
   * total is 0), so their breakdowns stay BYTE-IDENTICAL.
   */
  vendorDeliveryFee: z38.number().optional(),
  /**
   * MyThings only: the portion of `vendorDeliveryFee` attributed to the
   * Discounted_Delivery_Tier (~6 JOD program), labelled "Discounted delivery".
   * OPTIONAL and ABSENT whenever `vendorDeliveryFee` is absent.
   */
  vendorDeliveryFeeDiscounted: z38.number().optional(),
  /**
   * MyThings only: the portion of `vendorDeliveryFee` attributed to the
   * Free_Delivery_Tier (>8 JOD program), labelled "Free delivery".
   * OPTIONAL and ABSENT whenever `vendorDeliveryFee` is absent.
   *
   * INVARIANT: `vendorDeliveryFeeDiscounted + vendorDeliveryFeeFree ===
   * vendorDeliveryFee` (to 2 dp), regardless of how any single near-threshold
   * order is classified.
   */
  vendorDeliveryFeeFree: z38.number().optional(),
  /**
   * MyThings only, RESERVED: a charge on orders the restaurant DECLINED. Not
   * emitted today — MyThings' Balance Summary (the transferred amount) carries
   * no revenue and no charge for a declined order, even though its sheet row
   * shows a negative net. Kept optional so a future platform-reported charge
   * can be surfaced (the owner summary renders it when present).
   */
  declinedCharges: z38.number().optional(),
  /** MyThings only: how many orders were declined in the period (not delivered). */
  declinedCount: z38.number().int().optional(),
  /**
   * MyThings only: per-order deductions for missing/wrong items (Σ "Deduction
   * Order Amount"). Already netted out of the payout; itemized as customer
   * refunds & quality issues. ABSENT when there were none.
   */
  qualityDeductions: z38.number().optional(),
  /**
   * MyThings only: cash the restaurant collected DIRECTLY from customers (a
   * cash take-away MyThings never handled — its "Deposit To Vendor's Account"
   * is short of the order value). MyThings' net omits it, so it is ADDED to
   * `netToOwner`. ABSENT when there was none.
   */
  cashCollected: z38.number().optional(),
  /**
   * MyThings only: orders with Status "Cancelled" that MyThings still paid in
   * full. Counted as sales (the money is real) but not as "delivered".
   */
  cancelledPaidCount: z38.number().int().optional(),
  /**
   * MyThings only: the item SALE-PRICE markdown the restaurant funded (Σ "Sale
   * Price Value By subStore" — menu price minus the sale price customers paid).
   * The settled subtotal is already net of it, so it is ADDED back to `gross`
   * (gross at menu prices) and itemized as a discount to users. It never
   * changes `netToOwner`. ABSENT when the period had no sale prices.
   */
  saleDiscount: z38.number().optional()
});
var PayoutGapCauseSchema = z38.object({
  kind: z38.enum([
    "quiet_days",
    "history_starts_late",
    "history_ends_early",
    "other_payout",
    "cancelled"
  ]),
  count: z38.number().int().nonnegative().default(0),
  days: z38.array(z38.string()).default([])
});
var PayoutMatchRowSchema = z38.object({
  externalPayoutId: z38.string().min(1),
  /**
   * Careem only: Clarity's own figure for this weekly cycle against what Careem
   * reported, so the row can show a tick or the exact difference (the Careem
   * counterpart of Talabat's "Clarity vs Talabat" check). `clarityNet` is rebuilt
   * from the orders Clarity holds for the cycle's days (Σ order payout − Careem
   * Plus − bank transfer fee − ads); `reportedNet` is Careem's cycle balance.
   * `carriedFrom` names the earlier cycle whose unpaid balance Careem rolled into
   * this one (explains the difference exactly). Absent for Talabat.
   */
  reconciliation: z38.object({
    clarityNet: z38.number(),
    reportedNet: z38.number(),
    /** `reportedNet − clarityNet − carriedAmount`; ~0 when everything is explained. */
    difference: z38.number(),
    carriedFrom: z38.string().nullable(),
    carriedAmount: z38.number(),
    status: z38.enum(["ok", "carried", "differs"])
  }).nullable().optional(),
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  /** The day Talabat paid this payout (00:00 UTC); `null` when not known yet. */
  paidAt: IsoTimestampSchema.nullable().default(null),
  reportedNetPayout: z38.number(),
  reportedGrossSales: z38.number(),
  reportedCommissions: z38.number(),
  /**
   * Reported fees deducted on the settlement (payment + additional fees), and
   * the advertising portion of marketing (Search CPC/Premium/Targeted; the
   * per-order Pro-delivery is excluded). Both are reported figures from the
   * payout breakdown, surfaced in the per-payout expand.
   */
  reportedFees: z38.number(),
  reportedAds: z38.number(),
  /**
   * Careem-only itemized adjustments (clarity-careem-payout-status Req 2): the
   * weekly cycle's net is explained by gross basket − partner-funded promo
   * (⇒ order earnings) − commission − payment-gateway fee − Careem Plus − bank
   * transfer fee − ads. Talabat collapses its fees differently and leaves these
   * at their defaults, so a Talabat row renders byte-identically (the flat
   * Gross/Commission/Fees/Ads/Net expand). `reportedOrderEarnings` is `null`
   * when not a Careem cycle / not computable; the fee lines default to 0.
   */
  reportedOrderEarnings: z38.number().nullable().default(null),
  reportedPartnerFundedPromo: z38.number().default(0),
  reportedPaymentGatewayFee: z38.number().default(0),
  reportedCareemPlus: z38.number().default(0),
  reportedBankTransferFee: z38.number().default(0),
  /**
   * Σ clawback (+tax) Careem reclaimed this cycle (customer refunds / quality
   * issues), a positive magnitude. Part of the itemized ladder so the reported
   * net ties out. Defaults 0 (Talabat / cycles with no clawback).
   */
  reportedClawback: z38.number().default(0),
  /**
   * Σ promo-sharing (+tax) Careem booked at the cycle level — a discount it did
   * NOT deduct from the per-order earnings. Shown as its own ladder line so the
   * owner sees the deduction that Careem missed on the orders. Defaults 0.
   */
  reportedPromoSharing: z38.number().default(0),
  /**
   * SIGNED carry-over: the previous cycle's balance Careem rolled into this one
   * (a positive carry ADDS to this cycle's payout, a negative subtracts). Shown
   * as its own ladder line so the reported net ties and the owner sees it
   * explicitly rather than as an "unexplained" gap. Defaults 0.
   */
  reportedCarryOver: z38.number().default(0),
  /**
   * Talabat-only itemized settlement breakdown (the REAL reported groups from the
   * payout's `breakdown`), surfaced so the per-payout expand shows the same
   * line-by-line detail the Careem cycle ladder does — leveling the two panels up.
   * Every figure is Talabat's own reported total (never fabricated); deductions
   * are positive magnitudes here (the UI renders the sign). Absent for Careem and
   * for rows whose `breakdown` wasn't captured ⇒ the flat Gross/Commission/Fees/
   * Ads/Net expand still renders, so nothing regresses.
   */
  talabatBreakdown: z38.object({
    /** Commission total (deduction). */
    commission: z38.number(),
    /** Marketing + advertising total (deduction). */
    marketing: z38.number(),
    /** Payment + additional fees total (deduction), incl. the payment-fee line. */
    additionalFees: z38.number(),
    /** Additional SALES Talabat paid in (delivery/packaging/MOV/tips/food-cost reimbursement) — an ADDITION. */
    additionalSales: z38.number(),
    /** Customer-refund charges (deduction). */
    customerRefundCharges: z38.number(),
    /** Customer fees (deduction). */
    customerFees: z38.number(),
    /** Loans/recovery (deduction). */
    loans: z38.number(),
    /** Tax charges (deduction). */
    taxCharges: z38.number(),
    /** Per-order wait-time fee (deduction). */
    waitTimeFee: z38.number()
  }).optional(),
  /** Orders linked to this payout by window (clarity-reconciliation Req 5). */
  linkedOrderCount: z38.number().int().nonnegative(),
  /** Order count the payout itself reports; `null` when the payout didn't carry one. */
  expectedOrderCount: z38.number().int().nonnegative().nullable(),
  /**
   * Σ per-order earnings over the orders linked to THIS payout — the amount our
   * order data accounts for. Uses each order's signed "Estimated earnings" when
   * the export carried it (so what Talabat charges for a vendor-cancelled order
   * counts as a negative), else its "Payout amount". Compared against
   * `reportedNetPayout` in the expand so the user sees how much of the deposit
   * the orders explain (the gap is the account-level ads/loan/tax/balance
   * adjustments). `null` when no orders linked.
   */
  linkedOrdersPayout: z38.number().nullable(),
  /**
   * What Talabat charged on linked orders the restaurant cancelled
   * ("Amount owed back to Talabat"), as a positive amount, and how many orders
   * it came from. 0 / 0 when none, or when the orders predate capturing it.
   */
  cancellationCharges: z38.number().default(0),
  cancellationChargeOrders: z38.number().int().nonnegative().default(0),
  /**
   * Does the money add up? `true` when Σ linked-order earnings − the payout's ads
   * equals the reported net (within a few fils). `false` when it doesn't, `null`
   * when it can't be judged (no linked orders, or some orders predate capturing
   * earnings). A count gap with `moneyReconciles === true` is harmless: the
   * extra items Talabat counted carry no money.
   */
  moneyReconciles: z38.boolean().nullable().default(null),
  /** `true` ⇒ coverage complete (linked order count == expected). */
  matched: z38.boolean(),
  /**
   * Rough sales value of the orders Talabat paid for that Clarity doesn't have:
   * `(expected − linked) × average subtotal of the linked orders`. `null` unless
   * there is a shortfall AND at least one linked order to average over.
   */
  estimatedMissingSales: z38.number().nullable().default(null),
  /**
   * Facts found in the data that could explain a coverage gap (empty when the
   * payout matched, or when nothing in the data explains it). The web copy turns
   * each into a plain-language reason for the owner.
   */
  gapCauses: z38.array(PayoutGapCauseSchema).default([])
});
var StatementCheckSchema = z38.object({
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  /** What the month earned, net (the statement's "Month Net"). */
  earned: z38.number(),
  /**
   * The statement's cash-flow lines, `null` when this statement was stored without
   * them (re-derive to load them).
   */
  cashFlow: z38.object({
    /** The statement's opening balance (owed to the restaurant at the start of the month). */
    openingBalance: z38.number(),
    /** Paid to the restaurant during the month (payouts paid that month). */
    paid: z38.number(),
    /** The statement's closing ("final") balance. */
    owedAtEnd: z38.number(),
    /** `openingBalance + earned − paid` equals `owedAtEnd` (to the fil). */
    adds: z38.boolean()
  }).nullable(),
  /** The payouts Clarity holds that Talabat PAID in this month (by paid date). */
  payouts: z38.object({
    count: z38.number().int().nonnegative(),
    /** The `externalPayoutId`s counted (the payouts paid in this month). */
    payoutIds: z38.array(z38.string()).default([]),
    net: z38.number(),
    /** `false` when some payouts have no known paid date (re-derive to load it). */
    datesKnown: z38.boolean(),
    /** `net − cashFlow.paid`; `null` when it can't be judged. */
    difference: z38.number().nullable(),
    /** `|difference| / paid × 100`; `null` when it can't be judged. */
    differencePct: z38.number().nullable(),
    /** Within tolerance of what the statement says was paid; `null` = can't judge. */
    withinTolerance: z38.boolean().nullable(),
    /**
     * Payouts for THIS month's orders (their earning window starts in the month)
     * that Talabat paid only AFTER the month ended, so the statement counts them in
     * the next month's "paid". Shown so the month's own orders are fully accounted
     * for.
     */
    paidLater: z38.object({
      count: z38.number().int().nonnegative(),
      net: z38.number(),
      payoutIds: z38.array(z38.string()),
      /** First day (`YYYY-MM-DD`) any of them was paid; `null` when none. */
      paidOn: z38.string().nullable()
    }).default({ count: 0, net: 0, payoutIds: [], paidOn: null }),
    /**
     * For the FIRST month Clarity holds only: part of what was paid went to
     * payouts for the previous month's earnings, which fall before the period
     * Clarity pulled. This is that amount (the month's opening balance), counted
     * towards the match. 0 otherwise.
     */
    earlierPayouts: z38.number().default(0)
  })
});
var STATEMENT_TOLERANCE_PCT = 1;
var CareemCycleCheckSchema = z38.object({
  /** The month (00:00 UTC on its first day) these cycles were paid in. */
  periodStart: IsoTimestampSchema,
  /** The month end (exclusive upper bound). */
  periodEnd: IsoTimestampSchema,
  /** The weekly cycles paid in this month. */
  cycles: z38.object({
    count: z38.number().int().nonnegative(),
    /** The `externalPayoutId`s (cycle ids) paid in this month. */
    payoutIds: z38.array(z38.string()).default([]),
    /** Σ net payout across the month's cycles. */
    net: z38.number()
  }),
  /** Every cycle's money reconciles (Clarity's orders explain Careem's balance). */
  moneyReconciles: z38.boolean(),
  /** Every cycle's order coverage holds (linked count == reported count). */
  coverageHolds: z38.boolean(),
  /**
   * The month verdict: `ok` (money + coverage hold), `check_orders` (money holds
   * but at least one cycle's coverage doesn't — surfaced, not failed), `bad` (at
   * least one cycle's money doesn't reconcile).
   */
  state: z38.enum(["ok", "check_orders", "bad"])
});
var PayoutSummarySchema = z38.object({
  /** `true` IFF every payout row matched (coverage + net within tolerance). */
  allMatched: z38.boolean(),
  /** Σ reported net payout across the period's payouts. */
  totalReportedNetPayout: z38.number(),
  /** Per-payout detail. */
  rows: z38.array(PayoutMatchRowSchema),
  /** Payouts vs the monthly statement net, one entry per statement in the period (Talabat). */
  statementChecks: z38.array(StatementCheckSchema).default([]),
  /**
   * Careem-only (clarity-careem-payout-status Req 4): weekly cycles grouped by
   * the month they were paid, with a per-month reconciliation verdict. Empty for
   * Talabat (which uses `statementChecks`); the web panel branches on this being
   * non-empty.
   */
  cycleChecks: z38.array(CareemCycleCheckSchema).default([])
});
var EstimatedBalanceSchema = z38.object({
  /**
   * `available` — a closing balance could be read + reconciled (a zero here is a
   * genuine "nothing outstanding"); `unconfirmed` — it reconciled to ~0 but is
   * too stale to assert a zero (Req 4.2c); `unavailable` — could not be computed.
   */
  state: z38.enum(["available", "unconfirmed", "unavailable"]),
  /** The CONFIRMED, reconciled closing balance (no later payout accounts for). 0 when since-paid / unavailable. */
  confirmedOwed: z38.number(),
  /** The closing balance a later payout HAS accounted for (informational). 0 when outstanding / unavailable. */
  sincePaid: z38.number(),
  /**
   * Best-effort estimate of order earnings accrued SINCE the latest statement's
   * month end that no statement/payout accounts for yet (statements lag). 0 when
   * none / unavailable. Defaults to 0 so a payload from before this field still
   * parses.
   */
  unpaidOrdersEstimate: z38.number().default(0),
  /**
   * Account-level advertising spend over the same post-statement window,
   * subtracted from the headline (ads aren't in the per-order earnings). 0 when
   * none / unavailable. Defaults to 0 for backward-compat.
   */
  adsSinceStatement: z38.number().default(0),
  /**
   * The full "money still coming" headline: `confirmedOwed +
   * unpaidOrdersEstimate − adsSinceStatement`, floored at 0. Equals
   * `confirmedOwed` when the window adds nothing. Defaults to 0 for
   * backward-compat (an older payload reads its confirmed figure as the headline
   * via the consumer's fallback).
   */
  totalEstimatedOwed: z38.number().default(0),
  /** The latest statement period end used; `null` when unavailable. */
  asOf: IsoTimestampSchema.nullable(),
  /**
   * The post-statement window's upper bound (the `now` the "earned since" terms
   * were measured to). With `asOf` (the window's lower bound) this gives the
   * EXACT window dates the UI shows ("since 1 Oct → 17 Oct"), not just the month.
   * `null` when unavailable / no open window. Defaults to `null` (backward-compat).
   */
  windowEnd: IsoTimestampSchema.nullable().default(null),
  /**
   * The most recent payout Talabat actually PAID for this store — its `paidAt`
   * and net `amount` — so the owner sees when they were last paid and how much.
   * `null` when none has been paid / synced. Defaults to `null` (backward-compat).
   */
  lastPayment: z38.object({
    /** The day Talabat paid this settlement. */
    paidAt: IsoTimestampSchema,
    /** The net amount paid. */
    amount: z38.number(),
    /** The earning window this payment COVERED (paid days after `periodEnd`). */
    periodStart: IsoTimestampSchema,
    periodEnd: IsoTimestampSchema
  }).nullable().default(null),
  /**
   * The ITEMIZED post-statement window (clarity-daily-update Req 5.7) — the exact
   * orders and payout-ads the headline's `unpaidOrdersEstimate` /
   * `adsSinceStatement` are summed from, so the UI can show "how Talabat owes you
   * this" order-by-order. Computed by `core`'s `estimateBalanceItems` over the
   * SAME window + filter, so the itemized totals equal the headline by
   * construction. Defaults to an empty breakdown (backward-compat).
   */
  breakdown: z38.object({
    /** Finalized, not-yet-paid orders in the window (the earned-since constituents). */
    orders: z38.array(
      z38.object({
        id: IdSchema,
        externalOrderId: z38.string(),
        timestamp: IsoTimestampSchema,
        earnings: z38.number(),
        cancelled: z38.boolean()
      })
    ).default([]),
    /**
     * The window's account-level ads deductions (the ads-since constituents):
     * `reported` — Talabat Ads spend + VAT for the days no payout has settled;
     * `estimated_cpc` — CPC for days not reported yet (orders × rate); `deals` —
     * estimated sponsored-deal fees (orders × rate); `per_order` — marketing an
     * older export already folded into its earnings. `payout` is the legacy
     * per-payout item (older payloads).
     */
    ads: z38.array(
      z38.object({
        kind: z38.enum(["reported", "estimated_cpc", "deals", "per_order", "payout", "manual"]).default("payout"),
        externalPayoutId: z38.string().nullable().default(null),
        ads: z38.number(),
        periodStart: IsoTimestampSchema,
        periodEnd: IsoTimestampSchema,
        /** Orders the estimate multiplies (estimated_cpc / deals). */
        orders: z38.number().int().nonnegative().nullable().default(null),
        /** Per-order rate (estimated_cpc / deals). */
        ratePerOrder: z38.number().nullable().default(null)
      })
    ).default([])
  }).default({ orders: [], ads: [] }),
  /**
   * The next scheduled payout (clarity-payout-schedule Req 3.5), when the store's
   * platform has an admin-entered schedule: its date, coverage window, whether
   * it's due today, and a best-effort amount (a SLICE of what's owed, not extra
   * money; `null` for a monthly-closing row). `null` when no schedule is
   * configured. Defaults `null` (backward-compat).
   */
  upcomingPayout: UpcomingPayoutSchema.nullable().default(null),
  /** A short, non-secret reason — present only when `state === 'unavailable'`. */
  reason: z38.string().optional()
});
var EffectiveFoodCostSchema = z38.object({
  /** Σ per-order item-level food cost over the period (= `breakdown.foodCost`). */
  amount: z38.number(),
  /** `foodCost / gross × 100`, or `null` when there is no gross. */
  pct: z38.number().nullable(),
  /** Order lines that matched a catalog item (and so carry a real cost). */
  matchedLines: z38.number().int().nonnegative(),
  /** Total order lines seen over the period. */
  totalLines: z38.number().int().nonnegative(),
  /**
   * Basket option tokens matched to a priced (not needs-a-price) catalog option
   * over the same finalized non-cancelled set (clarity-catalog-options Req 8.1,
   * 8.2). Defaults to 0 when absent.
   */
  pricedOptions: z38.number().int().nonnegative().default(0),
  /** Total basket option tokens over the same set (Req 8.1, 8.2). Defaults to 0. */
  totalOptions: z38.number().int().nonnegative().default(0)
});
var FieldConfidenceSchema = z38.enum(["derived", "period", "blended", "defaulted"]);
var FieldConfidenceMapSchema = z38.object({
  comm_pct: FieldConfidenceSchema,
  pay_fee_pct: FieldConfidenceSchema,
  visa_pct: FieldConfidenceSchema,
  avg_order_plus: FieldConfidenceSchema,
  avg_order_non_plus: FieldConfidenceSchema,
  plus_pct: FieldConfidenceSchema,
  non_plus_pct: FieldConfidenceSchema,
  plus_fee: FieldConfidenceSchema,
  disc_del_pct: FieldConfidenceSchema,
  discount_platform_funded: FieldConfidenceSchema
});
var CareemDeriveExtraSchema = z38.object({
  plus_pct: z38.number().min(0).max(100),
  non_plus_pct: z38.number().min(0).max(100),
  pay_fee_pct: z38.number().nullable(),
  periodSignalsMissing: z38.boolean(),
  /**
   * Careem's REPORTED live balance (clarity-careem-payout-status Req 5), read
   * directly from `billingAccounts/earnings` — NOT a computed estimate. `total`
   * is the MERCHANT account's cashable balance, `underProcess` the amount Careem
   * is already paying out. The UI shows this as the headline ("reported by
   * Careem, not an estimate") instead of the Talabat statement-anchored estimate.
   * `null` when the balance read degraded (shown as unavailable, never a
   * fabricated zero). Defaults to `null` so older payloads parse.
   */
  liveBalance: z38.object({
    total: z38.number(),
    underProcess: z38.number(),
    currency: z38.string()
  }).nullable().default(null),
  /**
   * Best-effort "still owed" (clarity-careem-payout-status Req 6): orders Clarity
   * can see that the cycles have NOT paid for yet, valued from the order-vs-cycle
   * gap — Careem's answer to Talabat's estimated-balance-owed. Shown as a
   * secondary line beneath the live balance. `null` when it can't be judged (the
   * line is omitted). Defaults to `null`.
   */
  stillOwed: z38.number().nullable().default(null),
  /**
   * The raw period-level adjustment entries Careem charged OUTSIDE the weekly
   * cycles (e.g. CLAWBACK) — a flat, dated audit list (each row carries its own
   * `category`) so the owner can recheck a charge against the Careem portal.
   * These are the SAME figures the breakdown's `otherCareemAdjustments` line
   * sums. Defaults to `[]` (absent for Talabat and older payloads).
   */
  adjustmentEntries: z38.array(CareemAdjustmentEntrySchema).default([])
});
var DerivedProfileSummarySchema = z38.object({
  comm_pct: z38.number().min(0).max(100).nullable(),
  comm_tax_pct: z38.number().min(0).max(100).nullable(),
  visa_pct: z38.number().min(0).max(100),
  n_plus: z38.number().int().nonnegative(),
  n_non_plus: z38.number().int().nonnegative(),
  avg_order_plus: z38.number(),
  avg_order_non_plus: z38.number(),
  plus_fee: z38.number(),
  disc_del_pct: z38.number().min(0).max(100),
  // ── clarity-derived-summary additions (Req 1, 2, 3, 4) ──
  /**
   * Derivation period (Req 1.1) — the min/max finalized-order timestamp the
   * summary reflects. `null` when there is no finalized data (shown as
   * unavailable, never a fabricated range — Req 1.3).
   */
  periodStart: IsoTimestampSchema.nullable(),
  periodEnd: IsoTimestampSchema.nullable(),
  /**
   * Discount split by funder over the finalized non-cancelled orders (Req 3.1),
   * money in the store currency. `0` is a legitimate value (no discounts),
   * distinct from the nullable commission block.
   */
  discount_restaurant_funded: z38.number(),
  discount_platform_funded: z38.number(),
  discount_total: z38.number(),
  /**
   * Payment-gateway params passed through from `store.profile` (Req 2.1),
   * `null` when not configured (shown as "set in Step 1", no fabricated
   * per-order cost — Req 2.4). The UI computes the per-online-order cost from
   * these plus `avg_order_online` (Req 2.2).
   */
  pay_fee_fixed: z38.number().nullable(),
  pay_fee_pct: z38.number().nullable(),
  pay_fee_tax_pct: z38.number().nullable(),
  /** Derived card-bucket AOV — the base for the per-online-order cost (Req 2.2). */
  avg_order_online: z38.number(),
  /**
   * Food cost (Req 4): the store default food-cost % from `store.profile`
   * (`null` when unset — "set in Step 1", Req 4.3) and, where a per-item catalog
   * picture exists, the display-only catalog average (Req 4.2, `null` when no
   * catalog).
   */
  default_food_cost_pct: z38.number().nullable(),
  catalog_avg_food_cost_pct: z38.number().nullable(),
  /**
   * The ACTUAL effective food cost over the period (per-order item-level COGS),
   * with catalog-match coverage — the headline food-cost figure, distinct from
   * the Step-1 default rate (Req 4). Always present (zeros for a new store).
   */
  effectiveFoodCost: EffectiveFoodCostSchema,
  /** `true` ⇒ commission unavailable → completion blocked unless an admin proceeds (Req 5.4). */
  commissionsUnavailable: z38.boolean(),
  /** How many finalized orders fed the derivation (0 ⇒ nothing derivable). Includes cancelled. */
  finalizedCount: z38.number().int().nonnegative(),
  /**
   * How many of the finalized orders were CANCELLED — surfaced so the summary
   * shows the done-vs-cancelled split (`finalizedCount − cancelledCount` were
   * delivered/completed). Cancelled orders carry no real sale.
   */
  cancelledCount: z38.number().int().nonnegative(),
  /**
   * The money breakdown graphed at the top of the Step-3 summary
   * (`clarity-summary-breakdown` Req 2.1): gross decomposed into the owner's
   * take + the costs, plus the Talabat-contributed co-funding. Always present —
   * all zeros for a new store with no finalized orders (Req 2.3).
   */
  breakdown: MoneyBreakdownSchema,
  /**
   * The payouts for the completed period matched against the orders linked to
   * them (clarity-reconciliation). Always present — empty `rows` for a store
   * with no settled payouts yet.
   */
  payouts: PayoutSummarySchema,
  /**
   * The estimated balance the platform still owes the store (clarity-daily-update
   * Req 4.5), derived from the SAME statements/payouts as the per-month
   * `statementChecks` (so the headline estimate can't contradict a per-month
   * closing block). `state: 'unavailable'` with a reason when the data can't
   * support an estimate — never a fabricated number. Defaults to the unavailable
   * shape so a payload omitting it still parses (backward compatible).
   */
  estimatedBalance: EstimatedBalanceSchema.default({
    state: "unavailable",
    confirmedOwed: 0,
    sincePaid: 0,
    unpaidOrdersEstimate: 0,
    adsSinceStatement: 0,
    totalEstimatedOwed: 0,
    asOf: null,
    windowEnd: null,
    lastPayment: null,
    breakdown: { orders: [], ads: [] },
    upcomingPayout: null,
    reason: "no estimate computed"
  }),
  // ── clarity-careem-ingestion additions (Req 4.6, 6.4, 9.3) — OPTIONAL ──
  // Careem's derivation carries two things Talabat's does not: a per-field
  // provenance map (so the review UI can badge every field Derived / Period
  // graph / Blended / Defaulted) and the exact period-level Plus figures that
  // the integer `n_plus`/`n_non_plus` projection would otherwise round away.
  // Both are OPTIONAL and Careem-only: a Talabat summary omits them and renders
  // byte-identically to today (no badges, no blended explainer). The web surface
  // keys the Careem-specific layer on `confidence` being present.
  /**
   * Per-field confidence for the derived-profile review (Careem, Req 4.6): each
   * field's provenance — `derived` (from the store's own orders), `period` (from
   * the period-level analytics graph / cycle, not the orders), `blended` (the
   * no-per-order-Plus-flag blended-AOV approximation), or `defaulted` (a
   * documented default / unavailable gap). Absent for Talabat.
   */
  confidence: FieldConfidenceMapSchema.optional(),
  /**
   * Careem's period-level Plus figures carried verbatim (Req 4.3 / 6.4): the
   * exact `plus_pct` / `non_plus_pct` the integer `n_plus`/`n_non_plus` counts
   * are projected from, the gateway-fee `pay_fee_pct` (card fee over gross), and
   * whether the period signals were missing (⇒ the period fields are defaulted).
   * Absent for Talabat.
   */
  careem: CareemDeriveExtraSchema.optional()
});
var OnboardingStep3BodySchema = z38.object({
  proceedAnyway: z38.boolean().optional()
}).optional();
var OnboardingDeriveVerdictSchema = z38.enum([
  "completed",
  "completed_commission_unavailable",
  "blocked_failed_pull"
]);
var MonthSummarySchema = z38.object({
  /** Machine key, e.g. `"2026-09"`. */
  key: z38.string().min(1),
  /** Human label, e.g. `"September 2026"`. */
  label: z38.string().min(1),
  /** Summary scoped to this single month. */
  summary: DerivedProfileSummarySchema
});
var OnboardingDeriveResultSchema = z38.object({
  /** The OVERVIEW summary — all completed months combined (the current behavior). */
  derived: DerivedProfileSummarySchema,
  /**
   * Per-month summaries for the tab switcher (newest first). Each entry is the
   * full `DerivedProfileSummary` scoped to a single completed month. Empty when
   * only one month (or no data) — the overview alone suffices.
   */
  months: z38.array(MonthSummarySchema).default([]),
  store: StoreResponseSchema,
  /** The reference sources that failed to pull during the Step-3 reference pull (Req 7.2). */
  failedSources: z38.array(z38.string()),
  /**
   * The completion verdict (clarity-portal-pulls Req 6.1): `blocked_failed_pull`
   * (a required pull failed — completion blocked), `completed_commission_unavailable`
   * (new store, no finalized data yet — completes with commission unavailable), or
   * `completed` (derived + complete).
   */
  verdict: OnboardingDeriveVerdictSchema,
  /**
   * The outcome of the AUTOMATIC enable attempt that fires WHEN completing Step 3
   * sets `onboardingComplete` (onboarding-automation Req 3, option C — enable at
   * wizard completion). ABSENT when completion did not just flip (a re-derive of
   * an already-complete store) or when completion was blocked. When present:
   * - `enabled: true` — the store transitioned to `enabled` and daily ingestion
   *   is now on; `store` above reflects the enabled state.
   * - `enabled: false` — completion succeeded but the automatic enable was NOT
   *   applied; `reason` names why (e.g. `no_subscription`,
   *   `subscription_suspended`, `lifecycle_rejected`). The subscription guard
   *   still applies — a store with no / a suspended subscription stays
   *   complete-but-not-enabled until the admin enables it, exactly like the
   *   manual enable. The UI surfaces the reason.
   */
  autoEnable: z38.object({
    enabled: z38.boolean(),
    reason: z38.string().optional()
  }).optional()
});
var DerivedReadSchema = z38.object({
  derived: DerivedProfileSummarySchema,
  months: z38.array(MonthSummarySchema).default([])
});
var DeriveSourceStatusSchema = z38.object({
  source: z38.enum(["catalog", "statements", "detailed", "orders", "payouts"]),
  state: z38.enum(["pending", "running", "done", "failed", "skipped"]),
  /** Completed units for this source (e.g. months/statements done). */
  done: z38.number().int().nonnegative().default(0),
  /** Known total, or `null` when not yet known. */
  total: z38.number().int().nonnegative().nullable().default(null)
});
var DeriveProgressSchema = z38.object({
  running: z38.boolean(),
  startedAt: IsoTimestampSchema.nullable(),
  /** Heartbeat — the timestamp of the most recent progress event. */
  lastEventAt: IsoTimestampSchema.nullable(),
  sources: z38.array(DeriveSourceStatusSchema),
  /** Last human-readable event, or `null`. */
  note: z38.string().nullable().default(null)
});
var CatalogProvisionProgressSchema = z38.object({
  /** `true` from when the crawl starts until it returns (success or failure). */
  running: z38.boolean(),
  /** Order DETAILs processed so far. */
  processed: z38.number().int().min(0),
  /** Total order details to fetch over the window; `null` until the id list is known. */
  total: z38.number().int().min(0).nullable().default(null),
  /** Heartbeat — the most recent progress update, or `null`. */
  updatedAt: IsoTimestampSchema.nullable()
});
var CatalogFoodCostEditBodySchema = z38.object({
  /** The catalog item (contract id) whose food cost is being edited. */
  itemId: z38.string().uuid(),
  /** The new food-cost percentage `[0,100]`, effective from `effectiveFrom`. */
  foodCostPct: FoodCostPctSchema,
  /**
   * The instant the new rate takes effect (ISO-8601). May be in the past — the
   * version-split re-costs exactly the orders in `[effectiveFrom, …)` (Req 11.5).
   */
  effectiveFrom: IsoTimestampSchema,
  /**
   * When `true` and the item is `archived`, flip its `status` to `active` as part
   * of the new effective-dated version (Req 11.6). Ignored for an already-active
   * item. Defaults to `false`.
   */
  confirmArchived: z38.boolean().optional()
});
var CatalogFoodCostEditResultSchema = z38.object({
  /** The edited item's new current version (post-split, open-ended). */
  item: CatalogItemResponseSchema,
  /** The refreshed Step-2-style review payload (every current item + platform price). */
  review: CatalogReviewSchema
});
var MergeCatalogBodySchema = z38.object({
  /** The catalog item to absorb (the "source"). */
  sourceItemId: z38.string().uuid(),
  /** The catalog item to keep (the "target" / canonical). */
  targetItemId: z38.string().uuid()
});
var UnmergeCatalogBodySchema = z38.object({
  /** The canonical item carrying the alias. */
  itemId: z38.string().uuid(),
  /** The alias text to remove. */
  alias: z38.string().min(1)
});
var OnboardingGatherResultSchema = z38.object({
  /** `true` IFF every reference source pulled cleanly (no `failedSources`). */
  ok: z38.boolean(),
  /** The reference sources that failed to pull (session not connected, etc.). */
  failedSources: z38.array(z38.string()).default([]),
  /** Per-source counts of the records persisted by this gather. */
  persisted: z38.object({
    /** Catalog items persisted (0 when the persisted catalog was reused — the Skip_Path). */
    catalogItems: z38.number().int().nonnegative(),
    campaigns: z38.number().int().nonnegative(),
    statements: z38.number().int().nonnegative(),
    orders: z38.number().int().nonnegative(),
    payouts: z38.number().int().nonnegative()
  }),
  /**
   * `true` IFF the persisted catalog was REUSED rather than pulled (the
   * Skip_Path in `runReferencePull`), so the gather did not clobber a catalog a
   * later step edited.
   */
  catalogReused: z38.boolean().default(false),
  /** The completed-months window `[start, end)` this gather covered (Req 3.3). */
  period: z38.object({
    start: IsoTimestampSchema,
    end: IsoTimestampSchema
  }),
  /**
   * `true` when this request did NOT start a new gather because the store
   * ALREADY had a run in `running` status (reference / backfill / daily) — the
   * dedup path (unified-data-gathering Req 8.2). One gather per store: the
   * response ATTACHES to that existing running run (`runId` / `batchId` below)
   * instead of pulling again, so a reload / second tab / admin panel can never
   * spawn a duplicate concurrent run. The per-source `persisted` counts are all
   * zero while the run is in flight — the client resumes by polling the run
   * history (the same source the admin panel reads) until it finalizes (Req 8.3,
   * 8.4). Defaults `false` so a normal gather response is byte-compatible and
   * older clients ignore it.
   */
  alreadyRunning: z38.boolean().default(false),
  /**
   * The id of the Ingestion_Run this response attaches to — the NEW run minted
   * for this gather, or (on the `alreadyRunning` dedup path) the EXISTING running
   * run's id. Lets the client key its resume poll to the exact run. `null` when
   * no run row could be recorded (a history-write failure never fails the gather,
   * Req 7.1). Defaults `null` for back-compat.
   */
  runId: z38.string().nullable().default(null),
  /**
   * The `batchId` of the run this response attaches to (the new gather's batch,
   * or the existing running run's batch on the dedup path) — the key the run
   * history / admin panel groups by. `null` when no run row was recorded.
   * Defaults `null` for back-compat.
   */
  batchId: z38.string().nullable().default(null)
});
var onboardingContract = c8.router(
  {
    gather: {
      method: "POST",
      path: "/stores/:id/onboarding/gather",
      pathParams: z38.object({ id: z38.string().uuid() }),
      // No body — the window is server-resolved from REFERENCE_WINDOW_MONTHS.
      body: c8.type(),
      responses: {
        // Always 200 — a failed/incomplete gather is reported in the body
        // (`ok: false` + `failedSources`), never an HTTP error, so the UI can
        // offer Retry without an upload fallback (unified-data-gathering Req 3.5).
        200: OnboardingGatherResultSchema,
        ...commonErrorResponses
      },
      summary: "Data_Gathering_Step \u2014 the FIRST onboarding step: pull + persist the whole reference window up front via runReferencePull (unified-data-gathering Req 3.1\u20133.4)."
    },
    step1: {
      method: "PUT",
      path: "/stores/:id/onboarding/step1",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: OnboardingStep1BodySchema,
      responses: {
        200: StoreResponseSchema,
        ...commonErrorResponses
      },
      summary: "Step 1 \u2014 set markup + default food cost (+ confirm payment-gateway params; Req 2.1)."
    },
    getCatalog: {
      method: "GET",
      path: "/stores/:id/onboarding/catalog",
      pathParams: z38.object({ id: z38.string().uuid() }),
      query: CatalogReviewQuerySchema,
      responses: {
        200: CatalogReviewSchema,
        ...commonErrorResponses
      },
      summary: "Step 2 \u2014 fetch the resolved catalog for review (Req 2.2); `provision=skip` = menu only, `run` (default) also provisions ordered items (catalog-coverage Req 6.1); `retry` re-provisions over the current window, ignoring the run-once marker."
    },
    saveCatalog: {
      method: "PUT",
      path: "/stores/:id/onboarding/catalog",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: OnboardingStep2BodySchema,
      responses: {
        200: CatalogReviewSchema,
        ...commonErrorResponses
      },
      summary: "Step 2 \u2014 persist per-item food-cost / base / platform-price edits (Req 2.2)."
    },
    mergeCatalog: {
      method: "POST",
      path: "/stores/:id/onboarding/catalog/merge",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: MergeCatalogBodySchema,
      responses: {
        200: CatalogReviewSchema,
        // 409: the source's names/ids collide with another item's name-space
        // (alias conflict) — nothing persisted; the body names the clash
        // (catalog-item-aliases Req 2.7, 3.3).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Step 2 \u2014 merge a source catalog item into a canonical item as aliases; returns the refreshed review (catalog-item-aliases Req 2.1, 2.8)."
    },
    unmergeCatalog: {
      method: "POST",
      path: "/stores/:id/onboarding/catalog/unmerge",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: UnmergeCatalogBodySchema,
      responses: {
        200: CatalogReviewSchema,
        // 409: restoring the alias would collide with another item's name-space.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Step 2 \u2014 remove an alias from a canonical item, restoring it as a separate item; returns the refreshed review (catalog-item-aliases Req 5.1, 5.4)."
    },
    editCatalogItemFoodCost: {
      method: "POST",
      path: "/stores/:id/catalog/food-cost",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: CatalogFoodCostEditBodySchema,
      responses: {
        200: CatalogFoodCostEditResultSchema,
        // 409: the chosen effectiveFrom lands on an existing boundary / overlaps
        // / falls outside any version — the split is rejected, timeline untouched
        // (Req 11.4). The error body names the conflict.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Back-dated food-cost edit \u2014 split the covering version at effectiveFrom; may flip archived\u2192active (Req 11.4\u201311.6)."
    },
    getCampaignScope: {
      method: "GET",
      path: "/stores/:id/onboarding/campaign-scope",
      pathParams: z38.object({ id: z38.string().uuid() }),
      responses: {
        200: CampaignScopeCandidatesResponseSchema,
        // 409: onboarding not ready (consistent with the sibling steps).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Campaigns step \u2014 list the store scoped-candidate campaigns + the catalog pick-list for item scoping; reads persisted data, no pull (clarity-onboarding-campaign-scope Req 2.1, 2.3)."
    },
    setCampaignScope: {
      method: "PUT",
      path: "/stores/:id/onboarding/campaign-scope",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: SetCampaignScopeBodySchema,
      responses: {
        200: SetCampaignScopeResponseSchema,
        // 409: onboarding not ready (consistent with the sibling steps).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Campaigns step \u2014 set one campaign's item scope (itemScoped + scopedProductIds) as an append-only version; empty ids \u21D2 whole-order; no pull (clarity-onboarding-campaign-scope Req 1.1\u20131.4)."
    },
    derive: {
      method: "POST",
      path: "/stores/:id/onboarding/derive",
      pathParams: z38.object({ id: z38.string().uuid() }),
      body: OnboardingStep3BodySchema,
      responses: {
        200: OnboardingDeriveResultSchema,
        // 409: completion blocked because a REQUIRED pull FAILED (session
        // disconnected/expired/portal error) — verdict `blocked_failed_pull`
        // (clarity-portal-pulls Req 5.2). A connected new store with no finalized
        // data yet COMPLETES (200, `completed_commission_unavailable`) and is NOT
        // blocked.
        409: OnboardingDeriveResultSchema,
        ...commonErrorResponses
      },
      summary: "Step 3 \u2014 reference pull + derive the read-only profile; completes onboarding (Req 2.3, 6.1)."
    },
    getDerived: {
      method: "GET",
      path: "/stores/:id/onboarding/derived",
      pathParams: z38.object({ id: z38.string().uuid() }),
      responses: {
        // The derived summary RECOMPUTED from the store's PERSISTED raw records
        // (orders/statements/payouts/catalog) — no portal re-pull. `null` when
        // no reference data has been ingested yet (the UI shows "Run derivation").
        200: DerivedReadSchema.nullable(),
        ...commonErrorResponses
      },
      summary: "Step 3 \u2014 read the derived summary recomputed from persisted raw data (no re-pull)."
    },
    recalculate: {
      method: "POST",
      path: "/stores/:id/onboarding/recalculate",
      pathParams: z38.object({ id: z38.string().uuid() }),
      // No body — recalculation is parameter-free: it re-derives from whatever is
      // currently persisted.
      body: c8.type(),
      responses: {
        // Recalculate the food-cost / economic derivation after the catalog was
        // confirmed or edited (onboarding-automation Req 2): re-derives the
        // economic profile from the PERSISTED orders, re-persists the profile
        // fields onto the store, and returns the freshly recomputed summary over
        // the current catalog. A PURE READ of persisted data — it never re-pulls
        // the platform (ingestion-single-source). `null` when no reference data
        // has been ingested yet (nothing to recalculate against).
        200: DerivedReadSchema.nullable(),
        ...commonErrorResponses
      },
      summary: "Recalculate the derived food-cost / economic summary from persisted data after a catalog confirm/edit; re-persists profile fields, no re-pull (onboarding-automation Req 2)."
    },
    getDeriveProgress: {
      method: "GET",
      path: "/stores/:id/onboarding/derive/progress",
      pathParams: z38.object({ id: z38.string().uuid() }),
      responses: {
        200: DeriveProgressSchema,
        ...commonErrorResponses
      },
      summary: "Step 3 \u2014 poll live reference-pull progress (clarity-derive-progress Req 3.1)."
    },
    getCatalogProgress: {
      method: "GET",
      path: "/stores/:id/onboarding/catalog/progress",
      pathParams: z38.object({ id: z38.string().uuid() }),
      responses: {
        200: CatalogProvisionProgressSchema,
        ...commonErrorResponses
      },
      summary: "Step 2 \u2014 poll live orders-provisioning progress (N / M orders processed)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/orders.ts
import { initContract as initContract9 } from "@ts-rest/core";
import { z as z39 } from "zod";
var c9 = initContract9();
var ordersContract = c9.router(
  {
    list: {
      method: "GET",
      path: "/stores/:id/orders",
      pathParams: z39.object({ id: z39.string().uuid() }),
      query: z39.object({
        /** Cap the number of orders returned (simple paging; default server-side). */
        limit: z39.coerce.number().int().positive().max(1e3).optional()
      }),
      responses: {
        200: z39.array(OrderListItemResponseSchema),
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "List a store's normalized orders, each as { order, economics } \u2014 order + projected OrderEconomics per row (tenant-scoped; Req 14.1, clarity-orders-detail Req 4.1)."
    },
    getById: {
      method: "GET",
      path: "/stores/:id/orders/:orderId",
      pathParams: z39.object({ id: z39.string().uuid(), orderId: z39.string().uuid() }),
      responses: {
        200: OrderDetailResponseSchema,
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Order detail: { order, economics } \u2014 order (payoutId, line items, fees, provenance) + projected OrderEconomics (Req 10.2, 8.4)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/statements.ts
import { initContract as initContract10 } from "@ts-rest/core";
import { z as z40 } from "zod";
var c10 = initContract10();
var statementsContract = c10.router(
  {
    list: {
      method: "GET",
      path: "/stores/:id/statements",
      pathParams: z40.object({ id: z40.string().uuid() }),
      responses: {
        200: z40.array(StatementResponseSchema),
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "List a store's merged per-period statements (tenant-scoped; Req 14.1)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/payouts.ts
import { initContract as initContract11 } from "@ts-rest/core";
import { z as z41 } from "zod";
var c11 = initContract11();
var payoutsContract = c11.router(
  {
    list: {
      method: "GET",
      path: "/stores/:id/payouts",
      pathParams: z41.object({ id: z41.string().uuid() }),
      responses: {
        200: z41.array(PayoutResponseSchema),
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "List a store's normalized payouts (tenant-scoped; Req 14.1)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/reconciliation.ts
import { initContract as initContract12 } from "@ts-rest/core";
import { z as z42 } from "zod";
var c12 = initContract12();
var reconciliationContract = c12.router(
  {
    byPayout: {
      method: "GET",
      path: "/stores/:id/payouts/:payoutId/reconciliation",
      pathParams: z42.object({ id: z42.string().uuid(), payoutId: z42.string().uuid() }),
      responses: {
        200: ReconciliationReportSchema,
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Latest reconciliation report for a payout (tenant-scoped; Req 8.4); 404 when not yet reconciled."
    },
    byPeriod: {
      method: "GET",
      path: "/stores/:id/reconciliation/period",
      pathParams: z42.object({ id: z42.string().uuid() }),
      query: z42.object({
        /** Period window start (ISO-8601), inclusive. */
        periodStart: z42.string().datetime(),
        /** Period window end (ISO-8601), exclusive (half-open `[start, end)`). */
        periodEnd: z42.string().datetime()
      }),
      responses: {
        200: ReconciliationReportSchema,
        // 409: store is `assigned` and not onboarded — read blocked (Req 1.2).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Latest reconciliation report for a period window (tenant-scoped; Req 8.4); 404 when none."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/settings.ts
import { initContract as initContract13 } from "@ts-rest/core";
import { z as z43 } from "zod";
var c13 = initContract13();
var settingsContract = c13.router(
  {
    listFeeDefaults: {
      method: "GET",
      path: "/settings/platform-fee-defaults",
      responses: {
        200: z43.array(PlatformFeeDefaultsSchema),
        ...commonErrorResponses
      },
      summary: "List per-platform payment-gateway fee defaults (admin only; Req 4.1)."
    },
    updateFeeDefaults: {
      method: "PUT",
      path: "/settings/platform-fee-defaults/:platformKey",
      pathParams: z43.object({ platformKey: PlatformKeySchema }),
      body: PlatformFeeDefaultsUpdateSchema,
      responses: {
        200: PlatformFeeDefaultsSchema,
        ...commonErrorResponses
      },
      summary: "Set a platform's payment-gateway fee defaults (admin only; Req 4.1)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/admin-ingestion.ts
import { initContract as initContract14 } from "@ts-rest/core";
import { z as z44 } from "zod";
var IngestionTriggerSchema = z44.enum(["scheduled", "manual", "reference"]);
var IngestionModeSchema = z44.enum(["daily", "backfill", "reference"]);
var IngestionRunStatusSchema = z44.enum(["running", "succeeded", "failed", "skipped"]);
var IngestionPersistedSchema = z44.object({
  orders: z44.number().int().nonnegative(),
  statements: z44.number().int().nonnegative(),
  payouts: z44.number().int().nonnegative(),
  provisionedItems: z44.number().int().nonnegative(),
  /**
   * Campaigns pulled + written this run (clarity-campaign-matching) — the ads +
   * platform-deal campaigns the connectors fetched (monthly offers are admin-
   * entered, not pulled, so they are not counted here). Defaults 0 so older run
   * rows (written before campaign ingestion) still parse.
   */
  campaigns: z44.number().int().nonnegative().default(0),
  /**
   * Orders whose `campaignMatches` set CHANGED this run — the matcher's delta
   * (the orders the run actually (re)attributed to a campaign). Defaults 0 so
   * older run rows still parse.
   */
  ordersMatched: z44.number().int().nonnegative().default(0),
  /**
   * Records persisted WITH their verbatim `raw_json` captured this run — the
   * count the IngestionRuns panel surfaces so the log attests the verbatim
   * capture (Req 1) is happening (Req 7.4). Defaults 0 so older run rows
   * (written before raw_json capture) still parse.
   */
  rawJsonCaptured: z44.number().int().nonnegative().default(0)
});
var IngestionRunSchema = z44.object({
  /** The run's own id (one row per store per attempt). */
  id: IdSchema,
  /** The branch this attempt pulled. */
  storeId: IdSchema,
  /** Resolved store label (external store id / name) for the panel (Req 3.3). */
  storeLabel: z44.string(),
  /**
   * The platform the store pulls from (`talabat` / `careem`), resolved from the
   * store alongside `storeLabel` so the panel can badge each run with its
   * platform without a second lookup. Defaults to `'talabat'` so run rows
   * written before this field (or for a since-removed store) still parse.
   */
  platformKey: z44.string().default("talabat"),
  /** Groups one triggered/scheduled pass's per-store rows. */
  batchId: IdSchema,
  /** What kicked this attempt off. */
  trigger: IngestionTriggerSchema,
  /** The pull mode this attempt ran. */
  mode: IngestionModeSchema,
  /** Lifecycle status (running until finalized). */
  status: IngestionRunStatusSchema,
  /** Whether the store's ingestion watermark advanced. */
  advanced: z44.boolean(),
  /** The per-grain counts persisted by this attempt. */
  persisted: IngestionPersistedSchema,
  /** The source names that failed within this attempt (empty on full success). */
  failedSources: z44.array(z44.string()),
  /** A short, non-secret failure/skip reason; `null` on success. */
  error: z44.string().nullable(),
  /** When the per-store work started, ISO-8601. */
  startedAt: IsoTimestampSchema,
  /** When it finished, ISO-8601; `null` while still running. */
  finishedAt: IsoTimestampSchema.nullable(),
  /**
   * The start of the DATA window this attempt actually covered, ISO-8601 — the
   * ingested period's lower bound (daily: the horizon-clamped since-last-ingest
   * start; backfill: the clamped 6-month horizon start). Distinct from
   * `startedAt` (the wall-clock run time). `null` while `running`, and for a
   * `skipped` row where no pull was issued.
   */
  coveredFrom: IsoTimestampSchema.nullable(),
  /**
   * The end of the DATA window this attempt covered, ISO-8601 — the ingested
   * period's upper bound (the poll instant `now`). `null` while `running`, and
   * for a `skipped` row.
   */
  coveredTo: IsoTimestampSchema.nullable()
});
var TriggerAcceptedSchema = z44.object({
  /** The minted batch id; the run-history read is keyed by it (Req 4.6). */
  batchId: IdSchema,
  /** How many stores were enqueued for this batch. */
  storeCount: z44.number().int().nonnegative()
});
var ListRunsQuerySchema = z44.object({
  storeId: IdSchema.optional(),
  batchId: IdSchema.optional(),
  status: IngestionRunStatusSchema.optional(),
  limit: z44.coerce.number().int().positive().optional()
});
var TriggerRunBodySchema = z44.object({
  storeIds: z44.array(IdSchema).optional(),
  mode: IngestionModeSchema.optional()
});
var c14 = initContract14();
var adminIngestionContract = c14.router(
  {
    listRuns: {
      method: "GET",
      path: "/admin/ingestion/runs",
      query: ListRunsQuerySchema,
      responses: {
        200: z44.array(IngestionRunSchema),
        ...commonErrorResponses
      },
      summary: "List recent ingestion runs, newest first (admin only; Req 3.1)."
    },
    triggerRun: {
      method: "POST",
      path: "/admin/ingestion/runs",
      body: TriggerRunBodySchema,
      responses: {
        202: TriggerAcceptedSchema,
        ...commonErrorResponses
      },
      summary: "Trigger an asynchronous ingestion batch (admin only; Req 4.1, 4.2)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/subscriptions.ts
import { initContract as initContract15 } from "@ts-rest/core";
import { z as z45 } from "zod";
var CreateSubscriptionBodySchema = z45.object({
  /** The anniversary anchor for the billing cycle (Req 1.3, 5). */
  activationDate: IsoTimestampSchema,
  /** Optional flat price that wins over the ladder when present (Req 3.2). */
  priceOverride: JodAmountSchema.optional()
});
var SetOverrideBodySchema = z45.object({
  priceOverride: JodAmountSchema
});
var ToggleAddonBodySchema = z45.object({
  enabled: z45.boolean()
});
var SetAddonDiscountBodySchema = z45.object({
  discount: z45.number().finite().min(0).max(100)
});
var MarkPaidBodySchema = z45.object({
  /** Must equal the invoice total due and be > 0 (validated server-side, Req 9.4). */
  amount: JodInvoiceAmountSchema
});
var GenerateInvoiceBodySchema = z45.object({}).strict();
var c15 = initContract15();
var subscriptionsContract = c15.router(
  {
    create: {
      method: "POST",
      path: "/admin/clients/:clientId/subscription",
      pathParams: z45.object({ clientId: IdSchema }),
      body: CreateSubscriptionBodySchema,
      responses: {
        201: SubscriptionReadSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Create the client subscription (admin only; a second create \u2192 409, Req 1.4)."
    },
    read: {
      method: "GET",
      path: "/admin/clients/:clientId/subscription",
      pathParams: z45.object({ clientId: IdSchema }),
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Read the client subscription + computed pricing (admin only; Req 14.1)."
    },
    setOverride: {
      method: "PUT",
      path: "/admin/clients/:clientId/subscription/override",
      pathParams: z45.object({ clientId: IdSchema }),
      body: SetOverrideBodySchema,
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Set the flat price override (admin only; Req 3.2)."
    },
    clearOverride: {
      method: "DELETE",
      path: "/admin/clients/:clientId/subscription/override",
      pathParams: z45.object({ clientId: IdSchema }),
      body: c15.noBody(),
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Clear the override, reverting to the ladder (admin only; Req 3.2)."
    },
    toggleAddon: {
      method: "PUT",
      path: "/admin/clients/:clientId/subscription/addon",
      pathParams: z45.object({ clientId: IdSchema }),
      body: ToggleAddonBodySchema,
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Toggle the account-manager add-on (admin only; Req 3.5)."
    },
    setAddonDiscount: {
      method: "PUT",
      path: "/admin/clients/:clientId/subscription/addon/discount",
      pathParams: z45.object({ clientId: IdSchema }),
      body: SetAddonDiscountBodySchema,
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Set the add-on discount, 0\u2013100 (admin only; Req 3.6)."
    },
    setBillingContact: {
      method: "PUT",
      path: "/admin/clients/:clientId/subscription/billing-contact",
      pathParams: z45.object({ clientId: IdSchema }),
      body: BillingContactSchema,
      responses: {
        200: SubscriptionReadSchema,
        ...commonErrorResponses
      },
      summary: "Set the billing contact (email / WhatsApp) (admin only; Req 13.1)."
    },
    generateInvoice: {
      method: "POST",
      path: "/admin/clients/:clientId/invoices",
      pathParams: z45.object({ clientId: IdSchema }),
      body: GenerateInvoiceBodySchema,
      responses: {
        // The generated (or idempotently re-read) invoice for the completed cycle.
        200: InvoiceSchema,
        // `409` when no billing cycle has completed yet (nothing to bill — Req 8).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Generate the invoice for the client's latest completed cycle (admin only; idempotent per cycle, 409 when no cycle has completed)."
    },
    listInvoices: {
      method: "GET",
      path: "/admin/clients/:clientId/invoices",
      pathParams: z45.object({ clientId: IdSchema }),
      responses: {
        200: z45.array(InvoiceSchema),
        ...commonErrorResponses
      },
      summary: "List the client's invoices (admin only; raw PDF key omitted \u2014 Req 14.6)."
    },
    markPaid: {
      method: "POST",
      path: "/admin/invoices/:invoiceId/payments",
      pathParams: z45.object({ invoiceId: IdSchema }),
      body: MarkPaidBodySchema,
      responses: {
        200: InvoiceSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Record a manual payment and mark the invoice paid (admin only; non-payable \u2192 409, Req 9.4/9.6)."
    },
    regeneratePdf: {
      method: "POST",
      path: "/admin/invoices/:invoiceId/pdf/regenerate",
      pathParams: z45.object({ invoiceId: IdSchema }),
      body: z45.object({}).strict(),
      responses: {
        // The invoice with its PDF freshly re-rendered + re-stored (pdfState 'stored').
        200: InvoiceSchema,
        // `409` when the invoice (or its owning client) can't be found.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Re-render + overwrite an existing invoice's PDF through the current template (admin only)."
    },
    downloadPdf: {
      method: "GET",
      path: "/admin/invoices/:invoiceId/pdf",
      pathParams: z45.object({ invoiceId: IdSchema }),
      responses: {
        200: c15.otherResponse({
          contentType: "application/pdf",
          // Scoped, admin-only raw PDF bytes — never carries the server-only
          // storage key; availability is conveyed by `Invoice.pdfState` (Req 14.6).
          // Typed as `Uint8Array` (not `Buffer`) so the contract stays a
          // dependency-free leaf with no `@types/node` reliance.
          body: c15.type()
        }),
        // `409` when the invoice's `pdfState` is `not_stored` (Req 14.6).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Download the scoped invoice PDF bytes (admin only; 409 when not_stored, Req 14.6)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/campaigns.ts
import { initContract as initContract16 } from "@ts-rest/core";
import { z as z46 } from "zod";
var MonthlyOfferOccurrenceDraftSchema = z46.object({
  start_date: z46.string().nullable().optional(),
  end_date: z46.string().nullable().optional(),
  discount_pct: z46.number().nullable().optional(),
  /** Declared range lower/upper bound ("from X% to Y%"); null/absent ⇒ point. */
  discount_min: z46.number().nullable().optional(),
  discount_max: z46.number().nullable().optional(),
  max_discount_cap: z46.number().nullable().optional(),
  co_fund_pct: z46.number().nullable().optional(),
  min_order: z46.number().nullable().optional(),
  audience: z46.string().nullable().optional()
});
var MonthlyOfferItemDraftSchema = z46.object({
  name: z46.string().nullable().optional(),
  arabic_name: z46.string().nullable().optional(),
  audience: z46.string().nullable().optional(),
  discount_pct: z46.number().nullable().optional(),
  /** Declared range lower/upper bound ("from X% to Y%"); null/absent ⇒ point. */
  discount_min: z46.number().nullable().optional(),
  discount_max: z46.number().nullable().optional(),
  max_discount_cap: z46.number().nullable().optional(),
  co_fund_pct: z46.number().nullable().optional(),
  min_order: z46.number().nullable().optional(),
  /** The admin-set dedupe link key (Req 5.2); tagged during review, null ⇒ none. */
  platform_deal_key: z46.string().nullable().optional(),
  occurrences: z46.array(MonthlyOfferOccurrenceDraftSchema).optional()
});
var MonthlyOfferDraftSchema = z46.object({
  offers: z46.array(MonthlyOfferItemDraftSchema)
});
var UploadMonthlyOfferBodySchema = z46.object({
  platformKey: PlatformKeySchema,
  /** Base64-encoded offer-sheet image bytes. */
  imageBase64: z46.string().min(1),
  /** The image mime-type (e.g. `image/jpeg`, `image/png`). */
  mimeType: z46.string().min(1)
});
var UploadMonthlyOfferResponseSchema = z46.object({
  /** `'gemini'` when the OCR key is configured, `'noop'` when absent. */
  provider: z46.string(),
  draft: MonthlyOfferDraftSchema
});
var SaveMonthlyOfferBodySchema = z46.object({
  platformKey: PlatformKeySchema,
  draft: MonthlyOfferDraftSchema
});
var SaveMonthlyOfferResponseSchema = z46.object({
  campaigns: z46.array(CampaignSchema),
  occurrences: z46.array(CampaignOccurrenceSchema),
  warnings: z46.array(z46.string())
});
var ListMonthlyOffersResponseSchema = z46.object({
  campaigns: z46.array(CampaignSchema),
  occurrences: z46.array(CampaignOccurrenceSchema)
});
var AdsSpendTieSchema = z46.object({
  /** Σ ads-campaign spend attributed (= `PeriodRollup.adsSpend`). */
  adsCampaignSpend: z46.number(),
  /** Σ payout `marketingAds` — the ads ground-truth total. */
  payoutMarketingAds: z46.number(),
  /** `adsCampaignSpend − payoutMarketingAds` — the signed drift (the "Δ"). */
  delta: z46.number(),
  /** Whether `|delta| ≤ tolerance` — the ads spend ties to the payout. */
  withinTolerance: z46.boolean()
});
var PeriodRollupSchema = z46.object({
  /** Distinct orders with ≥1 campaign match (a multi-match order counts once). */
  ordersMatched: z46.number(),
  /** Total orders in the period (the denominator). */
  ordersTotal: z46.number(),
  /** Campaign-driven sales — Σ subtotal over the DISTINCT matched orders. */
  campaignDrivenSales: z46.number(),
  /** Total ad+promo cost = Σ ads spend + Σ restaurant-funded discount. */
  totalAdPromoCost: z46.number(),
  /** The ads-spend portion of {@link totalAdPromoCost} alone. */
  adsSpend: z46.number(),
  /** The restaurant-funded discount portion of {@link totalAdPromoCost} alone. */
  promoCost: z46.number(),
  /** Blended ROAS = campaignDrivenSales / totalAdPromoCost; null when cost is 0. */
  blendedRoas: z46.number().nullable(),
  /** The payout-ads tie; null when no payout `marketingAds` total was supplied. */
  adsSpendTie: AdsSpendTieSchema.nullable()
});
var CampaignVarianceSchema = z46.object({
  projectedProfitPerOrder: z46.number(),
  projectedAov: z46.number(),
  projectedSales: z46.number(),
  aovDelta: z46.number().nullable(),
  salesDelta: z46.number()
});
var CampaignActualsSchema = z46.object({
  campaignId: IdSchema,
  /** The occurrence these actuals are scoped to; null for the whole-campaign row. */
  occurrenceId: IdSchema.nullable(),
  matchedOrders: z46.number(),
  sales: z46.number(),
  actualAov: z46.number().nullable(),
  discountCost: z46.number(),
  coFundedAmount: z46.number(),
  netMarginImpact: z46.number(),
  adSpend: z46.number().nullable(),
  actualCr: z46.number().nullable(),
  cost: z46.number(),
  roas: z46.number().nullable(),
  cpa: z46.number().nullable(),
  /** ROAS/CPA are meaningful for ads; discount campaigns lead with net impact. */
  roasMeaningful: z46.boolean(),
  variance: CampaignVarianceSchema.nullable()
});
var CampaignListRowSchema = z46.object({
  campaign: CampaignSchema,
  actuals: CampaignActualsSchema,
  /** The forward projection for the actual-vs-projected bar (null if not modeled). */
  projection: CampaignProjectionSchema.nullable()
});
var CampaignListResponseSchema = z46.object({
  rollup: PeriodRollupSchema,
  campaigns: z46.array(CampaignListRowSchema)
});
var ParticipationStatusSchema = z46.enum([
  "participated",
  "not_enrolled",
  "upcoming"
]);
var CampaignOccurrenceViewSchema = z46.object({
  occurrence: CampaignOccurrenceSchema,
  status: ParticipationStatusSchema,
  matchedCount: z46.number(),
  /** The aggregate match confidence for `participated`; null otherwise. */
  confidence: z46.enum(["high", "medium", "low"]).nullable()
});
var CampaignDetailResponseSchema = z46.object({
  campaign: CampaignSchema,
  projection: CampaignProjectionSchema,
  occurrences: z46.array(CampaignOccurrenceViewSchema),
  actuals: CampaignActualsSchema
});
var MatchedOrderEvidenceSchema = z46.object({
  /** The order's contract id. */
  orderId: IdSchema,
  /** The platform's own order identifier (the "Order" column). */
  externalOrderId: z46.string(),
  /** When the order was placed (ISO-8601). */
  placedAt: z46.string(),
  /** Order subtotal (goods value). */
  subtotal: z46.number(),
  /** Restaurant-funded discount on this order (0 when the money block is pending). */
  discount: z46.number(),
  /** The match reason naming the signal (e.g. "discount 50% @ cap 3.00"). */
  reason: z46.string(),
  /** The match confidence for this occurrence. */
  confidence: z46.enum(["high", "medium", "low"])
});
var OccurrenceDetailResponseSchema = z46.object({
  campaign: CampaignSchema,
  occurrence: CampaignOccurrenceSchema,
  actuals: CampaignActualsSchema,
  evidence: z46.array(MatchedOrderEvidenceSchema)
});
var CampaignWhatIfQuerySchema = z46.object({
  /** What-if headline discount as a fraction 0..1 (overrides `campaign.discountPct`). */
  discountPct: z46.coerce.number().min(0).max(1).optional(),
  /** What-if per-order discount cap (overrides `campaign.maxDiscountCap`); nonnegative. */
  cap: z46.coerce.number().nonnegative().optional(),
  /** What-if CPC bid for an ads campaign (overrides `campaign.cpc`); nonnegative. */
  cpc: z46.coerce.number().nonnegative().optional()
});
var c16 = initContract16();
var CampaignReconciliationResponseSchema = z46.object({
  rows: z46.array(CampaignReconciliationSchema),
  total: CampaignReconciliationSchema,
  from: z46.string(),
  to: z46.string()
});
var CampaignReconciliationQuerySchema = z46.object({
  from: z46.string().optional(),
  to: z46.string().optional()
});
var CampaignTermsQuerySchema = z46.object({
  from: z46.string().optional(),
  to: z46.string().optional()
});
var TermBucketDetailQuerySchema = z46.object({
  from: z46.string().optional(),
  to: z46.string().optional(),
  /** 1-based page index of the orders slice; coerced from the query string. */
  page: z46.coerce.number().int().min(1).optional(),
  /** Page size of the orders slice; coerced from the query string. */
  pageSize: z46.coerce.number().int().min(1).optional()
});
var campaignsContract = c16.router(
  {
    list: {
      method: "GET",
      path: "/stores/:id/campaigns",
      pathParams: z46.object({ id: z46.string().uuid() }),
      responses: {
        200: CampaignListResponseSchema,
        // 409: store is `assigned` and not onboarded — read blocked.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped campaigns list + period roll-up headline (Req 9.1, 9.2, 10.1)."
    },
    getCampaignTerms: {
      method: "GET",
      // Static `/terms` MUST precede the `/:cid` param route below
      // (find-my-way static-before-param, the existing reconciliation-route
      // rule) — placed here, before `detail`, so the terms axis never resolves
      // as a `:cid` campaign id.
      path: "/stores/:id/campaigns/terms",
      pathParams: z46.object({ id: z46.string().uuid() }),
      query: CampaignTermsQuerySchema,
      responses: {
        200: TermsResultSchema,
        // 409: store is `assigned` and not onboarded — read blocked. The 404
        // (cross-tenant / missing store, no existence leak) comes from
        // `commonErrorResponses` below.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped campaign terms axis: discount buckets + spread/unexplained summary over a date range (clarity-campaign-terms-economics Req 5.1)."
    },
    getTermBucketDetail: {
      method: "GET",
      // The drill-down for ONE terms bucket. Sits UNDER the static `/terms`
      // segment as a param route (`/terms/:bucketKey`), so it is a different
      // path DEPTH than the exact `/terms` route above — find-my-way resolves
      // the exact `/terms` and the `/terms/:bucketKey` param without collision,
      // and both still precede the `/:cid` param route (static-before-param).
      // `bucketKey` carries `%|:` separators; the client URL-encodes it and
      // ts-rest/fastify decode the path param, so the handler sees the decoded
      // signature key (what `aggregateTerms`/`termBucketDetail` match on).
      path: "/stores/:id/campaigns/terms/:bucketKey",
      pathParams: z46.object({ id: z46.string().uuid(), bucketKey: z46.string().min(1) }),
      query: TermBucketDetailQuerySchema,
      responses: {
        200: TermBucketDetailSchema,
        // 409: store is `assigned` and not onboarded — read blocked. The 404
        // (cross-tenant / missing store, no existence leak) comes from
        // `commonErrorResponses` below. A bucketKey no order matches is NOT a
        // 404 — the core returns a zeroed, empty detail (total 0) and the
        // handler returns it 200 (an empty bucket is a valid, honest answer).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped term-bucket detail: one bucket\u2019s signature + summary + observed-% histogram + a paginated newest-first orders slice over a date range (clarity-campaign-terms-economics Req 5.1, task 9.2)."
    },
    detail: {
      method: "GET",
      path: "/stores/:id/campaigns/:cid",
      pathParams: z46.object({ id: z46.string().uuid(), cid: z46.string().uuid() }),
      // What-if projection params (task 12.2; Req 10.2, 7.3): optional
      // `discountPct`/`cap`/`cpc` recompute the SAME pure `projectCampaign`.
      // Co-funding is NOT a param — pinned (Req 7.3); no `coFund` key here.
      query: CampaignWhatIfQuerySchema,
      responses: {
        200: CampaignDetailResponseSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped campaign detail: projection + occurrences (participation) + actuals, with optional what-if discountPct/cap/cpc recompute (co-fund pinned; Req 9.3, 10.1, 10.2, 7.3)."
    },
    occurrence: {
      method: "GET",
      path: "/stores/:id/campaigns/:cid/occurrences/:oid",
      pathParams: z46.object({
        id: z46.string().uuid(),
        cid: z46.string().uuid(),
        oid: z46.string().uuid()
      }),
      responses: {
        200: OccurrenceDetailResponseSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped occurrence detail: override terms + matched-order evidence (Req 9.4, 10.1)."
    },
    getItemScopedDealEconomics: {
      method: "GET",
      // A deeper path UNDER the `:cid` param (`/:cid/economics`), so it coexists
      // with `/:cid` and the `/:cid/occurrences/:oid` sub-route without
      // collision — the `economics` segment is static where `occurrences` is.
      path: "/stores/:id/campaigns/:cid/economics",
      pathParams: z46.object({ id: z46.string().uuid(), cid: z46.string().uuid() }),
      query: CampaignTermsQuerySchema,
      responses: {
        200: ItemScopedDealEconomicsSchema,
        // 409: store is `assigned` and not onboarded — read blocked. The 404
        // (cross-tenant / missing store or campaign, no existence leak) comes
        // from `commonErrorResponses` below.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped item-scoped deal economics: per-item food-cost margin report for one campaign over a date range (reporting only; clarity-campaign-terms-economics Req 5.2)."
    },
    reconciliation: {
      method: "GET",
      path: "/stores/:id/campaigns/reconciliation",
      pathParams: z46.object({ id: z46.string().uuid() }),
      query: CampaignReconciliationQuerySchema,
      responses: {
        200: CampaignReconciliationResponseSchema,
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Tenant-scoped reconciliation of matched actuals vs platform performance over a date range (clarity-campaign-reconciliation Req 4)."
    },
    listMonthlyOffers: {
      method: "GET",
      path: "/admin/campaigns/monthly-offer",
      query: z46.object({ platformKey: PlatformKeySchema }),
      responses: {
        200: ListMonthlyOffersResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: list the current platform-shared monthly offers + occurrences for a platform (admin only; Req 3.4, 10.3)."
    },
    uploadMonthlyOffer: {
      method: "POST",
      path: "/admin/campaigns/monthly-offer/ocr",
      body: UploadMonthlyOfferBodySchema,
      responses: {
        200: UploadMonthlyOfferResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: upload a monthly-offer image and get the OCR draft (admin only; Req 3.1, 3.2, 10.3)."
    },
    saveMonthlyOffer: {
      method: "POST",
      path: "/admin/campaigns/monthly-offer",
      body: SaveMonthlyOfferBodySchema,
      responses: {
        200: SaveMonthlyOfferResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: save the reviewed monthly offers, shared across the platform stores (admin only; Req 3.3, 3.4, 10.3)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/payout-backtest.ts
import { initContract as initContract17 } from "@ts-rest/core";
import { z as z47 } from "zod";
var BacktestVerdictSchema = z47.enum(["pass", "fail"]);
var BacktestLineKeySchema = z47.enum([
  "sales",
  "talabatVoucher",
  "commission",
  "paymentFee",
  "proDelivery",
  "operational",
  "ads",
  "refundCharges",
  "adjustments",
  "otherAdditions",
  "otherDeductions",
  "net"
]);
var BacktestLineSchema = z47.object({
  key: BacktestLineKeySchema,
  /** Talabat's reported figure; `null` when the breakdown isn't captured. */
  talabat: z47.number().nullable(),
  /** Our order-derived figure; `null` when the line has no order-level source. */
  ours: z47.number().nullable(),
  /** `ours − talabat` when both exist. */
  diff: z47.number().nullable(),
  /** Talabat's itemized sub-lines (commission types, ad types, additional-sales reasons…). */
  talabatItems: z47.array(z47.object({ label: z47.string(), amount: z47.number() }))
});
var BacktestCarryInSchema = z47.object({
  periodStart: IsoTimestampSchema,
  /** EXCLUSIVE end (the payout's own start). */
  periodEnd: IsoTimestampSchema,
  /** INCLUSIVE last covered day. */
  periodEndInclusive: IsoTimestampSchema,
  /** Start inferred from the first order on file (no earlier payout stored). */
  inferredStart: z47.boolean(),
  orderCount: z47.number().int().nonnegative(),
  cancelledCount: z47.number().int().nonnegative(),
  /** Net of the carried orders' lines, before ads. */
  ordersNet: z47.number(),
  /** `reported` — every day's CPC spend came from Talabat Ads; `mixed` — some days; `estimated` — none. */
  adsSource: z47.enum(["reported", "mixed", "estimated"]),
  /** Reported CPC / placement spend incl. VAT (negative); `null` when no day was reported. */
  reportedAds: z47.number().nullable(),
  /** CPC estimate for days without reported spend (negative); 0 when all reported. */
  unreportedAdsEstimate: z47.number(),
  /** Per-order marketing the export folds into "Estimated earnings" (older exports), negative; 0 when none. */
  perOrderMarketing: z47.number(),
  /** Estimated sponsored-deal fees (negative; not in the Ads reporting); 0 when not applied. */
  dealsEstimate: z47.number(),
  /** CPC ads per order used for unreported days (neighbouring paid rounds). */
  cpcPerOrder: z47.number(),
  /** Sponsored-deal fees per order used for the deal estimate. */
  dealsPerOrder: z47.number(),
  /** Total ads for the carried round (negative). */
  adsEstimate: z47.number(),
  /** `ordersNet + adsEstimate` — our "Adjustments" figure. */
  net: z47.number()
});
var BacktestMinimumPayoutSchema = z47.object({
  threshold: z47.number(),
  /** Talabat's carried amount was under the minimum; `null` when nothing was carried. */
  carriedBelow: z47.boolean().nullable(),
  /** Our carried-round net was under the minimum; `null` when nothing was carried. */
  oursCarriedBelow: z47.boolean().nullable(),
  /** What Talabat paid was at or above the minimum. */
  paidAtOrAbove: z47.boolean(),
  /** The rule is consistent with this payout. */
  holds: z47.boolean()
});
var PayoutBacktestRowSchema = z47.object({
  externalPayoutId: z47.string(),
  periodStart: IsoTimestampSchema,
  /** Stored EXCLUSIVE window end (day after the last covered day). */
  periodEnd: IsoTimestampSchema,
  /** INCLUSIVE last covered day (`periodEnd − 1 day`) — what the portal shows. */
  periodEndInclusive: IsoTimestampSchema,
  paidAt: IsoTimestampSchema.nullable(),
  /** Orders linked by window. */
  linkedCount: z47.number().int().nonnegative(),
  /** The payout's reported order count; `null` when unknown. */
  expectedCount: z47.number().int().nonnegative().nullable(),
  /** Of the linked orders, how many were cancelled. */
  cancelledCount: z47.number().int().nonnegative(),
  /** Our net, built from the order lines. */
  estimate: z47.number(),
  /** Net Talabat paid. */
  actual: z47.number(),
  /** `estimate − actual`. */
  deltaAbs: z47.number(),
  /** Signed % vs actual; `null` when actual is 0. */
  deltaPct: z47.number().nullable(),
  /** `true` when the full Talabat breakdown was captured. */
  hasBreakdown: z47.boolean(),
  /** Line-by-line comparison, in display order. */
  lines: z47.array(BacktestLineSchema),
  /**
   * The skipped (under-minimum) round carried into this payout, as we built it
   * from its orders — what we compare with Talabat's "Adjustments". `null` when
   * no round was carried in.
   */
  carryIn: BacktestCarryInSchema.nullable(),
  /** The minimum-payout rule re-checked against this payout. */
  minimumPayout: BacktestMinimumPayoutSchema,
  /** Verdict on the FULL net at the server's default tolerance (client may recompute). */
  verdict: BacktestVerdictSchema
});
var PayoutBacktestGroupSchema = z47.object({
  storeId: IdSchema,
  storeName: z47.string(),
  platformKey: PlatformKeySchema,
  /**
   * Whether Talabat Ads daily spend was fetched for this store's carried rounds
   * (`reported`), or carried-round ads fall back to a per-order estimate
   * (`unavailable`, e.g. the acquisition session is not connected).
   */
  adSpend: z47.object({
    state: z47.enum(["reported", "unavailable"]),
    /** Covered days (inclusive), when reported. */
    coverFrom: z47.string().nullable(),
    coverTo: z47.string().nullable(),
    /** Short, non-secret reason when unavailable. */
    reason: z47.string().nullable()
  }).default({ state: "unavailable", coverFrom: null, coverTo: null, reason: null }),
  rows: z47.array(PayoutBacktestRowSchema)
});
var PayoutBacktestResponseSchema = z47.object({
  /** The percentage tolerance the server applied for the `verdict` field. */
  defaultTolerancePct: z47.number(),
  groups: z47.array(PayoutBacktestGroupSchema)
});
var c17 = initContract17();
var payoutBacktestContract = c17.router(
  {
    list: {
      method: "GET",
      path: "/admin/payout-backtest",
      responses: {
        200: PayoutBacktestResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin/dev: backtest our payout estimate against settled payouts across all stores (admin only)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/banner-options.ts
import { initContract as initContract18 } from "@ts-rest/core";
import { z as z48 } from "zod";
var CalendarDateSchema = z48.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");
var BannerRunSchema = z48.object({
  from: CalendarDateSchema,
  /** Inclusive end day. */
  to: CalendarDateSchema,
  /** Fee for this run, JOD. */
  fee: z48.number().positive()
}).refine((r) => r.to >= r.from, { message: "Run ends before it starts", path: ["to"] });
var BannerOptionStatusSchema = z48.enum(["draft", "published"]);
var BannerOptionSchema = z48.object({
  id: IdSchema,
  platformKey: PlatformKeySchema,
  status: BannerOptionStatusSchema,
  name: z48.string().trim().min(1),
  arabicName: z48.string().trim().min(1).nullable().default(null),
  /** Where customers see it. Required to publish (Req 2.2); may be empty on a draft. */
  where: z48.string().trim().default(""),
  /** Optional notes for owners. */
  notes: z48.string().trim().min(1).nullable().default(null),
  /** Last day the account manager takes requests. */
  askBy: CalendarDateSchema.nullable().default(null),
  runs: z48.array(BannerRunSchema).min(1),
  /** Stamped on each draft→published transition (Req 3.2, 3.3). */
  publishedAt: IsoTimestampSchema.nullable().default(null),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema
});
var SaveBannerOptionBodySchema = BannerOptionSchema.pick({
  platformKey: true,
  name: true,
  arabicName: true,
  where: true,
  notes: true,
  askBy: true,
  runs: true,
  status: true
}).superRefine((b, ctx) => {
  if (b.status === "published" && b.where.length === 0)
    ctx.addIssue({ code: "custom", path: ["where"], message: "Say where customers will see it" });
});
var SetBannerOptionStatusBodySchema = z48.object({ status: BannerOptionStatusSchema });
var BannerDisplayStateSchema = z48.enum(["draft", "published", "ended"]);
var AdminBannerOptionSchema = BannerOptionSchema.extend({
  displayState: BannerDisplayStateSchema
});
var ListAdminBannerOptionsResponseSchema = z48.object({
  options: z48.array(AdminBannerOptionSchema)
});
var AdminBannerOptionResponseSchema = z48.object({ option: AdminBannerOptionSchema });
var OwnerBannerOptionSchema = BannerOptionSchema.omit({ status: true }).extend({
  group: z48.enum(["upcoming", "running"]),
  isNew: z48.boolean(),
  /** Runs whose end is on/after today, start-ascending (what the owner sees). */
  openRuns: z48.array(BannerRunSchema).min(1),
  /** The first open run — what the join message names. */
  nextRun: BannerRunSchema,
  /** The open run that includes today, else null. */
  currentRun: BannerRunSchema.nullable()
});
var StoreBannerOptionsResponseSchema = z48.object({
  /** Upcoming first, then running (Req 6.2 order). */
  options: z48.array(OwnerBannerOptionSchema),
  newCount: z48.number().int().nonnegative()
});
var c18 = initContract18();
var bannerOptionsContract = c18.router(
  {
    listAdmin: {
      method: "GET",
      path: "/admin/banner-options",
      responses: {
        200: ListAdminBannerOptionsResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: list every banner option across platforms with its display state (Req 4.1)."
    },
    create: {
      method: "POST",
      path: "/admin/banner-options",
      body: SaveBannerOptionBodySchema,
      responses: {
        200: AdminBannerOptionResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: create a banner option as a draft or published (Req 2.1\u20132.5, 3.2)."
    },
    update: {
      method: "PUT",
      path: "/admin/banner-options/:bid",
      pathParams: z48.object({ bid: IdSchema }),
      body: SaveBannerOptionBodySchema,
      responses: {
        200: AdminBannerOptionResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: replace a banner option, keeping its id (Req 2.6, 3.2, 3.3)."
    },
    setStatus: {
      method: "POST",
      path: "/admin/banner-options/:bid/status",
      pathParams: z48.object({ bid: IdSchema }),
      body: SetBannerOptionStatusBodySchema,
      responses: {
        200: AdminBannerOptionResponseSchema,
        ...commonErrorResponses
      },
      summary: "Admin: publish or move a banner option to drafts (Req 3.2, 3.4)."
    },
    remove: {
      method: "DELETE",
      path: "/admin/banner-options/:bid",
      pathParams: z48.object({ bid: IdSchema }),
      body: c18.noBody(),
      responses: {
        200: z48.object({ id: IdSchema }),
        ...commonErrorResponses
      },
      summary: "Admin: delete a banner option (Req 2.7)."
    },
    listForStore: {
      method: "GET",
      path: "/stores/:id/banner-options",
      pathParams: z48.object({ id: z48.string().uuid() }),
      responses: {
        200: StoreBannerOptionsResponseSchema,
        ...commonErrorResponses
      },
      summary: "Owner: the published, not-yet-ended banner options for the store platform, grouped (Req 6.1\u20136.3)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/admin-messages.ts
import { initContract as initContract19 } from "@ts-rest/core";
import { z as z49 } from "zod";
var c19 = initContract19();
var adminMessagesContract = c19.router(
  {
    listContacts: {
      method: "GET",
      path: "/admin/clients/:clientId/whatsapp-contacts",
      pathParams: z49.object({ clientId: IdSchema }),
      responses: {
        200: z49.array(WhatsAppContactSchema),
        ...commonErrorResponses
      },
      summary: "List a client\u2019s WhatsApp contacts (admin only; Req 1.1)."
    },
    createContact: {
      method: "POST",
      path: "/admin/clients/:clientId/whatsapp-contacts",
      pathParams: z49.object({ clientId: IdSchema }),
      body: CreateContactBodySchema,
      responses: {
        201: WhatsAppContactSchema,
        ...commonErrorResponses,
        409: commonErrorResponses[400]
      },
      summary: "Add a WhatsApp contact to a client (admin only; Req 1.1\u20131.3)."
    },
    updateContact: {
      method: "PATCH",
      path: "/admin/whatsapp-contacts/:contactId",
      pathParams: z49.object({ contactId: IdSchema }),
      body: UpdateContactBodySchema,
      responses: {
        200: WhatsAppContactSchema,
        ...commonErrorResponses,
        409: commonErrorResponses[400]
      },
      summary: "Update a contact; a changed phone clears opt-in (admin only; Req 1.5)."
    },
    optIn: {
      method: "POST",
      path: "/admin/whatsapp-contacts/:contactId/opt-in",
      pathParams: z49.object({ contactId: IdSchema }),
      body: OptInBodySchema,
      responses: {
        200: WhatsAppContactSchema,
        ...commonErrorResponses
      },
      summary: "Record a contact opt-in (admin only; Req 1.3)."
    },
    optOut: {
      method: "POST",
      path: "/admin/whatsapp-contacts/:contactId/opt-out",
      pathParams: z49.object({ contactId: IdSchema }),
      body: OptOutBodySchema,
      responses: {
        200: WhatsAppContactSchema,
        ...commonErrorResponses
      },
      summary: "Record a contact opt-out (admin only; Req 1.4)."
    },
    listMessages: {
      method: "GET",
      path: "/admin/messages",
      query: ListMessagesQuerySchema,
      responses: {
        200: z49.array(OutboundMessageListItemSchema),
        ...commonErrorResponses
      },
      summary: "List sent messages, newest first, masked recipient (admin only; Req 8.1, 8.2)."
    },
    listTriggers: {
      method: "GET",
      path: "/admin/messages/triggers",
      query: TriggerRollupQuerySchema,
      responses: {
        200: z49.array(TriggerRollupSchema),
        ...commonErrorResponses
      },
      summary: "Per-trigger roll-up of message statuses (admin only; Req 8.3)."
    },
    listTemplates: {
      method: "GET",
      path: "/admin/messages/templates",
      responses: {
        200: z49.array(TemplateHealthSchema),
        ...commonErrorResponses
      },
      summary: "Template health: registry vs Meta category/status (admin only; Req 8.5)."
    },
    getMessage: {
      method: "GET",
      path: "/admin/messages/:mid",
      pathParams: z49.object({ mid: IdSchema }),
      responses: {
        200: OutboundMessageSchema,
        ...commonErrorResponses
      },
      summary: "One message with the unmasked recipient + status timeline (admin only; Req 8.4)."
    },
    sendTest: {
      method: "POST",
      path: "/admin/messages/test",
      body: SendTestBodySchema,
      responses: {
        200: DispatchSummarySchema,
        ...commonErrorResponses
      },
      summary: "Dispatch the clarity_test template to one contact (admin only; Req 8.6)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/locations.ts
import { initContract as initContract20 } from "@ts-rest/core";
import { z as z50 } from "zod";
var c20 = initContract20();
var locationsContract = c20.router(
  {
    view: {
      method: "GET",
      path: "/admin/clients/:clientId/locations-view",
      pathParams: z50.object({ clientId: IdSchema }),
      responses: {
        200: ClientLocationsViewSchema,
        ...commonErrorResponses
      },
      summary: "A client\u2019s locations + flagged stores + suggestions (admin only; Req 2.1, 3.5)."
    },
    create: {
      method: "POST",
      path: "/admin/clients/:clientId/locations",
      pathParams: z50.object({ clientId: IdSchema }),
      body: CreateLocationBodySchema,
      responses: {
        201: LocationSchema,
        ...commonErrorResponses,
        409: commonErrorResponses[400]
      },
      summary: "Create a location grouping the client\u2019s stores (admin only; Req 1, 2.2)."
    },
    update: {
      method: "PATCH",
      path: "/admin/locations/:locationId",
      pathParams: z50.object({ locationId: IdSchema }),
      body: UpdateLocationBodySchema,
      responses: {
        200: LocationSchema,
        ...commonErrorResponses,
        409: commonErrorResponses[400]
      },
      summary: "Update a location (name/Arabic/members, full replace; admin only; Req 2.2)."
    },
    remove: {
      method: "DELETE",
      path: "/admin/locations/:locationId",
      pathParams: z50.object({ locationId: IdSchema }),
      responses: {
        200: z50.object({ ok: z50.literal(true) }),
        ...commonErrorResponses
      },
      summary: "Delete a location; its stores become loose (admin only; Req 1.6)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/monthly-review.ts
import { initContract as initContract21 } from "@ts-rest/core";
import { z as z51 } from "zod";
var c21 = initContract21();
var monthlyReviewContract = c21.router(
  {
    getReview: {
      method: "GET",
      path: "/stores/:id/monthly-review/:month",
      pathParams: z51.object({ id: IdSchema, month: z51.string().regex(/^\d{4}-\d{2}$/) }),
      responses: {
        200: MonthlyReviewSchema,
        ...commonErrorResponses
      },
      summary: "The recomputed monthly review for a store + published month (owner/admin; Req 4)."
    },
    getBrief: {
      method: "GET",
      path: "/stores/:id/monthly-review/:month/brief",
      pathParams: z51.object({ id: IdSchema, month: z51.string().regex(/^\d{4}-\d{2}$/) }),
      query: z51.object({ locale: ContactLocaleSchema.optional() }),
      responses: {
        200: AmBriefSchema,
        ...commonErrorResponses
      },
      summary: "The one AM brief built from the review, in the owner\u2019s locale (Req 2)."
    },
    publishStatus: {
      method: "GET",
      path: "/admin/monthly-offer/:platformKey/:month/publish",
      pathParams: z51.object({
        platformKey: PlatformKeySchema,
        month: z51.string().regex(/^\d{4}-\d{2}$/)
      }),
      responses: {
        200: PublishStatusSchema,
        ...commonErrorResponses
      },
      summary: "Publish state + enabled-store count for a platform\u2019s month (admin only; Req 3)."
    },
    publish: {
      method: "POST",
      path: "/admin/monthly-offer/:platformKey/:month/publish",
      pathParams: z51.object({
        platformKey: PlatformKeySchema,
        month: z51.string().regex(/^\d{4}-\d{2}$/)
      }),
      body: z51.object({}),
      responses: {
        200: PublishResultSchema,
        ...commonErrorResponses
      },
      summary: "Build + send the review to every enabled store\u2019s owner (admin only; Req 3)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/advisor-chat.ts
import { initContract as initContract22 } from "@ts-rest/core";
import { z as z52 } from "zod";
var c22 = initContract22();
var advisorContract = c22.router(
  {
    thread: {
      method: "GET",
      path: "/advisor/thread",
      query: z52.object({ clientId: IdSchema.optional() }),
      responses: {
        200: AdvisorThreadViewSchema,
        ...commonErrorResponses
      },
      summary: "Open or create the caller\u2019s web advisor conversation (owner, or admin for a client; Req 5.1)."
    },
    send: {
      method: "POST",
      path: "/advisor/thread/messages",
      body: SendAdvisorMessageBodySchema,
      responses: {
        200: AdvisorThreadViewSchema,
        ...commonErrorResponses
      },
      summary: "Send a message to the advisor; relayed into Chatwoot (owner only; Req 5.1)."
    },
    since: {
      method: "GET",
      path: "/advisor/thread/messages",
      // `after` is an opaque message-id cursor from Chatwoot (e.g. `cw-<id>`),
      // not a UUID — a plain string (pure B2).
      query: z52.object({ after: z52.string().min(1).optional(), clientId: IdSchema.optional() }),
      responses: {
        200: AdvisorMessagesSinceSchema,
        ...commonErrorResponses
      },
      summary: "The advisor messages after a cursor \u2014 the reply poll (owner only; Req 5.4)."
    },
    // ── AI budget (clarity-advisor-llm) ──
    myUsage: {
      method: "GET",
      path: "/advisor/usage",
      query: z52.object({ clientId: IdSchema.optional() }),
      responses: {
        200: AdvisorUsageViewSchema,
        ...commonErrorResponses
      },
      summary: "The caller\u2019s workspace AI usage this month vs its cap (owner read-only; Req 4.4)."
    },
    getUsage: {
      method: "GET",
      path: "/admin/clients/:clientId/advisor-usage",
      pathParams: z52.object({ clientId: IdSchema }),
      responses: {
        200: AdvisorUsageViewSchema,
        ...commonErrorResponses
      },
      summary: "A workspace\u2019s month-to-date AI spend + effective cap (admin only; Req 4.1)."
    },
    setCap: {
      method: "PUT",
      path: "/admin/clients/:clientId/advisor-cap",
      pathParams: z52.object({ clientId: IdSchema }),
      body: SetAdvisorCapBodySchema,
      responses: {
        200: AdvisorUsageViewSchema,
        ...commonErrorResponses
      },
      summary: "Set or clear a workspace\u2019s AI cap override (admin only; Req 4.2)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/workspace-users.ts
import { initContract as initContract23 } from "@ts-rest/core";
import { z as z53 } from "zod";
var c23 = initContract23();
var WorkspaceUsersViewSchema = z53.object({
  users: z53.array(UserResponseSchema)
});
var workspaceUsersContract = c23.router(
  {
    list: {
      method: "GET",
      path: "/clients/:clientId/users",
      pathParams: z53.object({ clientId: IdSchema }),
      responses: {
        200: WorkspaceUsersViewSchema,
        ...commonErrorResponses
      },
      summary: "List a workspace\u2019s users (admin, or the workspace\u2019s own client)."
    },
    create: {
      method: "POST",
      path: "/clients/:clientId/users",
      pathParams: z53.object({ clientId: IdSchema }),
      body: CreateWorkspaceUserBodySchema,
      responses: {
        201: UserResponseSchema,
        // A duplicate email is a clean conflict, not a generic 400.
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Add a user to a workspace (admin, or the workspace\u2019s own client)."
    },
    remove: {
      method: "DELETE",
      path: "/clients/:clientId/users/:userId",
      pathParams: z53.object({ clientId: IdSchema, userId: IdSchema }),
      responses: {
        200: WorkspaceUsersViewSchema,
        // 409 ⇒ removing self or the workspace's last user (both refused).
        409: ErrorBodySchema,
        ...commonErrorResponses
      },
      summary: "Remove a user from a workspace (not self, not the last user)."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/mythings-manual.ts
import { initContract as initContract24 } from "@ts-rest/core";
import { z as z54 } from "zod";
var c24 = initContract24();
var storeParam = z54.object({ id: z54.string().uuid() });
var entryParam = z54.object({ id: z54.string().uuid(), entryId: z54.string().uuid() });
var mythingsManualContract = c24.router(
  {
    listPayouts: {
      method: "GET",
      path: "/stores/:id/mythings/payouts",
      pathParams: storeParam,
      responses: { 200: z54.array(MythingsManualPayoutSchema), ...commonErrorResponses },
      summary: "List the owner-reported MyThings payouts for a store."
    },
    createPayout: {
      method: "POST",
      path: "/stores/:id/mythings/payouts",
      pathParams: storeParam,
      body: NewMythingsManualPayoutBodySchema,
      responses: { 201: MythingsManualPayoutSchema, ...commonErrorResponses },
      summary: "Record a MyThings payout the owner received."
    },
    deletePayout: {
      method: "DELETE",
      path: "/stores/:id/mythings/payouts/:entryId",
      pathParams: entryParam,
      body: z54.object({}).optional(),
      responses: { 204: z54.null(), ...commonErrorResponses },
      summary: "Delete an owner-reported MyThings payout."
    },
    listExpenses: {
      method: "GET",
      path: "/stores/:id/mythings/expenses",
      pathParams: storeParam,
      responses: { 200: z54.array(MythingsManualExpenseSchema), ...commonErrorResponses },
      summary: "List the owner-entered MyThings expenses for a store."
    },
    createExpense: {
      method: "POST",
      path: "/stores/:id/mythings/expenses",
      pathParams: storeParam,
      body: NewMythingsManualExpenseBodySchema,
      responses: { 201: MythingsManualExpenseSchema, ...commonErrorResponses },
      summary: "Record a MyThings expense (marketing / banner / other)."
    },
    deleteExpense: {
      method: "DELETE",
      path: "/stores/:id/mythings/expenses/:entryId",
      pathParams: entryParam,
      body: z54.object({}).optional(),
      responses: { 204: z54.null(), ...commonErrorResponses },
      summary: "Delete an owner-entered MyThings expense."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/store-analytics.ts
import { initContract as initContract25 } from "@ts-rest/core";
import { z as z55 } from "zod";
var c25 = initContract25();
var storeParam2 = z55.object({ id: z55.string().uuid() });
var periodQuery = z55.object({
  from: z55.string(),
  to: z55.string(),
  platform: z55.enum(["talabat", "careem", "mythings"]).optional()
});
var storeAnalyticsContract = c25.router(
  {
    list: {
      method: "GET",
      path: "/stores/:id/analytics",
      pathParams: storeParam2,
      responses: { 200: z55.array(StoreAnalyticsSchema), ...commonErrorResponses },
      summary: "List the store\u2019s owner-maintained analytics records."
    },
    upsert: {
      method: "POST",
      path: "/stores/:id/analytics",
      pathParams: storeParam2,
      body: UpsertStoreAnalyticsBodySchema,
      responses: { 200: StoreAnalyticsSchema, ...commonErrorResponses },
      summary: "Create or update the store\u2019s analytics for a period (owner-entered)."
    },
    remove: {
      method: "DELETE",
      path: "/stores/:id/analytics/:entryId",
      pathParams: z55.object({ id: z55.string().uuid(), entryId: z55.string().uuid() }),
      body: z55.object({}).optional(),
      responses: { 204: z55.null(), ...commonErrorResponses },
      summary: "Delete one analytics record."
    },
    customerSplit: {
      method: "GET",
      path: "/stores/:id/analytics/customer-split",
      pathParams: storeParam2,
      query: periodQuery,
      responses: { 200: CustomerSplitSchema, ...commonErrorResponses },
      summary: "New vs returning customer split, computed from orders over the period."
    },
    dealIncrementality: {
      method: "GET",
      path: "/stores/:id/analytics/deal-incrementality",
      pathParams: storeParam2,
      query: periodQuery,
      responses: { 200: z55.array(DealIncrementalitySchema), ...commonErrorResponses },
      summary: "Per-deal estimated extra orders vs a no-deal baseline."
    }
  },
  { strictStatusCodes: true }
);

// src/contracts/index.ts
var c26 = initContract26();
var contract = c26.router({
  auth: authContract,
  clients: clientsContract,
  brands: brandsContract,
  stores: storesContract,
  connections: connectionsContract,
  sync: syncContract,
  onboarding: onboardingContract,
  orders: ordersContract,
  statements: statementsContract,
  payouts: payoutsContract,
  reconciliation: reconciliationContract,
  settings: settingsContract,
  adminIngestion: adminIngestionContract,
  subscriptions: subscriptionsContract,
  campaigns: campaignsContract,
  payoutSchedule: payoutScheduleContract,
  payoutBacktest: payoutBacktestContract,
  bannerOptions: bannerOptionsContract,
  adminMessages: adminMessagesContract,
  locations: locationsContract,
  monthlyReview: monthlyReviewContract,
  advisor: advisorContract,
  workspaceUsers: workspaceUsersContract,
  mythingsManual: mythingsManualContract,
  storeAnalytics: storeAnalyticsContract
});

// src/index.ts
var packageInfo = {
  name: "@clarity/contract"
};
var PlaceholderSchema = z56.object({
  ok: z56.boolean()
});
export {
  AccountFlowSchema,
  AccountStatusSchema,
  AccuracySchema,
  AddMythingsStoreBodySchema,
  AddMythingsStoreBranchResultSchema,
  AddMythingsStoreBranchSchema,
  AddMythingsStoreResponseSchema,
  AdditionalSalesSchema,
  AdminBannerOptionResponseSchema,
  AdminBannerOptionSchema,
  AdminConnectionEntrySchema,
  AdsSpendTieSchema,
  AdvisorBlockSchema,
  AdvisorChannelSchema,
  AdvisorChipSchema,
  AdvisorConversationSchema,
  AdvisorIntentSchema,
  AdvisorMessageSchema,
  AdvisorMessagesSinceSchema,
  AdvisorReplySchema,
  AdvisorThreadViewSchema,
  AdvisorUsageViewSchema,
  AdvisorVerdictRowSchema,
  AmBriefSchema,
  AnalyticsSourceSchema,
  BacktestCarryInSchema,
  BacktestLineKeySchema,
  BacktestLineSchema,
  BacktestMinimumPayoutSchema,
  BacktestVerdictSchema,
  BannerDisplayStateSchema,
  BannerOptionSchema,
  BannerOptionStatusSchema,
  BannerRunSchema,
  BillingContactSchema,
  BillingEventBaseSchema,
  BranchOptionSchema,
  BranchScopeSchema,
  BrandResponseSchema,
  BrandSchema,
  BreakEvenPivotSchema,
  CampaignActualsSchema,
  CampaignAudienceSchema,
  CampaignDetailResponseSchema,
  CampaignListResponseSchema,
  CampaignListRowSchema,
  CampaignMatchSchema,
  CampaignOccurrenceSchema,
  CampaignOccurrenceViewSchema,
  CampaignPerformanceSchema,
  CampaignPrioritySchema,
  CampaignProjectionSchema,
  CampaignProvenanceSchema,
  CampaignReconciliationQuerySchema,
  CampaignReconciliationResponseSchema,
  CampaignReconciliationSchema,
  CampaignSchema,
  CampaignScopeCandidateSchema,
  CampaignScopeCandidatesResponseSchema,
  CampaignSourceSchema,
  CampaignTermsQuerySchema,
  CampaignVarianceSchema,
  CampaignWhatIfQuerySchema,
  CareemAdjustmentEntrySchema,
  CareemCycleCheckSchema,
  CareemDeriveExtraSchema,
  CatalogBaseProvenanceSchema,
  CatalogEditSchema,
  CatalogFoodCostEditBodySchema,
  CatalogFoodCostEditResultSchema,
  CatalogItemResponseSchema,
  CatalogItemSchema,
  CatalogItemStatusSchema,
  CatalogOptionEditSchema,
  CatalogOptionRowSchema,
  CatalogOptionSchema,
  CatalogOptionsSourceSchema,
  CatalogOrdersSourceSchema,
  CatalogPickItemSchema,
  CatalogProvisionProgressSchema,
  CatalogReviewItemSchema,
  CatalogReviewQuerySchema,
  CatalogReviewSchema,
  ChannelPreferencesSchema,
  ChannelSchema,
  ClientConfigSchema,
  ClientLocationsViewSchema,
  ClientResponseSchema,
  ClientSchema,
  ConnectionStateSchema,
  ConnectionStatusSchema,
  ContactLocaleSchema,
  ContactRoleSchema,
  CpcBreakEvenSchema,
  CrCurvePointSchema,
  CreateBrandBodySchema,
  CreateClientBodySchema,
  CreateContactBodySchema,
  CreateLocationBodySchema,
  CreateStoreBodySchema,
  CreateSubscriptionBodySchema,
  CreateWorkspaceUserBodySchema,
  CredentialConnectionEntrySchema,
  CustomerSplitSchema,
  DealIncrementalitySchema,
  DeclaredCategorySchema,
  DeriveProgressSchema,
  DeriveSourceStatusSchema,
  DerivedProfileSummarySchema,
  DerivedReadSchema,
  DiscountSplitSchema,
  DiscoveredBrandSchema,
  DiscoveredStoreSchema,
  DiscrepancyKindSchema,
  DiscrepancySchema,
  DispatchRowSchema,
  DispatchSummarySchema,
  E164Schema,
  EffectiveDatingSchema,
  EffectiveFoodCostSchema,
  ErrorBodySchema,
  EstimatedBalanceSchema,
  FieldConfidenceMapSchema,
  FieldConfidenceSchema,
  FoodCostPctSchema,
  GenerateInvoiceBodySchema,
  GetPayoutScheduleQuerySchema,
  GetPayoutScheduleResponseSchema,
  GrainSchema,
  INVOICE_ISSUED_WORKFLOW_KEY,
  IdSchema,
  ImportBrandBodySchema,
  IngestionModeSchema,
  IngestionPersistedSchema,
  IngestionRunSchema,
  IngestionRunStatusSchema,
  IngestionTriggerSchema,
  InvoiceIssuedPayloadSchema,
  InvoiceLineItemSchema,
  InvoiceSchema,
  InvoiceStatusSchema,
  IsoTimestampSchema,
  ItemScopedDealEconomicsSchema,
  JodAmountSchema,
  JodInvoiceAmountSchema,
  LineEconomicsSchema,
  LineItemKindSchema,
  ListAdminBannerOptionsResponseSchema,
  ListMessagesQuerySchema,
  ListMonthlyOffersResponseSchema,
  ListPayoutSchedulesResponseSchema,
  ListRunsQuerySchema,
  LocationSchema,
  LocationStoreSchema,
  LoginBodySchema,
  LoginResponseSchema,
  MappingStatusSchema,
  MarkPaidBodySchema,
  MatchedOrderEvidenceSchema,
  MergeCatalogBodySchema,
  MessageChannelSchema,
  MessageStatusSchema,
  MoneyBreakdownSchema,
  MonthSummarySchema,
  MonthlyOfferDraftSchema,
  MonthlyOfferItemDraftSchema,
  MonthlyOfferOccurrenceDraftSchema,
  MonthlyReviewSchema,
  MythingsCredentialWriteSchema,
  MythingsExpenseCategorySchema,
  MythingsManualExpenseSchema,
  MythingsManualPayoutSchema,
  NewMythingsManualExpenseBodySchema,
  NewMythingsManualPayoutBodySchema,
  NormalizedOrderSchema,
  NormalizedPayoutSchema,
  NormalizedStatementSchema,
  NotificationEventEnvelopeSchema,
  NullableOrderMoneySchema,
  OccurrenceDetailResponseSchema,
  OnboardingDeriveResultSchema,
  OnboardingDeriveVerdictSchema,
  OnboardingGatherResultSchema,
  OnboardingStep1BodySchema,
  OnboardingStep2BodySchema,
  OnboardingStep3BodySchema,
  OpeningHoursDaySchema,
  OptInBodySchema,
  OptInSourceSchema,
  OptOutBodySchema,
  OrderDetailResponseSchema,
  OrderEconomicsSchema,
  OrderLineItemSchema,
  OrderListItemResponseSchema,
  OrderMoneySchema,
  OrderResponseSchema,
  OrderTypeWaterfallSchema,
  OtherFeeSchema,
  OutboundMessageListItemSchema,
  OutboundMessageSchema,
  OwnerBannerOptionSchema,
  PAYMENT_OVERDUE_WORKFLOW_KEY,
  PLATFORMS,
  ParticipationStatusSchema,
  PaymentMethodSchema,
  PaymentOverduePayloadSchema,
  PaymentRecordSchema,
  PayoutBacktestGroupSchema,
  PayoutBacktestResponseSchema,
  PayoutBacktestRowSchema,
  PayoutBreakdownGroupSchema,
  PayoutBreakdownItemSchema,
  PayoutBreakdownSchema,
  PayoutGapCauseSchema,
  PayoutMatchRowSchema,
  PayoutResponseSchema,
  PayoutScheduleRowSchema,
  PayoutScheduleRowTypeSchema,
  PayoutScheduleSchema,
  PayoutSummarySchema,
  PeriodRollupSchema,
  PivotAxisSchema,
  PivotCellSchema,
  PlaceholderSchema,
  PlatformCapabilitiesSchema,
  PlatformDescriptorSchema,
  PlatformFeeDefaultsSchema,
  PlatformFeeDefaultsUpdateSchema,
  PlatformKeySchema,
  PlatformLiveStatusSchema,
  PricingModelSchema,
  PricingResultSchema,
  ProjectionVerdictSchema,
  ProvenanceMapSchema,
  PublishResultSchema,
  PublishStateSchema,
  PublishStatusSchema,
  RawJsonSchema,
  RecipientContactSchema,
  RecipientSchema,
  RecommendationCadenceSchema,
  ReconciliationReportSchema,
  ReconciliationSideSchema,
  ReconciliationVerdictSchema,
  ReportSchema,
  RequestCodeBodySchema,
  RequestCodeResponseSchema,
  ResetPasswordBodySchema,
  RestaurantCategorySchema,
  ReviewHistorySchema,
  ReviewLineSchema,
  ReviewVerdictSchema,
  RoleSchema,
  RollupStateSchema,
  STATEMENT_TOLERANCE_PCT,
  SUBSCRIPTION_SUSPENDED_WORKFLOW_KEY,
  SaveBannerOptionBodySchema,
  SaveMonthlyOfferBodySchema,
  SaveMonthlyOfferResponseSchema,
  SavePayoutScheduleBodySchema,
  SavePayoutScheduleResponseSchema,
  ScopedItemEconomicsSchema,
  SendAdvisorMessageBodySchema,
  SendTestBodySchema,
  SessionConnectionEntrySchema,
  SetAddonDiscountBodySchema,
  SetAdvisorCapBodySchema,
  SetBannerOptionStatusBodySchema,
  SetCampaignScopeBodySchema,
  SetCampaignScopeResponseSchema,
  SetOverrideBodySchema,
  SettlementStatusSchema,
  SimilarByTermsSchema,
  SkipReasonSchema,
  StatementCheckSchema,
  StatementResponseSchema,
  StatementSourceSchema,
  StoreAnalyticsSchema,
  StoreBannerOptionsResponseSchema,
  StoreConnStateSchema,
  StoreProfileSchema,
  StoreResponseSchema,
  StoreSchema,
  StoreStateSchema,
  StoreStatusSchema,
  SubscriptionReadSchema,
  SubscriptionSchema,
  SubscriptionStatusSchema,
  SubscriptionSuspendedPayloadSchema,
  SuggestedLinkSchema,
  SyncPersistedSchema,
  SyncSourceSchema,
  SyncSummarySchema,
  TEST_PING_WORKFLOW_KEY,
  TemplateButtonSchema,
  TemplateButtonsSchema,
  TemplateDriftSchema,
  TemplateHealthSchema,
  TermBucketDetailQuerySchema,
  TermBucketDetailSchema,
  TermDetailOrderSchema,
  TermHistogramBinSchema,
  TermsBucketSchema,
  TermsResultSchema,
  TermsSpreadEntrySchema,
  TestPingPayloadSchema,
  ToggleAddonBodySchema,
  TokenUsageSchema,
  TriggerAcceptedSchema,
  TriggerKindSchema,
  TriggerRollupQuerySchema,
  TriggerRollupSchema,
  TriggerRunBodySchema,
  UnmergeCatalogBodySchema,
  UpcomingPayoutSchema,
  UpdateContactBodySchema,
  UpdateLocationBodySchema,
  UploadMonthlyOfferBodySchema,
  UploadMonthlyOfferResponseSchema,
  UpsertStoreAnalyticsBodySchema,
  UserResponseSchema,
  UserSchema,
  VerdictSchema,
  VerifyPhoneBodySchema,
  VouchersByBrandSchema,
  WaterfallLineKindSchema,
  WaterfallLineSchema,
  WeekdaySchema,
  WhatsAppContactSchema,
  WorkspaceUsersViewSchema,
  adminIngestionContract,
  adminMessagesContract,
  advisorContract,
  authContract,
  authModelOf,
  bannerOptionsContract,
  brandsContract,
  campaignsContract,
  capabilityOf,
  clientsContract,
  commonErrorResponses,
  connectionsContract,
  contract,
  livePlatforms,
  locationsContract,
  monthlyReviewContract,
  mythingsManualContract,
  onboardingContract,
  ordersContract,
  packageInfo,
  payoutBacktestContract,
  payoutScheduleContract,
  payoutsContract,
  reconciliationContract,
  resolve,
  settingsContract,
  statementsContract,
  storeAnalyticsContract,
  storesContract,
  subscriptionsContract,
  syncContract,
  workspaceUsersContract
};
//# sourceMappingURL=index.js.map